import { Fragment, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useZoom from '../hooks/useZoom';
import { GridControlsProvider, useGridControls } from '../context/GridControlsContext';
import ZoomStepIcon, { zoomLabel } from '../components/ZoomStepIcon';
import { OwnedPrintingBadge } from '../components/PrintingBadge';
import SetUpcomingBadge, { isUpcomingSet } from '../components/SetUpcomingBadge';
import PrintLabelsSheet from '../components/print/PrintLabelsSheet';
import RoundPricesSheet from '../components/print/RoundPricesSheet';
import {
  connect, isConnected, labelPrefs, printLabels, printerModel, rememberedPrinter,
} from '../services/labelPrinter';
import { SHARE_ORIGIN, labelQrUrl } from '../services/binderShare';
import {
  readCards, writeCards, readLastCards, writeLastProfile, FIRST_PAGE, readPrices, writePrices } from '../utils/cardCache';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { recallScroll } from '../utils/navMemory';
import { isGuest } from '../lib/guestMode';
import GuestEmptyState from '../components/GuestEmptyState';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import TutorialOverlay, { TutorialBackdrop } from '../components/TutorialOverlay';
import { supabase } from '../lib/supabase';
import { sendNewFollowerNotification, sendCardRemovedFromTradeNotification } from '../lib/notifications';
import { setCardNavigationContext } from '../lib/cardNavigationContext';
import { getBulkCardPrices, isWithinCooldown, daysSinceRefresh,
         refreshCollectionPrices, autoFetchMissingPrices, formatPrice, isGradedCard,
         needsPricing, priceKey } from '../services/cardPriceCache';
import { runPriceWaves } from '../utils/priceWaves';
import * as cardData from '../services/cardData';

// Modals
import AddCardModal from '../components/modals/AddCardModal';
import AddBinderModal from '../components/modals/AddBinderModal';
import AddToBinderModal from '../components/modals/AddToBinderModal';
// Old scanner modals moved to _old-scanner-files/ - now using /scanner page
import SortFilterModal from '../components/modals/SortFilterModal';
import { PC_ONLY, BINDER_TYPE_PC } from '../services/wishBinders';
import { deleteBinder, tradeBinderMoveNote, isTradeBinder, TRADE_BINDER_LIMIT } from '../services/tradeBinder';
import WishListMatchModal from '../components/modals/WishListMatchModal';
import WishTab from '../components/wish/WishTab';
import WishFromExplore, { useWishFromExplore } from '../components/wish/WishFromExplore';
import { buildTopTenSlots } from '../components/wish/topTenSlots';
import AllBadgesModal from '../components/modals/AllBadgesModal';
import { BadgeProgressRing } from '../components/BadgeProgressRing';

// Binder Images
import BabyBlueBinder from '../assets/images/Binders/BabyBlueBinder.png';
import BlueBinder from '../assets/images/Binders/BlueBinder.png';
import GreenBinder from '../assets/images/Binders/GreenBinder.png';
import MintGreenBinder from '../assets/images/Binders/MintGreenBinder.png';
import OrangeBinder from '../assets/images/Binders/OrangeBinder.png';
import PinkBinder from '../assets/images/Binders/PinkBinder.png';
import PurpleBinder from '../assets/images/Binders/PurpleBinder.png';
import RedBinder from '../assets/images/Binders/RedBinder.png';
import WhiteBinder from '../assets/images/Binders/WhiteBinder.png';
import YellowBinder from '../assets/images/Binders/YellowBinder.png';
import CardImage from '../components/CardImage';
import { isPricingVisible } from '../utils/pricingGate';
import haptics from '../utils/haptics';
import usePrintLabels from '../hooks/usePrintLabels';
import { formatMoney, loadInWaves, tcgKey, sumSetTotals } from '../utils/catalogPrices';
import CardFan from '../components/collection/CardFan';
import { onScrollPosition, resetChrome } from '../utils/scrollChrome';
import {
  DEFAULT_CATALOG_SORT, DEFAULT_SEARCH_SORT, EMPTY_CATALOG_FILTERS, countCatalogFilters,
  hasCatalogFilters, describeCatalogFilters, SORT_VERB, SORT_PHRASE,
  catalogGrouping, normaliseFilters, rarityRankOf,
  EMPTY_COLLECTION_FILTERS, filterList,
  RARITY_BUCKETS, shortEraLabel,
} from '../services/cardData/catalogFilters';
import { loadCollectionFacets } from '../services/collectionFacets';
import { warmCatalogIndex } from '../services/localCatalogIndex';
import { SelectionActionBar } from '../components/collection/SelectionBars';
import BinderPickerSheet from '../components/modals/BinderPickerSheet';
import BinderActionSheet from '../components/modals/BinderActionSheet';
import { shareBinder } from '../services/binderShare';
import { TcgPocketChip, TcgPocketLine } from '../components/TcgPocketNotice';
import {
  isTcgPocketCard, isTcgPocketSet, useTcgPocketSets, TCG_POCKET_ERA_ID,
  TCG_POCKET_TRADE_NONE, tcgPocketSkippedLine,
} from '../utils/tcgPocket';
import { BarChart3, Check, ChevronDown, Layers, Library, Printer } from 'lucide-react';
import BottomSheet from '../components/BottomSheet';
import { fetchSlotGrid } from '../services/wishSlots';
import { printingsOf } from '../components/wish/setSlots';
import {
  printingColor, printingShortLabel, printingParam, standardPrintingKey,
} from '../components/wish/printings';
import {
  buildSetPrintingTiles, sumPrintingValue, countPrintingSlots,
} from '../components/collection/setPrintingTiles';
import cardChaseLogo from '../assets/logo.png';
import useSmartSearch from '../hooks/useSmartSearch';
import SmartSearchResults from '../components/collection/SmartSearchResults';
import CatalogCardGrid from '../components/collection/CatalogCardGrid';
import { listCatalogCards, countCatalogCards } from '../services/cardData';

// ---- 2.0 adapter compatibility helpers -----------------------------------
// The old Collection.js UI was written against TCGdex's raw response shape
// (releaseDate, cardCount.total, base image URL without extension). The new
// cardData adapter returns a normalized shape. These helpers rebuild the
// legacy shape at the call site so downstream renderers don't need changes.
// Hold timing for entering select mode. Long enough to never fire on a tap
// or the start of a scroll, short enough that the lift reads as a response.
/**
 * How much of the set view's slot grid is mounted at once, and the step it
 * grows by. Same numbers as the Wish tab's slot grid, for the same reason: one
 * set can be four hundred tiles.
 */
const SET_SLOT_FIRST_PAGE = 60;
const SET_SLOT_PAGE_STEP = 60;

const HOLD_MS = 420;
const LIFT_AFTER_MS = 110;
const HOLD_DRIFT_PX = 12;
const holdScale = (id, pressingId, poppedId) => (
  poppedId === id ? [1.06, 1.13, pressingId === id ? 1.06 : 1] : pressingId === id ? 1.06 : 1
);
const holdTransition = (id, pressingId, poppedId) => (
  poppedId === id
    ? { duration: 0.34, times: [0, 0.35, 1], ease: 'easeOut' }
    : pressingId === id
      ? { type: 'spring', stiffness: 320, damping: 32 }
      : { type: 'spring', stiffness: 420, damping: 30 }
);
const stripSizeSuffix = (url) => (typeof url === 'string' ? url.replace(/\/(?:high|low)\.webp$/, '') : url);
// A TCGdex base URL takes /low.webp for a thumbnail. Our own mirrored copies
// are a finished file already, and a suffix on them is a 404.
const thumbOf = (url) => (typeof url === 'string' && url ? (/\.(?:webp|png|jpe?g)$/i.test(url) ? url : `${url}/low.webp`) : null);
const legacySet = (s, sortIndex) => ({
  id: s.id,
  name: s.name,
  releaseDate: s.release_date,
  serie: { id: s.era_id },
  cardCount: { total: s.card_count_total, official: s.card_count_official, held: s.card_count ?? null },
  // Printings we hold, by the set grid's own rule. Base Set is 102 cards and
  // 408 printings, and the grid draws 408, so the tile says 408.
  printingCount: s.printing_count ?? null,
  logo: s.logo_url || null, // extensionless base URL — renderers append .png (TCGdex convention)
  provisional: s.provisional_source || null,
  sortIndex,
});
const legacyCard = (c) => ({
  id: c.id,
  name: c.name,
  localId: c.local_id,
  set: c.set_id ? { id: c.set_id, name: c.set_name } : undefined,
  image: stripSizeSuffix(c.image_url),
  rarity: c.rarity,
  category: c.category,
  hp: c.hp,
  types: c.types,
});

const binderImages = {
  babyblue: BabyBlueBinder,
  blue: BlueBinder,
  green: GreenBinder,
  mintgreen: MintGreenBinder,
  orange: OrangeBinder,
  pink: PinkBinder,
  purple: PurpleBinder,
  red: RedBinder,
  white: WhiteBinder,
  yellow: YellowBinder,
};

// Icons
const ActivityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LayersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
    <path d="m6.08 11-3.5 1.6a1 1 0 0 0 0 1.81l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9a1 1 0 0 0 0-1.83L17.9 11" />
  </svg>
);

const CardsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

const BindersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <path d="M8 7h8" />
    <path d="M8 11h6" />
  </svg>
);

const WishListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const MatchBoltIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// Grid icon for Sets view toggle

/** A dashed ring: the outline of a card you have not got yet. */
const MissingOnlyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3">
    <circle cx="12" cy="12" r="9" />
  </svg>
);

const SortIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 16 4 4 4-4" />
    <path d="M7 20V4" />
    <path d="m21 8-4-4-4 4" />
    <path d="M17 4v16" />
  </svg>
);


/**
 * The zoom control, as one icon that says which step it is on.
 *
 * Level 0 is a plain lens and the button is unfilled, so it reads as untouched,
 * which is what it is: the resting state and the smallest size. Level 1 adds a
 * plus inside the lens. Level 2 adds a second, smaller plus outside it.
 *
 * Two pluses INSIDE one lens was the owner's suggestion and it does not survive
 * the size: the lens is an 8-unit circle in a 24-unit box, drawn at 16px, and
 * two plus marks inside it collapse into a smudge. Putting the second one
 * outside, up and to the right, keeps both legible and still reads as "more".
 */

const PrinterIcon = () => <Printer size={14} strokeWidth={2.2} />;
const AnalyzeIcon = () => <BarChart3 size={14} strokeWidth={2.2} />;
const PlusIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "#facc15" : "none"} stroke="#facc15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const MoreVerticalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1.5"></circle>
    <circle cx="12" cy="12" r="1.5"></circle>
    <circle cx="12" cy="19" r="1.5"></circle>
  </svg>
);

const LockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z"/>
    <path d="M7 11V7a5 5 0 0110 0v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// User Options Menu Component
const UserOptionsMenu = ({ isOpen, onClose, isFollowing, onToggleFollow, onReport, onBlock }) => {
  if (!isOpen) return null;

  return (
    <>
      <div style={menuStyles.backdrop} onClick={onClose} />
      <div style={menuStyles.menu}>
        <button style={menuStyles.menuItem} onClick={() => { onToggleFollow(); onClose(); }}>
          <span style={menuStyles.menuItemText}>
            {isFollowing ? 'Unfollow' : 'Follow'}
          </span>
        </button>
        <div style={menuStyles.menuDivider} />
        <button style={menuStyles.menuItem} onClick={() => { onBlock(); onClose(); }}>
          <span style={menuStyles.menuItemText}>Block User</span>
        </button>
        <div style={menuStyles.menuDivider} />
        <button style={menuStyles.menuItemDanger} onClick={() => { onReport(); onClose(); }}>
          <span style={menuStyles.menuItemTextDanger}>Report User</span>
        </button>
      </div>
    </>
  );
};

// Report User Modal Component
const ReportUserModal = ({ isOpen, onClose, userId, currentUserId, onSuccess }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { id: 'nsfw', label: 'NSFW Content', description: 'Posts adult or inappropriate content' },
    { id: 'wrong_content', label: 'Wrong Content', description: 'Posts non-Pokemon/TCG content' },
    { id: 'spam', label: 'Spam', description: 'Posts repetitive or promotional content' },
    { id: 'scam', label: 'Scam', description: 'Fraudulent or deceptive behavior' },
    { id: 'other', label: 'Other', description: 'Another issue not listed' },
  ];

  const handleSubmit = async () => {
    if (!selectedReason || !currentUserId || !userId) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reports')
        .insert({
          reporter_id: currentUserId,
          reported_user_id: userId,
          reason: selectedReason,
          description: description.trim() || null,
          status: 'pending',
        });

      if (error) {
        if (error.code === '23505') {
          alert('You have already reported this user.');
        } else {
          throw error;
        }
      } else {
        onSuccess && onSuccess();
        onClose();
        setSelectedReason(null);
        setDescription('');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={reportStyles.overlay} onClick={onClose}>
      <div style={reportStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={reportStyles.header}>
          <h2 style={reportStyles.title}>Report User</h2>
          <button style={reportStyles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div style={reportStyles.content}>
          <p style={reportStyles.subtitle}>Why are you reporting this user?</p>

          <div style={reportStyles.reasonsList}>
            {reasons.map((reason) => (
              <button
                key={reason.id}
                style={{
                  ...reportStyles.reasonButton,
                  ...(selectedReason === reason.id ? reportStyles.reasonButtonSelected : {}),
                }}
                onClick={() => setSelectedReason(reason.id)}
              >
                <div style={reportStyles.reasonContent}>
                  <span style={{
                    ...reportStyles.reasonLabel,
                    ...(selectedReason === reason.id ? reportStyles.reasonLabelSelected : {}),
                  }}>{reason.label}</span>
                  <span style={reportStyles.reasonDescription}>{reason.description}</span>
                </div>
                <div style={{
                  ...reportStyles.radioOuter,
                  ...(selectedReason === reason.id ? reportStyles.radioOuterSelected : {}),
                }}>
                  {selectedReason === reason.id && <div style={reportStyles.radioInner} />}
                </div>
              </button>
            ))}
          </div>

          <div style={reportStyles.descriptionSection}>
            <label style={reportStyles.descriptionLabel}>Additional details (optional)</label>
            <textarea
              style={reportStyles.descriptionInput}
              placeholder="Tell us more about the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div style={reportStyles.footer}>
          <button style={reportStyles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...reportStyles.submitButton,
              ...((!selectedReason || submitting) ? reportStyles.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

const menuStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    overflow: 'hidden',
    minWidth: '140px',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  menuItem: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemDanger: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemText: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  menuItemTextDanger: {
    color: 'var(--error)',
    fontSize: '14px',
    fontWeight: '500',
  },
  menuDivider: {
    height: '1px',
    backgroundColor: 'var(--border)',
  },
};

const reportStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--overlay)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
  },
  title: {
    color: 'var(--text)',
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-card)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  content: {
    padding: '20px',
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reasonButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
  },
  reasonButtonSelected: {
    backgroundColor: 'var(--accent-bg)',
    borderColor: 'var(--accent)',
  },
  reasonContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  reasonLabel: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  reasonLabelSelected: {
    color: 'var(--accent)',
  },
  reasonDescription: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  radioOuter: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioOuterSelected: {
    borderColor: 'var(--accent)',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent)',
  },
  descriptionSection: {
    marginTop: '20px',
  },
  descriptionLabel: {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    marginBottom: '8px',
  },
  descriptionInput: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid var(--border)',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'var(--error)',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    backgroundColor: 'var(--border)',
    cursor: 'not-allowed',
  },
};

/**
 * Where a label's QR code sends a buyer: the card's own page, which anyone can
 * open without an account. Defined once out here so the print sheet's preview
 * does not redraw on every render of the page.
 */
/**
 * Where a label's QR sends somebody.
 *
 * Into the binder, on that card. Not the generic card page: the point of
 * scanning a sticker on a card in a shop is to see THAT copy - the owner's own
 * photographs, their condition, their price - which is the shared binder view
 * and nothing else.
 *
 * The card's own page, with the binder named on the end. The binder is what
 * makes the arrows and the back button work for somebody who has never been in
 * the app: it is the list they are standing in, and without it they land on an
 * island. /card is public and enters guest mode by itself, which is the whole
 * point - whoever scans this has never heard of us and is holding a card.
 */
/**
 * The QR on a printed label.
 *
 * No longer needs the binder: a card has one `binder_id`, so its short code
 * resolves to both. The old form carried two UUIDs and 106 characters, which
 * forced a 41 module QR - 0.26mm per module on a 12mm strip, well under the
 * 0.4mm a phone camera needs. The short code brings it to 25 modules.
 */
const qrUrlForLabel = () => (card) => labelQrUrl(card);

/**
 * Wrapped so a grid nested inside can put its controls on the page's toolbar.
 * The provider has to sit ABOVE the component that reads the registry, which is
 * why this is a wrapper rather than a provider inside the render.
 */
const Collection = (props) => (
  <GridControlsProvider>
    <CollectionPage {...props} />
  </GridControlsProvider>
);

const CollectionPage = () => {
  const { user } = useAuth();
  const { activeProfile, userProfile } = useProfile();
  const { theme, isDark } = useTheme();
  // TCG Pocket cards are digital-only and have no price anywhere in here.
  // Keeps the set list current so a newly ingested Pocket set is badged too.
  useTcgPocketSets();
  const { addAction, setAddAction } = useUI();
  const {
    currentStep,
    handleAddCardBinderComplete,
    handleCardAdded,
    handleBinderAdded,
    handleCongratsComplete,
    handleMoreTutorials,
  } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId: viewUserId } = useParams(); // If viewing another user's collection
  const [searchParams, setSearchParams] = useSearchParams();
  const binderParam = searchParams.get('binder'); // If coming from Full Binder link
  const tabParam = searchParams.get('tab'); // Current tab
  const setParam = searchParams.get('set'); // Wish list set
  const cardSetParam = searchParams.get('cardset'); // All tab (key 'cards') set filter
  const [profile, setProfile] = useState(null);
  const [isViewingOther, setIsViewingOther] = useState(false);
  const [activeTab, setActiveTab] = useState(
    tabParam || (binderParam ? 'binders' : 'cards')
  );
  const [initialLoading, setInitialLoading] = useState(() => !readLastCards()?.length);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  // Smart search state - combined to prevent multiple re-renders
  const [smartSearch, setSmartSearch] = useState({
    open: false,
    query: '',
    expandedSection: null, // Only one section open at a time
  });
  const [tcgSearchResults, setTcgSearchResults] = useState([]);
  const [tcgSearchLoading, setTcgSearchLoading] = useState(false);
  const searchInputRef = useRef(null);
  const loadMoreObserver = useRef(null);
  // The catalogue pager is declared with the Explore-tab views, far below the
  // scroll handler that has to call it. A ref is the seam.
  const loadMoreCatalogRef = useRef(() => {});
  // Seeded from the device on the very first render, before any query runs.
  //
  // Leaving Cards for Home and coming straight back showed a full-screen
  // spinner every time, because the cache could only be read after the profile
  // query returned. Reading it synchronously here means the collection is on
  // screen in the first frame and the fetch quietly corrects it.
  //
  // It also fixes the gap: scroll restoration was landing on an empty list, so
  // it scrolled to where you had been, found nothing there, and the cards
  // appeared to slide up from underneath as they arrived.
  const [cards, setCards] = useState(() => readLastCards() || []);
  /**
   * False until the grid has drawn its first full set of cards.
   *
   * Every tile carries framer-motion's `layout` with a 0.4s transition, which
   * is right for a sort or a zoom change and wrong for arriving. The list grows
   * from nothing to the cached set to the fetched set, and each growth animated
   * every tile into position at once - which is the strange settling motion
   * that looked like an animation nobody wrote.
   */
  const [gridSettled, setGridSettled] = useState(false);
  // Layout animation is armed only for the moment after a sort, filter or
  // zoom changes. Left on all the time, framer measured every tile on every
  // render, and on a low-end Android a price wave landing turned into a
  // visible stall across the whole grid.
  const [layoutBurst, setLayoutBurst] = useState(false);
  const layoutBurstTimer = useRef(null);
  const [binders, setBinders] = useState([]);
  const [selectedBinder, setSelectedBinder] = useState(null);
  const [printSheetOpen, setPrintSheetOpen] = useState(false);
  // Preference defaults and storage key
  const PREFS_STORAGE_KEY = 'cardchase_collection_prefs';
  const DEFAULT_PREFS = {
    cardsViewMode: 'cards',
    cardZoom: 0,
    cardsSetsZoom: 0,
    cardsSetsSort: 'newest',
    cardsSetsFilter: 'all',
    setsLanguage: 'en', // 'en' | 'ja' | 'zh-tw' | etc.
    setCardsSort: 'number-asc',
    tradeFilter: 'all',
    visibilityFilter: 'all',
    cardSortBy: 'recent',
    binderSortBy: 'name-asc',
    wishListZoom: 0,
    catalogSort: DEFAULT_CATALOG_SORT,
    catalogFilters: EMPTY_CATALOG_FILTERS,
    catalogZoom: 0,
    // Search results are their OWN place, with their own three keys. The
    // owner's rule is that "the device remembers the last version in the last
    // place", and a search is a different place from the flat catalogue: value
    // high-to-low is the right order for hunting the rare Magikarp and the
    // wrong order for opening All Cards. Sharing catalogSort would have made
    // one choice overwrite the other.
    searchSort: DEFAULT_SEARCH_SORT,
    searchFilters: EMPTY_CATALOG_FILTERS,
    searchZoom: 0,
    // One remembered sort per binder, keyed by binder id. The owner: "the key
    // is for the device to remember the last version in the last place." A
    // binder is a place, and two binders are two places - a set binder wants
    // card-number order where a trade binder wants value.
    binderCardSort: {},
    // Type, rarity and era on the All tab. Same three groups the catalogue
    // offers, same ids, so a person who learns the sheet once has learned it.
    cardFilters: EMPTY_COLLECTION_FILTERS,
  };

  // Load saved preferences from localStorage
  const getSavedPrefs = () => {
    try {
      const saved = localStorage.getItem(PREFS_STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_PREFS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading preferences:', e);
    }
    return DEFAULT_PREFS;
  };

  const savedPrefs = getSavedPrefs();

  const [cardsViewMode, setCardsViewMode] = useState('cards'); // legacy in-page sets view retired in favor of /sets era flow; ignore any stale saved value
  // Explore tab (key 'all'): a drill-down menu, eras -> sets -> cards. Not a
  // flat list.
  const [allEras, setAllEras] = useState([]);
  const [allEraLogos, setAllEraLogos] = useState({});
  const [allErasLoading, setAllErasLoading] = useState(false);
  const [allEra, setAllEra] = useState(searchParams.get('era') || null); // null = era level
  // Which door of the Explore tab you are standing in. `null` is the menu
  // itself, which is where the tab now lands: eras are one way in, not the
  // only way.
  //   null    the menu
  //   'era'   eras -> sets in that era -> cards          (the original path)
  //   'sets'  every set, flat -> straight into its cards (no era in between)
  //   'cards' every card in the catalogue, flat
  //   'chase' the Chase Cards placeholder
  const [allMode, setAllMode] = useState(searchParams.get('allmode') || null);

  // The flat catalogue behind the Cards tile. 23,591 rows, so this is a real
  // server-side page rather than a display limit over a fetched-everything
  // array: nothing is asked for until the tile is opened, and then 60 at a time.
  const [catalogCards, setCatalogCards] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const catalogExhausted = useRef(false);
  const catalogFetching = useRef(false);
  const catalogCursor = useRef(0);
  // The order and the narrowing, both answered by the database. Sorting the
  // sixty rows already fetched would order the page and lie about the other
  // 23,531, which is why this screen had no sort control until now.
  const [catalogSort, setCatalogSort] = useState(savedPrefs.catalogSort || DEFAULT_CATALOG_SORT);
  // Read through the normaliser, so a preference saved before filters went
  // multi-select comes back as a one-item list rather than being dropped.
  const [catalogFilters, setCatalogFilters] = useState(
    () => normaliseFilters(savedPrefs.catalogFilters),
  );
  // How many cards the current filter matches. Counted, never guessed: the
  // loading line names this number out loud.
  const [catalogMatches, setCatalogMatches] = useState(null);
  // Thumbnail size for the catalogue grid. 0 = five up and no captions, the
  // same "small" the All tab means, for skimming 23,591 cards quickly.
  // 1 = three up, which is how this screen has always looked. 2 = two up.
  // Saved like every other zoom, so it survives leaving the screen.
  /**
   * One zoom for the whole app, not five for this screen.
   *
   * There used to be cardZoom, cardsSetsZoom, wishListZoom, catalogZoom and
   * searchZoom here, plus three more in the wish surfaces' own storage keys.
   * Setting the size in Explore left the binder none the wiser, which is how it
   * felt to use. The owner: "it's a general preference throughout, tapping one
   * impacts all zooms". Sort and filter stay per place, deliberately - those are
   * about the task, and this is about somebody's eyes.
   *
   * The aliases below are read in dozens of places and all mean the same value
   * now, so they are kept rather than renamed across the file.
   */
  const { zoom: sharedZoom, setZoom, cycleZoom } = useZoom();
  const gridControls = useGridControls();

  /**
   * A tab a half-finished selection cannot follow you to.
   *
   * Picking for the chase list happens IN Explore, so moving around inside it -
   * back to the sets, back to the eras - is fine and stays fine. Leaving it is
   * not: the selection would either vanish silently or trail you onto a screen
   * it means nothing on. The bar shakes instead, which says "still holding
   * something" without stopping to ask a question.
   */
  const leavingExploreBlocked = (target) => {
    if (!wish.selecting || target === 'all') return false;
    wish.bump();
    return true;
  };

  const cardZoom = sharedZoom;
  const cardsSetsZoom = sharedZoom;
  const wishListZoom = sharedZoom;
  const catalogZoom = sharedZoom;
  const searchZoom = sharedZoom;
  // The search's own order, narrowing and card size. Read through the same
  // normaliser as the catalogue's, so a preference saved in an older shape
  // comes back usable rather than being dropped.
  const [searchSort, setSearchSort] = useState(savedPrefs.searchSort || DEFAULT_SEARCH_SORT);
  const [searchFilters, setSearchFilters] = useState(
    () => normaliseFilters(savedPrefs.searchFilters),
  );
  const [binderCardSort, setBinderCardSort] = useState(savedPrefs.binderCardSort || {});
  const [cardFilters, setCardFilters] = useState(
    () => ({ ...EMPTY_COLLECTION_FILTERS, ...(savedPrefs.cardFilters || {}) }),
  );
  // What the collection actually contains, read from the database rather than
  // from whatever the grid has loaded so far. `null` means not answered yet,
  // which is NOT the same as "you own nothing" and must not fade anything.
  const [collectionFacets, setCollectionFacets] = useState(null);

  /** The remembered sort for the binder currently open, or the All tab's. */
  const binderSortFor = (binderId) => binderCardSort[binderId] || DEFAULT_PREFS.cardSortBy;
  const rememberBinderSort = (binderId, sortId) => {
    if (!binderId) return;
    setBinderCardSort((prev) => ({ ...prev, [binderId]: sortId }));
  };
  // A run counter, so a page that comes back after the order changed is thrown
  // away instead of being appended under the new heading.
  const catalogRun = useRef(0);
  const [allSortDir, setAllSortDir] = useState('oldest'); // oldest | newest | a-z | z-a
  // The era and set ROW grids answer to the same preference as the card grids.
  // The owner: "yes, the eras and sets zoom also, it's a general preference
  // throughout". Three up, two up, one big column - the same three steps,
  // counted the same way, so the control means one thing everywhere.
  const allZoom = sharedZoom;
  const [cardsSetsSort, setCardsSetsSort] = useState(savedPrefs.cardsSetsSort); // 'newest' | 'oldest' | 'a-z' | 'z-a'
  const [cardsSetsFilter, setCardsSetsFilter] = useState(savedPrefs.cardsSetsFilter); // 'all' | 'owned'
  const [setsLanguage, setSetsLanguage] = useState(savedPrefs.setsLanguage); // 'en' | 'ja' | 'zh-tw' | etc.
  const [setCardsSort, setSetCardsSort] = useState(savedPrefs.setCardsSort); // 'number-asc' | 'number-desc' | 'name-asc' | 'name-desc'
  const [selectedCardSet, setSelectedCardSet] = useState(cardSetParam || null); // Set ID when drilling into a set from sets view
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [cardPrices, setCardPrices] = useState({}); // { cardId: { price, formatted } }
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ done: 0, total: 0 });
  const [lastCollectionRefresh, setLastCollectionRefresh] = useState(null);
  const refreshCooldown = lastCollectionRefresh && isWithinCooldown(lastCollectionRefresh);
  const pricingEnabled = isPricingVisible(activeProfile);
  const [tradeStats, setTradeStats] = useState({
    totalTrades: 0,
    averageRating: 0,
    totalReviews: 0,
    ratingBreakdown: {
      timeliness: 0,
      communication: 0,
      fairness: 0,
      overall: 0,
    },
    // Trade funnel stats
    messagesStarted: 0,
    scheduled: 0,
    completed: 0,
    incomplete: 0,
    // Activity indicators
    avgResponseTime: null, // in hours
    lastActive: null, // timestamp
    completionRate: 0, // percentage
  });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [distanceToUser, setDistanceToUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Modal states
  const [allBadgesOpen, setAllBadgesOpen] = useState(false);
  // Remembers that All Badges was launched from the stats panel, so closing it
  // returns there instead of dumping the user back on the collection.
  const [badgesOpenedFromStats, setBadgesOpenedFromStats] = useState(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [addBinderModalOpen, setAddBinderModalOpen] = useState(false);
  const [addToBinderModalOpen, setAddToBinderModalOpen] = useState(false);
  // Old scan modals removed - now using navigate('/scanner')
  const [sortFilterModalOpen, setSortFilterModalOpen] = useState(false);
  const [wishListItems, setWishListItems] = useState([]);
  const [wishListLoading, setWishListLoading] = useState(false);
  const [selectedWishListSet, setSelectedWishListSet] = useState(setParam || null); // null = sets view, 'all' = all cards, or set_id
  const [wishListMatches, setWishListMatches] = useState([]); // Cards they have that YOU want
  const [reverseMatches, setReverseMatches] = useState([]); // Cards YOU have that THEY want
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchCheckTrigger, setMatchCheckTrigger] = useState(0); // Increment to force re-check
  const [allSets, setAllSets] = useState([]);
  const [allSetsLoading, setAllSetsLoading] = useState(false);
  const [setsDisplayLimit, setSetsDisplayLimit] = useState(30);
  const [cardsDisplayLimit, setCardsDisplayLimit] = useState(30);
  const [tcgSetCards, setTcgSetCards] = useState([]); // Cards for selected set from TCGdex
  /* A set is its PRINTINGS, not its unique cards.
     Base Set is 102 cards and 408 slots, and a person who has only ever seen
     the 102 has no way to learn that the Charizard they are holding is one of
     four. So the set view draws the same (card, printing) slots the Wish tab's
     master set grid draws, from the same `wish_set_slot_grid` read, and these
     rows are the source of the printing badge and of that printing's own price.
     Ownership here still comes from `cards` and `wishListItems`, the way it
     always has on this screen, so the All / Owned / Wish chips keep their
     card-level meaning. */
  const [setSlots, setSetSlots] = useState([]);
  const [setSlotsLoading, setSetSlotsLoading] = useState(false);
  // The printing the grid is narrowed to, as a printing key. Null is every
  // printing, which is what a set IS and therefore the default.
  const [setPrinting, setSetPrinting] = useState(null);
  const [printingSheetOpen, setPrintingSheetOpen] = useState(false);
  // How much of a 408-tile grid is in the DOM. The owner tests on a phone, and
  // four hundred mounted tiles is four hundred subtrees before the first scroll,
  // so the grid grows as it is scrolled the way the Wish tab's slot grid does.
  const [setSlotsShown, setSetSlotsShown] = useState(SET_SLOT_FIRST_PAGE);
  const setSlotSentinelRef = useRef(null);
  // Prices for cards you are browsing rather than own: catalogue set cards and
  // wish list rows, keyed by catalogue id. Asked for once per id.
  const [catPrices, setCatPrices] = useState({});
  const catAskedRef = useRef(new Set());
  // Sum of plain printings per set, from set_price_totals. Eras add theirs up.
  const [setTotals, setSetTotals] = useState({});
  const [tcgSetCardsLoading, setTcgSetCardsLoading] = useState(false);
  const [setCardFilter, setSetCardFilter] = useState('all'); // 'all' | 'owned' | 'wish'
  // Select mode. Entered by holding a card; `bulkEditMode` is 'select' while
  // on, null otherwise. Older code only ever checks it for truthiness.
  const [bulkEditMode, setBulkEditMode] = useState(null);
  const [binderPickerOpen, setBinderPickerOpen] = useState(false);
  // Print opens the rounding pass, not the settings sheet. Settings is a detour
  // from there for when the roll or the printer changes.
  const [roundOpen, setRoundOpen] = useState(false);
  // Seeded from the live connection rather than always starting at false.
  // The printer stays linked between visits, and a screen that opens saying
  // "Connect and print" to somebody already connected is asking for a step
  // that has already happened.
  // One printer path for every screen; see hooks/usePrintLabels.
  const printLink = usePrintLabels({ qrUrlFor: qrUrlForLabel() });
  // Held binder: the Share / Rename / Delete sheet for it.
  const [heldBinder, setHeldBinder] = useState(null);
  const [selectBusy, setSelectBusy] = useState(false);
  // Set by a long press so the click that follows it does not open the card.
  const longPressFiredRef = useRef(false);
  const [pressingId, setPressingId] = useState(null);
  const [poppedId, setPoppedId] = useState(null);
  // Card whose hold has fired and is waiting on the finger to lift.
  const armedRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]); // Card IDs selected for bulk edit
  // Catalogue facts the saved row does not carry: hp, types, set and release
  // date, keyed by catalogue id. Filled once per id, separate from `cards` so
  // the three load waves never see it change.
  const [cardMeta, setCardMeta] = useState({});
  const cardMetaAskedRef = useRef(new Set());

  // Sort/Filter state (persisted)
  const [tradeFilter, setTradeFilter] = useState(savedPrefs.tradeFilter); // 'all' | 'pc' | 'tradable'
  const [visibilityFilter, setVisibilityFilter] = useState(savedPrefs.visibilityFilter); // 'all' | 'public' | 'private'
  // imageFilter reads from global preference (shared with Home, Preferences, Profile)
  const [imageFilter, setImageFilter] = useState(() => {
    return localStorage.getItem('imagePreference') === 'photo' ? 'photo' : 'digital';
  }); // 'digital' | 'photo'
  // The All tab's own remembered sort - the tab keyed 'cards'. A binder does
  // not use this - it has its own, in binderCardSort - so the sort function
  // resolves which applies.
  const [tabCardSortBy, setCardSortBy] = useState(savedPrefs.cardSortBy);
  // Which sort actually applies right now. A binder remembers its own, so
  // opening a set binder does not re-order a trade binder, and neither
  // disturbs the All tab. Every consumer below reads this.
  const cardSortBy = selectedBinder ? binderSortFor(selectedBinder.id) : tabCardSortBy;
  const [binderSortBy, setBinderSortBy] = useState(savedPrefs.binderSortBy); // newest, oldest, name-asc, name-desc, value-desc
  const [wishListSortBy, setWishListSortBy] = useState('newest'); // 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'recent'

  // The chase card hold menu lives inside the Wish tab now, with the rest of
  // the chase surfaces, so its open state went with it.
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('info');

  // Toast helper
  const showToast = useCallback((message, type = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  // Ask the catalogue for sort facts on ids we have not asked about yet. Keyed
  // on the id list, not the card objects, so a price or flag update on a card
  // does not refetch.
  const cardIdKey = useMemo(() => cards.map((c) => c.card_id).filter(Boolean).sort().join('|'), [cards]);
  useEffect(() => {
    const wanted = cardIdKey ? cardIdKey.split('|').filter((id) => !cardMetaAskedRef.current.has(id)) : [];
    if (wanted.length === 0) return undefined;
    wanted.forEach((id) => cardMetaAskedRef.current.add(id));
    // Never cancelled. The collection arrives in waves, and a wave that lands
    // while the previous fetch is in flight must not throw that answer away,
    // or the first cards on screen never learn their HP, type or set.
    cardData.getCardMeta(wanted)
      .then((meta) => { if (Object.keys(meta).length) setCardMeta((prev) => ({ ...prev, ...meta })); })
      .catch((err) => {
        console.warn('[collection] card meta', err?.message || err);
        wanted.forEach((id) => cardMetaAskedRef.current.delete(id));
      });
  }, [cardIdKey]);

  // Set values, one read, cached for the session.
  useEffect(() => {
    if (!pricingEnabled) return undefined;
    let alive = true;
    supabase.from('set_price_totals').select('set_id, priced_cards, priced_printings, total_near_mint')
      .then(({ data }) => {
        if (!alive || !data) return;
        const map = {};
        for (const row of data) map[row.set_id] = row;
        setSetTotals(map);
      });
    return () => { alive = false; };
  }, [pricingEnabled]);

  // Card prices for whatever catalogue cards are on screen: the open set in
  // All, and the wish list. Waves of 40 so the first rows paint first.
  const catLookupKey = useMemo(() => {
    const ids = [];
    if (selectedCardSet) for (const c of tcgSetCards) ids.push(tcgKey(selectedCardSet, c.localId));
    for (const w of wishListItems) if (w.tcgdex_card_id) ids.push(w.tcgdex_card_id);
    return ids.sort().join('|');
  }, [selectedCardSet, tcgSetCards, wishListItems]);
  useEffect(() => {
    if (!pricingEnabled || !catLookupKey) return undefined;
    const lookups = [];
    // A TCG Pocket set is 300-odd cards that can never be priced. Asking would
    // be 300 round trips for 300 blanks.
    if (selectedCardSet && !isTcgPocketSet(selectedCardSet)) {
      const setInfo = allSets.find((x) => x.id === selectedCardSet);
      for (const c of tcgSetCards) {
        const id = tcgKey(selectedCardSet, c.localId);
        if (catAskedRef.current.has(id)) continue;
        lookups.push({ id, name: c.name, set_name: c.set?.name || setInfo?.name || '', set_number: c.localId, variant: '', grade: 'NM' });
      }
    }
    for (const w of wishListItems) {
      const id = w.tcgdex_card_id;
      if (!id || catAskedRef.current.has(id) || isTcgPocketCard(w)) continue;
      const localId = id.replace(`${w.set_id}-`, '');
      lookups.push({ id, name: w.card_name, set_name: w.set_name || '', set_number: localId, variant: '', grade: 'NM' });
    }
    if (lookups.length === 0) return undefined;
    lookups.forEach((l) => catAskedRef.current.add(l.id));
    // The cache keys on the exact printing and stores numbers without their
    // leading zeros. The catalogue knows neither, so the plain printing is
    // asked for first and whatever is still unpriced is asked for as holo,
    // which is what a card with no plain printing was.
    const shaped = (variant) => lookups.map((l) => ({ ...l, variant, set_number: String(l.set_number).replace(/^0+(?=\d)/, '') }));
    const got = new Set();
    const apply = (wave, found) => {
      const add = {};
      for (const [id, result] of found) {
        if (result?.price != null && result.price > 0) { add[id] = { price: result.price, formatted: result.formatted }; got.add(id); }
      }
      if (Object.keys(add).length) setCatPrices((prev) => ({ ...prev, ...add }));
    };
    (async () => {
      await loadInWaves(shaped('normal'), { read: (wave) => getBulkCardPrices(wave), apply });
      const rest = shaped('holo').filter((l) => !got.has(l.id));
      if (rest.length) await loadInWaves(rest, { read: (wave) => getBulkCardPrices(wave), apply });
    })().catch(() => {
      // Asked, not answered. Let a later screen ask again.
      lookups.forEach((l) => catAskedRef.current.delete(l.id));
    });
    // Not cancelled on re-run: the collection lesson applies here too. A wave
    // that lands late is still the right answer for those ids.
    return undefined;
  }, [pricingEnabled, catLookupKey]);

  // Keep the last known prices on the device for the next cold open.
  useEffect(() => {
    if (isViewingOther || !currentUserId) return undefined;
    const ids = Object.keys(cardPrices);
    if (ids.length === 0) return undefined;
    const t = setTimeout(() => writePrices(currentUserId, cardPrices), 1500);
    return () => clearTimeout(t);
  }, [cardPrices, currentUserId, isViewingOther]);

  useEffect(() => {
    if (!gridSettled) return undefined;
    setLayoutBurst(true);
    if (layoutBurstTimer.current) clearTimeout(layoutBurstTimer.current);
    layoutBurstTimer.current = setTimeout(() => setLayoutBurst(false), 600);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardSortBy, cardZoom, tradeFilter, visibilityFilter, imageFilter, searchQuery, selectedCardSet, selectedBinder?.id]);

  // Save preferences to localStorage when they change
  useEffect(() => {
    const prefs = {
      cardsViewMode,
      cardZoom,
      cardsSetsZoom,
      cardsSetsSort,
      cardsSetsFilter,
      setsLanguage,
      setCardsSort,
      tradeFilter,
      visibilityFilter,
      cardSortBy,
      binderSortBy,
      wishListZoom,
      catalogSort,
      catalogFilters,
      catalogZoom,
      searchSort,
      searchFilters,
      searchZoom,
      binderCardSort,
      cardFilters,
    };
    try {
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      console.error('Error saving preferences:', e);
    }
  }, [cardsViewMode, cardZoom, cardsSetsZoom, cardsSetsSort, cardsSetsFilter, setsLanguage, setCardsSort, tradeFilter, visibilityFilter, cardSortBy, binderSortBy, wishListZoom, catalogSort, catalogFilters, catalogZoom, searchSort, searchFilters, searchZoom, binderCardSort, cardFilters]);

  // Sync imageFilter with global preference and save when changed
  useEffect(() => {
    localStorage.setItem('imagePreference', imageFilter);
  }, [imageFilter]);

  // Sync when returning to page (preference may have changed in Preferences screen)
  useEffect(() => {
    const syncImagePreference = () => {
      const pref = localStorage.getItem('imagePreference');
      setImageFilter(pref === 'photo' ? 'photo' : 'digital');
    };
    window.addEventListener('focus', syncImagePreference);
    return () => window.removeEventListener('focus', syncImagePreference);
  }, []);

  // Reset preferences to defaults
  const resetPreferences = useCallback(() => {
    setCardsViewMode(DEFAULT_PREFS.cardsViewMode);
    setZoom(0); // one preference now, so one thing to put back
    setCardsSetsSort(DEFAULT_PREFS.cardsSetsSort);
    setCardsSetsFilter(DEFAULT_PREFS.cardsSetsFilter);
    setSetsLanguage(DEFAULT_PREFS.setsLanguage);
    setSetCardsSort(DEFAULT_PREFS.setCardsSort);
    setTradeFilter(DEFAULT_PREFS.tradeFilter);
    setVisibilityFilter(DEFAULT_PREFS.visibilityFilter);
    setImageFilter('digital'); // Reset to digital (default)
    setCardSortBy(DEFAULT_PREFS.cardSortBy);
    setBinderSortBy(DEFAULT_PREFS.binderSortBy);
    setCatalogSort(DEFAULT_CATALOG_SORT);
    setCatalogFilters(EMPTY_CATALOG_FILTERS);
    setSearchSort(DEFAULT_SEARCH_SORT);
    setSearchFilters(EMPTY_CATALOG_FILTERS);
    setBinderCardSort({});
    setCardFilters(EMPTY_COLLECTION_FILTERS);
    showToast('Preferences reset to defaults');
  }, [showToast]);

  // Get themed styles
  const styles = getStyles(theme);

  // Sync URL params with state
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Update tab param
    if (activeTab && activeTab !== 'cards') {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }

    // Update set param for wish list
    if (activeTab === 'wishlist' && selectedWishListSet) {
      params.set('set', selectedWishListSet);
    } else {
      params.delete('set');
    }

    // Update cardset param for the set drill-down.
    //
    // This used to be written for the All tab only, which meant a set opened
    // from Explore left no history entry: backing out of it skipped the set
    // level entirely and landed two screens up. Safe to widen — selectedCardSet
    // is seeded from this same param on mount, so there is no window where the
    // effect runs before the value exists and writes a null over a real one.
    if ((activeTab === 'cards' || activeTab === 'all') && selectedCardSet) {
      params.set('cardset', selectedCardSet);
    } else {
      params.delete('cardset');
    }

    // Which Explore-tab door is open. The menu is the absence of this param, so
    // back from any list lands on the menu rather than leaving the tab.
    if (activeTab === 'all' && allMode) {
      params.set('allmode', allMode);
    } else {
      params.delete('allmode');
    }

    // Explore-tab era drill-down. Without this, coming back to Explore always
    // lands on the era list even though the tab itself was remembered.
    if (activeTab === 'all' && allEra) {
      params.set('era', allEra);
    } else {
      params.delete('era');
    }

    // The open binder lives in the URL too. Without it, coming back from a
    // card opened inside a binder restored the tab and dropped you on the
    // binder list instead of the binder you were in.
    if (selectedBinder?.id) {
      params.set('binder', selectedBinder.id);
    } else if (binders.length > 0) {
      // Only once binders are known. On the way back from a card this effect
      // runs before they load, and clearing here wiped the binder the restore
      // was about to read.
      params.delete('binder');
    }

    // Update URL - push to history for back button support
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(params);
    }
  }, [activeTab, selectedWishListSet, selectedCardSet, allEra, allMode, selectedBinder?.id]);

  // Listen for URL param changes (back/forward buttons)
  useEffect(() => {
    const newTab = searchParams.get('tab') || 'cards';
    const newSet = searchParams.get('set') || null;
    const newCardSet = searchParams.get('cardset') || null;
    const newEra = searchParams.get('era') || null;
    const newMode = searchParams.get('allmode') || null;

    if (newEra !== allEra) {
      setAllEra(newEra);
    }
    if (newMode !== allMode) {
      setAllMode(newMode);
    }
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
    if (newSet !== selectedWishListSet) {
      setSelectedWishListSet(newSet);
    }
    if (newCardSet !== selectedCardSet) {
      setSelectedCardSet(newCardSet);
    }
    // Binder from the URL, once binders are known. Back from a card lands
    // here with the binder still in the query string.
    const newBinderId = searchParams.get('binder') || null;
    if (newBinderId !== (selectedBinder?.id || null) && binders.length) {
      setSelectedBinder(newBinderId ? (binders.find((b) => b.id === newBinderId) || null) : null);
    }
  }, [searchParams, binders]);

  // Put the scroller back where it was when you left this tab. Runs once, and
  // retries briefly because the list it needs to scroll through is still
  // arriving from the cache or the network.
  useEffect(() => {
    const target = recallScroll('cards');
    if (!target) return undefined;

    let tries = 0;
    const id = setInterval(() => {
      const scroller = document.querySelector('.app-content');
      tries += 1;
      if (scroller && scroller.scrollHeight > target) {
        scroller.scrollTop = target;
        clearInterval(id);
      } else if (tries > 20) {
        clearInterval(id);
      }
    }, 50);

    return () => clearInterval(id);
  }, []);

  // Fetch user profile and data
  useEffect(() => {
    let cancelled = false;

    // Everything the grid needs, and nothing it does not.
    //
    // This used to be eight awaits in a row - profile, follows, cards, binders,
    // wish list, ratings, trades, messages - each waiting on the one before it.
    // On wifi that is about a second. On cellular, where a round trip is 400 to
    // 600ms, it is six seconds of blank screen before a single card appears.
    //
    // Now it runs in two phases. Phase one is the three queries the screen
    // actually renders from, fired together, and the screen paints the moment
    // they land. Phase two is the header statistics, which nothing waits for.
    const loadCore = async () => {
      const isGuestViewingOther = !user && isGuest() && viewUserId;
      if ((!user || !activeProfile) && !isGuestViewingOther) {
        setInitialLoading(false);
        return null;
      }

      if (activeProfile?.id) setCurrentUserId(activeProfile.id);

      let profileData;

      if (viewUserId) {
        // Someone else's collection, so someone else's profile: public_profiles
        // rather than users. This is also the path a shared /b/:token binder
        // lands on after enterGuestMode, so it has to work for anon, and the
        // view is granted to anon for exactly that reason.
        const { data } = await supabase
          .from('public_profiles')
          .select('id, username, avg_rating, total_ratings, successful_trades, successful_reports, image_contributions, auth_id, location_sharing_enabled, distance_mi')
          .eq('id', viewUserId)
          .single();
        if (cancelled) return null;
        profileData = data;
        setIsViewingOther(data?.id !== activeProfile?.id);

        // distance_mi is already measured from the viewer's own stored
        // location, so the haversine that used to live here is gone along with
        // the coordinates it needed. The owner's opt-in is still the gate.
        if (data && activeProfile?.id && data.id !== activeProfile.id && data.location_sharing_enabled && data.distance_mi != null) {
          setDistanceToUser(data.distance_mi);
        }
      } else {
        // Own collection: the profile is already in memory. No round trip at all.
        profileData = activeProfile;
        setIsViewingOther(false);
      }

      if (!profileData) {
        setInitialLoading(false);
        return null;
      }
      setProfile(profileData);
      setWishListLoading(true);

      // Whatever was here last time, painted before the network is asked
      // anything. A collection does not change much between two openings, and
      // the alternative was a blank screen for as long as the request took.
      writeLastProfile(profileData.id);
      const cached = readCards(profileData.id);
      if (cached && !cancelled) {
        setCards(cached);
        setInitialLoading(false);
      }
      // Last known prices too, so a value sort is in order on the first paint.
      const cachedPrices = readPrices(profileData.id);
      if (cachedPrices && !cancelled) {
        setCardPrices((prev) => ({ ...cachedPrices, ...prev }));
      }

      // The first page and the rest are two requests, not one. The screen only
      // shows a handful of cards at a time, so waiting on the whole collection
      // to draw the first row is time spent for nothing.
      // Ordered by id as well. A bulk scan saves many cards with one timestamp,
      // and pages sorted on the timestamp alone had no stable order between
      // requests: a card could land on two pages while another fell between.
      const cardsQuery = (from, to) => supabase.from('cards').select('*')
        .eq('user_id', profileData.id).is('deleted_at', null)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);

      const [cardsRes, bindersRes, wishRes] = await Promise.all([
        cardsQuery(0, FIRST_PAGE - 1),
        supabase.from('binders').select('*')
          .eq('user_id', profileData.id)
          .match(PC_ONLY)
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        supabase.from('wish_list').select('*', { count: 'exact' })
          .eq('user_id', profileData.id)
          .order('added_at', { ascending: false }),
      ]);

      if (cancelled) return null;

      if (cardsRes.data) {
        setCards(cardsRes.data);
        setInitialLoading(false);

        // Written now, not only when the whole collection has arrived.
        //
        // The cache used to be saved at the end of the tail loop, so leaving
        // Cards before every page landed wrote nothing at all - and on a big
        // collection over cellular that is most visits. The first page is
        // already the useful part; the tail overwrites it a moment later.
        writeCards(profileData.id, cardsRes.data);

        // The tail, once the screen is already usable. Only asked for when the
        // first page came back full, because a short first page is the whole
        // collection.
        if (cardsRes.data.length === FIRST_PAGE) {
          (async () => {
            const all = [...cardsRes.data];
            let from = FIRST_PAGE;
            // Paged rather than one open-ended request: a single unbounded
            // select over a large collection is what made this slow, and a
            // dropped page costs one page rather than everything.
            const seen = new Set(all.map((c) => c.id));
            for (let guard = 0; guard < 40; guard += 1) {
              const { data, error } = await cardsQuery(from, from + 199);
              if (cancelled || error || !data?.length) break;
              // Belt and braces: a row already on screen never appears twice.
              for (const row of data) {
                if (!seen.has(row.id)) { seen.add(row.id); all.push(row); }
              }
              setCards([...all]);
              if (data.length < 200) break;
              from += 200;
            }
            if (!cancelled) writeCards(profileData.id, all);
          })();
        } else {
          // Already written above; nothing more to do for a short collection.
        }
      }

      if (bindersRes.data) {
        setBinders(bindersRes.data);
        if (binderParam) {
          const targetBinder = bindersRes.data.find(b => b.id === binderParam);
          if (targetBinder) setSelectedBinder(targetBinder);
        }
      }

      setWishListItems(wishRes.data || []);
      setWishListLoading(false);

      // The screen is renderable here. Everything below is decoration.
      setInitialLoading(false);
      return profileData;
    };

    // Header counts, ratings and trade activity. Slow, and nothing on screen
    // blocks on them, so they arrive when they arrive.
    const loadStats = async (profileData) => {
      if (viewUserId && profileData && activeProfile?.id && profileData.id !== activeProfile.id) {
        const { data: followData } = await supabase
          .from('user_follows').select('id')
          .eq('follower_id', activeProfile.id).eq('following_id', viewUserId)
          .single();
        if (!cancelled) setIsFollowing(!!followData);
      }

      const [ratingsRes, activity, tradesForResponse] = await Promise.all([
        supabase.from('trade_reviews')
          .select('communication_rating, fairness_rating, timeliness_rating, overall_rating')
          .eq('reviewed_user_id', profileData.id),

        Promise.all([
          supabase.from('trade_requests').select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${profileData.id},card_owner_id.eq.${profileData.id}`)
            .eq('status', 'accepted'),
          (async () => {
            const { data: userTrades } = await supabase.from('trade_requests').select('id')
              .or(`requester_id.eq.${profileData.id},card_owner_id.eq.${profileData.id}`);
            if (!userTrades || userTrades.length === 0) return { count: 0 };
            return supabase.from('trade_schedules').select('id', { count: 'exact', head: true })
              .in('trade_id', userTrades.map(t => t.id)).eq('status', 'confirmed');
          })(),
          supabase.from('meetups').select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${profileData.id},receiver_id.eq.${profileData.id}`)
            .eq('status', 'completed'),
          supabase.from('meetups').select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${profileData.id},receiver_id.eq.${profileData.id}`)
            .eq('status', 'incomplete'),
          supabase.from('messages').select('created_at')
            .eq('sender_id', profileData.id)
            .order('created_at', { ascending: false }).limit(1).single(),
        ]),

        supabase.from('trade_requests').select('id')
          .or(`requester_id.eq.${profileData.id},card_owner_id.eq.${profileData.id}`)
          .eq('status', 'accepted'),
      ]);

      if (cancelled) return;

      const ratingsData = ratingsRes.data;
      let ratingBreakdown = { timeliness: 0, communication: 0, fairness: 0, overall: 0 };

      if (ratingsData && ratingsData.length > 0) {
        let totalTimeliness = 0, totalComm = 0, totalFairness = 0, totalOverall = 0;
        let countTimeliness = 0, countComm = 0, countFairness = 0, countOverall = 0;

        ratingsData.forEach(rating => {
          if (rating.timeliness_rating) { totalTimeliness += rating.timeliness_rating; countTimeliness++; }
          if (rating.communication_rating) { totalComm += rating.communication_rating; countComm++; }
          if (rating.fairness_rating) { totalFairness += rating.fairness_rating; countFairness++; }
          if (rating.overall_rating) { totalOverall += rating.overall_rating; countOverall++; }
        });

        ratingBreakdown = {
          timeliness: countTimeliness > 0 ? totalTimeliness / countTimeliness : 0,
          communication: countComm > 0 ? totalComm / countComm : 0,
          fairness: countFairness > 0 ? totalFairness / countFairness : 0,
          overall: countOverall > 0 ? totalOverall / countOverall : 0,
        };
      }

      const [messagesStartedResult, scheduledResult, completedResult, incompleteResult, lastMessageResult] = activity;
      const messagesStarted = messagesStartedResult?.count || 0;
      const scheduled = scheduledResult?.count || 0;
      const completed = completedResult?.count || 0;
      const incomplete = incompleteResult?.count || 0;
      const lastActive = lastMessageResult?.data?.created_at || null;

      let avgResponseTime = null;
      const userTradesForResponse = tradesForResponse.data;

      if (userTradesForResponse && userTradesForResponse.length > 0) {
        const { data: responseMessages } = await supabase
          .from('messages')
          .select('sender_id, created_at, trade_request_id')
          .in('trade_request_id', userTradesForResponse.map(t => t.id))
          .order('created_at', { ascending: true })
          .limit(200);

        if (cancelled) return;
        const responseTimeResult = responseMessages || [];

        if (responseTimeResult.length > 1) {
          const messagesByTrade = {};
          responseTimeResult.forEach(msg => {
            if (!messagesByTrade[msg.trade_request_id]) messagesByTrade[msg.trade_request_id] = [];
            messagesByTrade[msg.trade_request_id].push(msg);
          });

          const responseTimes = [];
          Object.values(messagesByTrade).forEach(tradeMessages => {
            for (let i = 1; i < tradeMessages.length; i++) {
              const prevMsg = tradeMessages[i - 1];
              const currMsg = tradeMessages[i];
              if (prevMsg.sender_id !== profileData.id && currMsg.sender_id === profileData.id) {
                const responseTime = new Date(currMsg.created_at) - new Date(prevMsg.created_at);
                if (responseTime > 0 && responseTime < 72 * 60 * 60 * 1000) responseTimes.push(responseTime);
              }
            }
          });

          if (responseTimes.length > 0) {
            const avgMs = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
            avgResponseTime = avgMs / (1000 * 60 * 60);
          }
        }
      }

      if (cancelled) return;

      const cancelledCount = profileData.trades_cancelled || 0;
      const cancelledLastMinute = profileData.trades_cancelled_last_minute || 0;
      const completionRate = (completed + cancelledCount) > 0
        ? Math.round((completed / (completed + cancelledCount)) * 100)
        : 0;
      const calculatedAvgRating = ratingBreakdown.overall || profileData.avg_rating || profileData.average_rating || 0;

      setTradeStats({
        totalTrades: profileData.successful_trades || profileData.completed_trades_count || 0,
        averageRating: calculatedAvgRating,
        totalReviews: ratingsData?.length || profileData.total_ratings || 0,
        ratingBreakdown,
        messagesStarted,
        scheduled,
        completed,
        incomplete,
        cancelled: cancelledCount,
        cancelledLastMinute,
        avgResponseTime,
        lastActive,
        completionRate,
      });
    };

    (async () => {
      const profileData = await loadCore();
      if (profileData && !cancelled) {
        loadStats(profileData).catch((err) => console.warn('[Collection] stats failed:', err?.message));
      }
    })();

    return () => { cancelled = true; };
  }, [user, activeProfile?.id, viewUserId, binderParam]);

  // When viewing another user, reset view to show their cards directly
  useEffect(() => {
    if (viewUserId) {
      setCardsViewMode('cards');
      setTradeFilter('all');
      setVisibilityFilter('all');
      setCardsDisplayLimit(30);
      setSearchQuery('');
    }
  }, [viewUserId]);

  // Which cards this screen has already read a price row for, and what came
  // back. Refs rather than state: they steer the next read, they do not draw
  // anything, and putting them in state would re-run the effect that fills them.
  const pricedIdsRef = useRef(new Set());
  const priceRowsRef = useRef(new Map());

  // A different collection is a different set of prices. Our own starts from
  // the last known prices on this device, so a value sort is in order on the
  // first paint; this effect runs on mount too, so it must not clear them.
  useEffect(() => {
    pricedIdsRef.current = new Set();
    priceRowsRef.current = new Map();
    setCardPrices(viewUserId ? {} : (readPrices(activeProfile?.id) || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId]);

  // Fetch prices from card_prices cache table
  // How many cards to price before the rest, roughly the first few rows.
  // The grid is auto-fill, so this is a screenful at any zoom rather than an
  // exact row count.
  const PRICE_HEAD = 24;

  useEffect(() => {
    if (!cards.length || !pricingEnabled) return;

    let cancelled = false;

    const fetchPrices = async () => {
      try {
      const lookup = (card) => ({
        id: card.id,
        name: card.english_name || card.name,
        set_name: card.set_name || '',
        set_number: card.set_number || card.card_id?.split('-').pop() || '',
        grade: card.grade || 'NM',
        variant: card.variant || '',
      });

      // The collection arrives in three goes - the local cache, the first page,
      // then the tail - and each one used to re-read prices for every card and
      // replace the whole badge map with the result. Three full reads, the last
      // one deciding what you finally saw, which is where the wait on an
      // already-warm screen came from.
      const cardLookups = await runPriceWaves({
        // TCG Pocket cards are digital only. There is nothing to look up and
        // the badge already says so, so they never enter the wave.
        cards: cards.filter((c) => !isTcgPocketCard(c)).map(lookup),
        claimed: pricedIdsRef.current,
        headSize: PRICE_HEAD,
        isCancelled: () => cancelled,
        read: (wave) => getBulkCardPrices(wave),
        apply: (wave, found) => {
          for (const [cardId, result] of found) priceRowsRef.current.set(cardId, result);
          setCardPrices(prev => {
            const next = { ...prev };
            for (const [cardId, result] of found) {
              if (result.price != null && result.price > 0) {
                next[cardId] = { price: result.price, formatted: result.formatted };
              }
            }
            return next;
          });
        },
      });
      if (cancelled || cardLookups.length === 0) return;

      const cachedResults = priceRowsRef.current;
      console.log('[Collection] Priced', cachedResults.size, 'of', cards.length, 'cards');

      // Only set cooldown if majority of cards have cached prices (full refresh was done)
      // Don't lock the button from stray individual card caches
      const cachedRatio = cachedResults.size / cards.length;
      if (cachedRatio > 0.5) {
        let oldestRefresh = null;
        for (const [, result] of cachedResults) {
          const ts = result.row?.last_refreshed_at;
          if (ts && (!oldestRefresh || new Date(ts) < new Date(oldestRefresh))) {
            oldestRefresh = ts;
          }
        }
        if (oldestRefresh) {
          setLastCollectionRefresh(oldestRefresh);
        }
      }

      // Backfill anything still unpriced, quietly, behind the prices above.
      //
      // This used to run in front of the user: a progress bar, and then one
      // re-read of the whole collection once every card had come back. The
      // cached prices were already on screen, so the only thing the bar
      // announced was a wait for cards that had no price to show either way.
      // Now it runs unannounced, in the order the cards appear, and each batch
      // lands as it arrives.
      if (!cancelled) {
        const missing = cardLookups.filter(c => {
          const hit = cachedResults.get(c.id);
          return needsPricing(hit?.row, hit?.price);
        });
        if (missing.length > 0) {
          console.log('[Collection] Backfilling', missing.length, 'of', cardLookups.length, 'cards due for pricing');
          await autoFetchMissingPrices(
            missing,
            null,
            cachedResults,
            async (batch) => {
              if (cancelled) return;
              // The fetch works in printings, the screen works in cards, and
              // two copies of one printing are two cards. Reading back only the
              // batch would price the first Xerneas BREAK and leave the second
              // blank until the next visit.
              const done = new Set(batch.map(c => priceKey({
                name: c.name, setName: c.set_name, setNumber: c.set_number, variant: c.variant,
              })));
              const affected = cardLookups.filter(c => done.has(priceKey({
                name: c.name, setName: c.set_name, setNumber: c.set_number, variant: c.variant,
              })));
              const fresh = await getBulkCardPrices(affected);
              if (cancelled) return;
              for (const [cardId, result] of fresh) priceRowsRef.current.set(cardId, result);
              setCardPrices(prev => {
                const next = { ...prev };
                for (const [cardId, result] of fresh) {
                  if (result.price != null && result.price > 0) {
                    next[cardId] = { price: result.price, formatted: result.formatted };
                  }
                }
                return next;
              });
            },
          );
        }
      }
      } catch (err) {
        console.error('[Collection] Price fetch error:', err);
      }
    };

    fetchPrices();
    return () => { cancelled = true; };
  }, [cards, pricingEnabled, isViewingOther]);

  // Total collection value
  const totalCollectionValue = useMemo(() => {
    const total = Object.values(cardPrices).reduce((sum, p) => sum + (p.price || 0), 0);
    return total > 0 ? formatPrice(total) : null;
  }, [cardPrices]);

  // Badge data for progress rings
  const badgeData = useMemo(() => {
    const qualifyingBinders = binders.filter(b => {
      const binderCardCount = cards.filter(c => c.binder_id === b.id).length;
      return binderCardCount >= 10;
    }).length;

    return {
      completedTrades: Math.max(tradeStats.completed || 0, profile?.successful_trades || 0),
      successfulReports: profile?.successful_reports || 0,
      imageContributions: profile?.image_contributions || 0,
      totalCards: cards.length,
      qualifyingBinders,
      charterMember: profile?.charter_member ? 1 : 0,
    };
  }, [tradeStats.completed, profile, cards, binders]);

  // Check collector/organized badges when counts change
  useEffect(() => {
    if (!isViewingOther && badgeData.totalCards > 0) {
      window.dispatchEvent(new CustomEvent('badge-check', {
        detail: { badgeType: 'collector', count: badgeData.totalCards },
      }));
    }
    if (!isViewingOther && badgeData.qualifyingBinders > 0) {
      window.dispatchEvent(new CustomEvent('badge-check', {
        detail: { badgeType: 'organized', count: badgeData.qualifyingBinders },
      }));
    }
  }, [badgeData.totalCards, badgeData.qualifyingBinders, isViewingOther]);

  // Handle add action from bottom nav
  useEffect(() => {
    if (addAction === 'card') {
      navigate('/bulk-scanner');
      setAddAction(null);
    } else if (addAction === 'binder') {
      setAddBinderModalOpen(true);
      setAddAction(null);
    }
  }, [addAction, setAddAction, navigate]);


  // Detect wish list matches when viewing another user's collection
  // Checks BOTH directions: cards they have that you want, AND cards you have that they want
  useEffect(() => {
    const detectMatches = async () => {
      // Only run when viewing someone else's collection
      if (!isViewingOther || !viewUserId || !activeProfile?.id) {
        setWishListMatches([]);
        setReverseMatches([]);
        return;
      }

      try {
        // === Direction 1: Cards THEY have that YOU want ===
        // Only the ranked ten. Matching used to read the entire wish list and
        // ignore is_priority in both directions, so a card parked deep in the
        // list pinged a stranger's collection the same as a chase card. Wish
        // binders and master sets live in other tables and never reach here.
        const { data: myWishList } = await supabase
          .from('wish_list')
          .select('tcgdex_card_id, variant')
          .eq('user_id', activeProfile.id)
          .eq('is_priority', true);

        let cardsTheyHaveThatIWant = [];
        if (myWishList && myWishList.length > 0) {
          const myWishListCardIds = myWishList.map(w => w.tcgdex_card_id).filter(Boolean);

          // Find matching cards in the other user's collection (any trading status)
          const { data: matchingCards } = await supabase
            .from('cards')
            .select('*')
            .eq('user_id', viewUserId)
            .is('deleted_at', null)
            .in('card_id', myWishListCardIds);

          if (matchingCards && matchingCards.length > 0) {
            cardsTheyHaveThatIWant = matchingCards.map(card => {
              const wishItem = myWishList.find(w => w.tcgdex_card_id === card.card_id);
              return {
                ...card,
                wishListVariant: wishItem?.variant || 'Normal',
                matchType: 'theyHave'
              };
            });
          }
        }

        // === Direction 2: Cards YOU have that THEY want ===
        // Their ranked ten, for the same reason.
        const { data: theirWishList } = await supabase
          .from('wish_list')
          .select('tcgdex_card_id, variant')
          .eq('user_id', viewUserId)
          .eq('is_priority', true);

        let cardsIHaveThatTheyWant = [];
        if (theirWishList && theirWishList.length > 0) {
          const theirWishListCardIds = theirWishList.map(w => w.tcgdex_card_id).filter(Boolean);

          // Find matching cards in MY collection (any trading status)
          const { data: myMatchingCards } = await supabase
            .from('cards')
            .select('*')
            .eq('user_id', activeProfile.id)
            .is('deleted_at', null)
            .in('card_id', theirWishListCardIds);

          if (myMatchingCards && myMatchingCards.length > 0) {
            cardsIHaveThatTheyWant = myMatchingCards.map(card => {
              const wishItem = theirWishList.find(w => w.tcgdex_card_id === card.card_id);
              return {
                ...card,
                wishListVariant: wishItem?.variant || 'Normal',
                matchType: 'youHave'
              };
            });
          }
        }

        setWishListMatches(cardsTheyHaveThatIWant);
        setReverseMatches(cardsIHaveThatTheyWant);
      } catch (error) {
        console.error('Error detecting wish list matches:', error);
        setWishListMatches([]);
        setReverseMatches([]);
      }
    };

    detectMatches();
  }, [isViewingOther, viewUserId, activeProfile?.id, matchCheckTrigger]);

  // Re-check matches when navigating back to this page (pathname includes /collection or /user/)
  useEffect(() => {
    // This runs whenever location changes - if we're on a collection page, re-check
    if (isViewingOther && (location.pathname.includes('/collection') || location.pathname.includes('/user/'))) {
      setMatchCheckTrigger(prev => prev + 1);
    }
  }, [location.pathname, isViewingOther]);

  // Fetch all sets from TCGdex for wish list browse
  // TCGdex API supports server-side sorting by releaseDate
  // Track which language we last fetched
  const lastFetchedLanguageRef = useRef(null);

  useEffect(() => {
    const fetchAllSets = async () => {
      // Only the tabs that browse sets need this. It pulls the entire set
      // catalogue (200+ rows), and it used to run on mount regardless of tab,
      // which is dead weight on the All tab, where it is never read.
      const needsSets = activeTab === 'all' || activeTab === 'wishlist' || !!selectedCardSet;
      if (!needsSets) return;

      // Skip if already fetched for this language
      if (allSets.length > 0 && lastFetchedLanguageRef.current === setsLanguage) return;

      setAllSetsLoading(true);
      try {
        // Adapter fetch — english routes to digitalcards.*, other languages to TCGdex.
        // Rewrapped into the TCGdex-raw shape via legacySet() so downstream code is untouched.
        const rawSets = await cardData.getSets({ language: setsLanguage });
        const enrichedSets = rawSets.map((s, i) => legacySet(s, i));
        setAllSets(enrichedSets);
        lastFetchedLanguageRef.current = setsLanguage;
      } catch (error) {
        console.error('Error fetching sets:', error);
      }
      setAllSetsLoading(false);
    };

    fetchAllSets();
  }, [allSets.length, setsLanguage, activeTab, selectedCardSet]);

  // The Explore tab's search is useSmartSearch now — see `smart` below. The
  // effect that used to live here fired cardData.searchCards on every pause,
  // which matched a raw substring against card names only and so could never
  // answer "wizards of the coast" or "char 004".

  // Reset cards display limit when filters change
  useEffect(() => {
    setCardsDisplayLimit(30);
  }, [tradeFilter, visibilityFilter, cardSortBy, searchQuery]);

  // Fetch cards for selected set from TCGdex (used by both wishlist and cards set view)
  useEffect(() => {
    const fetchSetCards = async () => {
      // Determine which set to fetch (wishlist set or cards set)
      const setToFetch = selectedWishListSet && selectedWishListSet !== 'all'
        ? selectedWishListSet
        : selectedCardSet;

      if (!setToFetch) {
        setTcgSetCards([]);
        return;
      }

      setTcgSetCardsLoading(true);
      try {
        // Adapter fetch — includes the cards array. Cards get remapped to the
        // legacy TCGdex-raw shape so existing renderers keep working.
        const setData = await cardData.getSet(setToFetch, { language: setsLanguage, includeCards: true });
        setTcgSetCards((setData?.cards || []).map(legacyCard));
      } catch (error) {
        console.error('Error fetching set cards:', error);
        setTcgSetCards([]);
      }
      setTcgSetCardsLoading(false);
    };

    fetchSetCards();
  }, [selectedWishListSet, selectedCardSet, setsLanguage]);

  /* The set's slots: one row per (card, printing), already resolved server-side.
     One read per set, and it does not depend on the sort, the chips or the
     search, so re-ordering the grid never re-reads it. */
  useEffect(() => {
    if (!selectedCardSet) { setSetSlots([]); return undefined; }
    let alive = true;
    setSetSlots([]);
    setSetSlotsLoading(true);
    fetchSlotGrid(selectedCardSet, null, null)
      .then((rows) => {
        if (!alive) return;
        setSetSlots(rows || []);
        /* A set opens on its main printing, not on all of them.
           Base Set is 102 cards and 408 printings, and opening on 408 meant
           four near-identical tiles per card, four times the images, and a
           scroll that taught less than the short version does. The tile says
           how many printings a card has and the Printings sheet is one tap, so
           the others are a choice rather than a wall.
           A set with no plain printing - some promo lines - falls back to all
           of them, because narrowing to a printing that is not there would show
           an empty grid. */
        setSetPrinting(standardPrintingKey(rows || []));
      })
      .catch((e) => {
        // A set whose printings could not be read still has cards to show, so
        // the grid falls back to one tile per card rather than to nothing.
        console.warn('[collection] set printings', e?.message || e);
        if (alive) setSetSlots([]);
      })
      .finally(() => { if (alive) setSetSlotsLoading(false); });
    return () => { alive = false; };
  }, [selectedCardSet]);

  /* Leaving a set drops its printing choice. "1st Edition" is a fact about the
     set you were in, and carrying it into the next one would silently hide most
     of a set somebody had only just opened. */
  useEffect(() => {
    setSetPrinting(null);
    setPrintingSheetOpen(false);
  }, [selectedCardSet]);

  /** Every printing in the open set, in badge order. One printing, nothing to narrow. */
  const setPrintings = useMemo(() => printingsOf(setSlots), [setSlots]);

  /** The printing the button names and the value line follows. */
  const activePrinting = useMemo(
    () => setPrintings.find((pr) => pr.key === setPrinting) || null,
    [setPrintings, setPrinting],
  );

  // Fetch TCGdex results when smart search is opened
  useEffect(() => {
    if (!smartSearch.open || !smartSearch.query || smartSearch.query.length < 2) {
      setTcgSearchResults([]);
      return;
    }

    const fetchResults = async () => {
      try {
        // Adapter search — english trigram in digitalcards.*, falls through to TCGdex.
        const results = await cardData.searchCards(smartSearch.query, { language: 'en', limit: 100 });
        setTcgSearchResults(results.map(legacyCard));
      } catch (error) {
        console.error('TCGdex search error:', error);
        setTcgSearchResults([]);
      }
      setTcgSearchLoading(false);
    };

    fetchResults();
  }, [smartSearch.open, smartSearch.query]);

  // Handle smart search trigger (Enter key or button) - single state update
  const triggerSmartSearch = useCallback(() => {
    if (searchQuery.length >= 2 && activeTab === 'cards') {
      setTcgSearchLoading(true);
      setSmartSearch({
        open: true,
        query: searchQuery,
        expandedSection: null,
      });
    }
  }, [searchQuery, activeTab]);

  // Toggle search section expansion - accordion style (only one open at a time)
  const toggleSearchSection = useCallback((section) => {
    setSmartSearch(prev => ({
      ...prev,
      expandedSection: prev.expandedSection === section ? null : section,
    }));
  }, []);

  // Toggle follow
  const handleToggleFollow = async () => {
    if (!currentUserId || !viewUserId) return;

    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    try {
      if (wasFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', viewUserId);
      } else {
        await supabase
          .from('user_follows')
          .insert({
            follower_id: currentUserId,
            following_id: viewUserId,
          });

        // Send notification to the user being followed
        const { data: followerData } = await supabase
          .from('users')
          .select('username')
          .eq('id', currentUserId)
          .single();
        const followerName = followerData?.username || 'Someone';
        sendNewFollowerNotification(viewUserId, followerName, currentUserId);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      setIsFollowing(wasFollowing);
    }
  };

  // Block user
  const handleBlockUser = async () => {
    if (!currentUserId || !viewUserId) return;

    try {
      const { error } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: currentUserId,
          blocked_id: viewUserId,
        });

      if (error) {
        if (error.code === '23505') {
          alert('User is already blocked');
        } else {
          throw error;
        }
      } else {
        alert('User blocked. You will no longer see their content.');
        navigate(-1);
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  // Report user
  const handleReportUser = () => {
    setReportModalOpen(true);
  };

  // Handle successful report
  const handleReportSuccess = () => {
    alert('Report submitted. Thank you for helping keep our community safe.');
  };

  const handleBinderClick = (binder) => {
    setSelectedBinder(binder);
  };

  const handleBackFromBinder = () => {
    setSelectedBinder(null);
  };

  // Hold a binder tile: Share, Rename, Delete. Own collection only.
  /**
   * Call `fn` the first time anything scrolls, then stop listening.
   *
   * This is what separates a press from a scroll, and it replaces trying to
   * infer it from how far the finger travelled. A slow drag covers less than
   * the 12px drift threshold in the 420ms the hold timer runs, so the timer
   * fired and the selection bar opened - and starting a scroll with a thumb on
   * a card is simply how a grid is scrolled, so it fired constantly.
   *
   * Distance was the wrong question. A scroll that has begun is categorically
   * not a press, at any speed, over any distance. And it has to be detected
   * from the scroll event rather than from touchmove, because once the browser
   * takes the gesture over for scrolling it stops delivering touchmove to the
   * element - which is exactly why the drift guard never got its chance.
   *
   * Capture phase on `document`, for the same reason the chrome listener uses
   * it: scroll does not bubble, and several different elements scroll here.
   */
  const cancelOnScroll = (fn) => {
    let done = false;
    const handler = () => {
      if (done) return;
      done = true;
      document.removeEventListener('scroll', handler, { capture: true });
      fn();
    };
    document.addEventListener('scroll', handler, { capture: true, passive: true });
    return () => {
      if (done) return;
      done = true;
      document.removeEventListener('scroll', handler, { capture: true });
    };
  };

  const binderHoldProps = (binder) => {
    if (isViewingOther) return {};
    let timer = null;
    let stopScrollWatch = null;
    const clear = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (stopScrollWatch) { stopScrollWatch(); stopScrollWatch = null; }
    };
    const fire = () => { longPressFiredRef.current = true; haptics.heavy(); setHeldBinder(binder); };
    return {
      onTouchStart: () => {
        clear();
        timer = setTimeout(fire, 450);
        stopScrollWatch = cancelOnScroll(clear);
      },
      onTouchEnd: clear,
      onTouchMove: clear,
      onTouchCancel: clear,
      onContextMenu: (e) => { e.preventDefault(); fire(); },
    };
  };

  const handleShareBinder = async (binder) => {
    const { token } = await shareBinder(binder);
    if (token && !binder.share_token) {
      setBinders((prev) => prev.map((b) => (b.id === binder.id ? { ...b, share_token: token } : b)));
    }
  };

  const handleRenameBinder = async (binder, name) => {
    const { error } = await supabase.from('binders').update({ name }).eq('id', binder.id);
    if (error) throw error;
    setBinders((prev) => prev.map((b) => (b.id === binder.id ? { ...b, name } : b)));
    haptics.success();
  };

  // The binder goes; its cards stay in the collection.
  //
  // Not a plain delete any more. A tradable card has to live in a binder, so
  // releasing everything loose would quietly create the one state the rule
  // forbids - and a hard delete erased the record of what the binder held,
  // which is exactly what a disputed trade needs later. cc_delete_binder does
  // both halves in one transaction: tradable cards land in a trade binder, personal
  // ones go loose, and the binder row is kept with a deleted_at stamp.
  const handleDeleteBinderOnly = async (binder) => {
    const result = await deleteBinder(binder.id);
    const moved = result?.moved_to_trade_binder || 0;
    const targetId = result?.trade_binder_id || null;
    const targetRow = result?.trade_binder || null;

    setCards((prev) => prev.map((c) => {
      if (c.binder_id !== binder.id) return c;
      return { ...c, binder_id: c.trading_status === 'TRADE' ? targetId : null };
    }));
    setBinders((prev) => {
      const without = prev.filter((b) => b.id !== binder.id);
      // The trade binder may have been created by the delete itself, in which
      // case the cards just moved into a binder the list has never heard of.
      if (targetRow && !without.some((b) => b.id === targetRow.id)) {
        return [...without, targetRow].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      return without;
    });
    haptics.success();
    if (moved > 0) {
      showToast(`${moved} tradable ${moved === 1 ? 'card' : 'cards'} moved to your Trade Binder`, 'info');
    }
  };

  // The binder and every card in it go. Cards take the normal delete path so
  // posts and trades clean up the same way they do from the grid.
  const handleDeleteBinderWithCards = async (binder) => {
    const ids = cards.filter((c) => c.binder_id === binder.id).map((c) => c.id);
    if (ids.length) {
      await handleBulkDelete(ids);
      // A card in a scheduled trade refuses to delete; the binder must stay with it.
      const { data: left } = await supabase.from('cards').select('id').eq('binder_id', binder.id).is('deleted_at', null).limit(1);
      if (left?.length) throw new Error('some cards could not be deleted');
    }
    // Soft delete, same as the binder-only path. The cards it held are already
    // soft-deleted above, and a dispute four weeks from now still needs to be
    // able to name the binder they were in.
    const { error } = await supabase
      .from('binders')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', binder.id);
    if (error) throw error;
    setBinders((prev) => prev.filter((b) => b.id !== binder.id));
    haptics.success();
  };

  // Apply filters and sorting to cards
  const getFilteredAndSortedCards = (cardList) => {
    let result = [...cardList];

    // Apply set filter (when drilling into a set from sets view)
    if (selectedCardSet) {
      result = result.filter(card => card.card_id?.startsWith(`${selectedCardSet}-`));
    }

    // Search filtering
    if (searchQuery && searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      result = result.filter(card =>
        card.card_name?.toLowerCase().includes(query) ||
        card.name?.toLowerCase().includes(query)
      );
    }

    // Apply trade filter (PC vs Tradable)
    if (tradeFilter === 'pc') {
      result = result.filter(card => card.trading_status !== 'TRADE');
    } else if (tradeFilter === 'tradable') {
      result = result.filter(card => card.trading_status === 'TRADE');
    }

    // Apply visibility filter (only for own collection)
    if (!isViewingOther) {
      if (visibilityFilter === 'public') {
        result = result.filter(card => !card.is_private);
      } else if (visibilityFilter === 'private') {
        result = result.filter(card => card.is_private);
      }
    }

    // imageFilter is a VIEW toggle (digital vs photo), not a filter
    // It determines which image URL to display, handled in the render

    // Type, rarity and era. Multi-select, an OR inside each group and an AND
    // between, exactly as the catalogue does it, so ticking Grass and Fire
    // widens and then ticking Trainer narrows.
    //
    // Applied in memory rather than in a query, because unlike the catalogue's
    // 23,591 the whole collection is already here - the largest one in the
    // product is 177 cards - and the facts it is matched against were read from
    // the database in one go.
    const wantTypes = filterList(cardFilters, 'types');
    const wantRarities = filterList(cardFilters, 'rarities');
    const wantEras = filterList(cardFilters, 'eras');
    if (wantTypes.length || wantRarities.length || wantEras.length) {
      // Read from the same catalogue-backed map the chips fade from, so a card
      // can never be hidden by a filter whose chip says you own none of it.
      // The collection row's own `types` and `rarity` columns are not used:
      // they are very nearly always empty, and reading them would drop almost
      // every card from every filtered view.
      const facts = collectionFacets?.byCard;
      result = result.filter((card) => {
        const f = facts?.get(card.card_id);
        if (!f) return false;
        if (wantTypes.length && !f.types.some((t) => wantTypes.includes(t))) return false;
        if (wantRarities.length && (!f.bucket || !wantRarities.includes(f.bucket))) return false;
        if (wantEras.length && (!f.era || !wantEras.includes(f.era))) return false;
        return true;
      });
    }

    // Apply sorting. Facts the saved row lacks come from the catalogue.
    const metaOf = (card) => cardMeta[card.card_id] || {};
    const hpOf = (card) => Number(card.hp ?? metaOf(card).hp ?? 0);
    const typeOf = (card) => card.types?.[0] || metaOf(card).types?.[0] || 'zzz';
    const dateOf = (card) => metaOf(card).releaseDate || '';
    // Eras have a real chronological order; their ids do not. A card whose era
    // is unknown sorts to the end rather than to the beginning.
    const eraOrderOf = (card) => {
      const id = metaOf(card).eraId;
      const era = id ? allEras.find((e) => e.id === id) : null;
      return era?.display_order ?? Number.MAX_SAFE_INTEGER;
    };
    const numOf = (card) => {
      const raw = metaOf(card).localId || card.set_number || card.card_id?.split('-').pop() || '';
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
    };
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
    const byNumber = (a, b) => numOf(a) - numOf(b) || byName(a, b);
    // The price the tile badge shows. Unpriced cards go last either way.
    const valueOf = (card) => cardPrices[card.id]?.price ?? null;
    const byValue = (dir) => (a, b) => {
      const va = valueOf(a); const vb = valueOf(b);
      if (va == null && vb == null) return byName(a, b);
      if (va == null) return 1;
      if (vb == null) return -1;
      return dir * (va - vb) || byName(a, b);
    };
    switch (cardSortBy) {
      case 'value-desc':
        result.sort(byValue(-1));
        break;
      case 'value-asc':
        result.sort(byValue(1));
        break;
      case 'name-asc':
        result.sort(byName);
        break;
      case 'name-desc':
        result.sort((a, b) => byName(b, a));
        break;
      case 'type':
        result.sort((a, b) => typeOf(a).localeCompare(typeOf(b)) || byName(a, b));
        break;
      case 'recent':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'hp':
      case 'hp-desc':
        result.sort((a, b) => hpOf(b) - hpOf(a) || byName(a, b));
        break;
      case 'hp-asc':
        // Cards with no HP (trainers, energy) sit at the end either way.
        result.sort((a, b) => (hpOf(a) || Infinity) - (hpOf(b) || Infinity) || byName(a, b));
        break;
      case 'set-newest':
      case 'set-oldest': {
        const dir = cardSortBy === 'set-newest' ? -1 : 1;
        result.sort((a, b) => {
          const da = dateOf(a); const db = dateOf(b);
          if (da !== db) {
            if (!da) return 1;
            if (!db) return -1;
            return dir * da.localeCompare(db);
          }
          const sa = metaOf(a).setName || a.set_name || ''; const sb = metaOf(b).setName || b.set_name || '';
          return sa.localeCompare(sb) || byNumber(a, b);
        });
        break;
      }
      // The two orders the catalogue had and this screen did not. Same ids and
      // same wording as over there, so a person who learns the sheet once has
      // learned it everywhere. Both fall back to set and then card number
      // inside a group, which is the catalogue's resting state too.
      case 'era-newest':
      case 'era-oldest': {
        const dir = cardSortBy === 'era-newest' ? -1 : 1;
        result.sort((a, b) => {
          const oa = eraOrderOf(a); const ob = eraOrderOf(b);
          if (oa !== ob) return dir * (oa - ob);
          const sa = metaOf(a).setName || a.set_name || ''; const sb = metaOf(b).setName || b.set_name || '';
          return sa.localeCompare(sb) || byNumber(a, b);
        });
        break;
      }
      case 'rarity-desc':
      case 'rarity-asc': {
        const dir = cardSortBy === 'rarity-desc' ? -1 : 1;
        result.sort((a, b) => {
          // Off-ladder rarities - promos, unrecorded - sink either way, the
          // same as the catalogue's own order does it.
          const ra = rarityRankOf(metaOf(a).rarity); const rb = rarityRankOf(metaOf(b).rarity);
          if (ra == null && rb == null) return byName(a, b);
          if (ra == null) return 1;
          if (rb == null) return -1;
          if (ra !== rb) return dir * (ra - rb);
          const sa = metaOf(a).setName || a.set_name || ''; const sb = metaOf(b).setName || b.set_name || '';
          return sa.localeCompare(sb) || byNumber(a, b);
        });
        break;
      }
      default:
        break;
    }

    return result;
  };

  const filteredCards = getFilteredAndSortedCards(cards);
  // Inside a binder, Select all means the binder's cards, not the whole collection.
  const selectBinderCards = selectedBinder ? cards.filter((c) => c.binder_id === selectedBinder.id) : null;

  // Apply sorting to binders
  const getSortedBinders = () => {
    const result = [...binders];
    const when = (b) => new Date(b.created_at || 0).getTime();
    const worth = (b) => cards.reduce((sum, c) => (c.binder_id === b.id ? sum + (cardPrices[c.id]?.price || 0) : sum), 0);
    switch (binderSortBy) {
      case 'name-asc':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name-desc':
        result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'newest':
        result.sort((a, b) => when(b) - when(a));
        break;
      case 'oldest':
        result.sort((a, b) => when(a) - when(b));
        break;
      case 'value-desc': {
        // Binders with nothing priced sit at the end, in name order.
        const w = new Map(result.map((b) => [b.id, worth(b)]));
        result.sort((a, b) => {
          const va = w.get(a.id), vb = w.get(b.id);
          if (va === 0 && vb === 0) return (a.name || '').localeCompare(b.name || '');
          if (va === 0) return 1;
          if (vb === 0) return -1;
          return vb - va;
        });
        break;
      }
      case 'value-asc': {
        // Same rule from the other end: unpriced binders still sit last.
        const w = new Map(result.map((b) => [b.id, worth(b)]));
        result.sort((a, b) => {
          const va = w.get(a.id), vb = w.get(b.id);
          if (va === 0 && vb === 0) return (a.name || '').localeCompare(b.name || '');
          if (va === 0) return 1;
          if (vb === 0) return -1;
          return va - vb;
        });
        break;
      }
      default:
        break;
    }
    // The trade binder leads, under every sort. It is not a binder among
    // binders - it is the one you carry to a trade and the one another trader
    // looks at before agreeing to meet, so burying it under "name ascending"
    // would hide the only binder with a job.
    result.sort((a, b) => (isTradeBinder(b) ? 1 : 0) - (isTradeBinder(a) ? 1 : 0));
    return result;
  };

  const sortedBinders = getSortedBinders();

  /**
   * What the collection holds, for fading the chips nobody can use.
   *
   * Keyed on the collection's size and the binder in view rather than on the
   * card array itself, so it re-reads when cards are added, removed or moved
   * between binders, and not on every render. Scoped to the open binder when
   * there is one: inside a set binder, the chips describe that binder.
   *
   * Deliberately NOT derived from `cards`. That array arrives in waves, and a
   * facet computed from the first wave is a confident statement about the whole
   * collection made from a fraction of it.
   */
  /** How many of the three chip groups are narrowing the grid right now. */
  const collectionFilterCount = ['types', 'rarities', 'eras']
    .reduce((n, g) => n + filterList(cardFilters, g).length, 0);

  /**
   * Why the grid is empty, in the person's own terms.
   *
   * Names the ticked value the collection no longer contains when there is
   * exactly one such culprit, because that is the case that actually confuses
   * people. With several, naming them all is a list nobody reads.
   */
  const emptyFilterReason = useMemo(() => {
    if (!collectionFacets) return 'Try unticking one of the filters.';
    const gone = [];
    for (const t of filterList(cardFilters, 'types')) {
      if (!collectionFacets.types.includes(t)) gone.push(t);
    }
    for (const r of filterList(cardFilters, 'rarities')) {
      if (!collectionFacets.rarities.includes(r)) {
        gone.push(RARITY_BUCKETS.find((b) => b.id === r)?.label || r);
      }
    }
    for (const e of filterList(cardFilters, 'eras')) {
      if (!collectionFacets.eras.includes(e)) {
        gone.push(shortEraLabel(allEras.find((x) => x.id === e) || { id: e }));
      }
    }
    if (gone.length === 1) return `You do not have any ${gone[0]} cards any more.`;
    if (gone.length > 1) return 'Some of those filters no longer match anything you own.';
    return 'Nothing in your collection matches all of those at once.';
  }, [collectionFacets, cardFilters, allEras]);

  const facetUserId = viewUserId || currentUserId;
  const facetBinderId = selectedBinder?.id || null;
  useEffect(() => {
    if (!facetUserId) { setCollectionFacets(null); return undefined; }
    let alive = true;
    loadCollectionFacets(facetUserId, { binderId: facetBinderId })
      .then((f) => { if (alive) setCollectionFacets(f); })
      .catch(() => { if (alive) setCollectionFacets(null); });
    return () => { alive = false; };
  }, [facetUserId, facetBinderId, cards.length]);

  // Scroll-based infinite loading (replaces IntersectionObserver for iOS compatibility)
  const handleContentScroll = useCallback((e) => {
    const el = e.target;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 300) {
      if (activeTab === 'cards' && cardsViewMode !== 'sets' && !selectedCardSet) {
        setCardsDisplayLimit(prev => {
          if (prev >= filteredCards.length) return prev;
          return Math.min(prev + 30, filteredCards.length);
        });
      } else if (activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet) {
        setSetsDisplayLimit(prev => {
          if (prev >= allSets.length) return prev;
          return Math.min(prev + 30, allSets.length);
        });
      } else if (activeTab === 'all' && allMode === 'cards' && !selectedCardSet) {
        // The only list here that is not already fully in memory: the next 60
        // rows come from the server.
        loadMoreCatalogRef.current();
      }
    }
  }, [activeTab, cardsViewMode, allMode, selectedCardSet, filteredCards.length, allSets.length]);

  // The app locks body scroll and scrolls `.app-content`, so the listener has to
  // go there. It was previously an onScroll prop on a div with no overflow -
  // scroll events do not bubble, so that handler never ran and paging relied
  // entirely on the observer below.
  useEffect(() => {
    const el = document.querySelector('.app-content');
    if (!el) return undefined;
    // The same listener also drives how much furniture is showing. One
    // listener, because two on the same element is two chances to disagree
    // about where the page is.
    const onScroll = () => handleContentScroll({ target: el });
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [handleContentScroll]);

  /**
   * How much furniture is showing, driven by wherever the scrolling happens.
   *
   * Listened for on `document` in the CAPTURE phase rather than bound to one
   * element. Scroll events do not bubble, which is what made the paging
   * listener above unreliable enough to need an IntersectionObserver behind it,
   * but they DO capture, so one listener here sees the scroll whichever element
   * is actually doing it. That matters because this screen has several
   * scrollers - the page, the set detail, the search panel - and binding to the
   * wrong one is a control that silently does nothing.
   */
  // The element that is actually doing the scrolling, learned from the first
  // scroll event rather than guessed from a class name.
  const scrollerRef = useRef(null);
  useEffect(() => {
    const onAnyScroll = (e) => {
      const el = (e.target === document || e.target === document.documentElement)
        ? document.scrollingElement
        : e.target;
      if (!el || typeof el.scrollTop !== 'number') return;
      // Remember WHICH element scrolled. This is the whole bug behind the jump
      // the owner felt: the compensation below was writing scrollTop on
      // `.app-content`, and `.app-content` is not what scrolls here - which is
      // the same reason its own scroll listener never fired and this handler
      // had to move to the capture phase in the first place. Writing a scroll
      // position to an element that does not scroll is a silent no-op, so the
      // height came out of the layout and nothing put it back.
      scrollerRef.current = el;
      // Never take the search bar away from someone typing in it. This is the
      // one place the scroll behaviour could have reached into the keyboard
      // path, and the answer is that it does not.
      const focused = document.activeElement;
      if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA')) return;
      onScrollPosition(el.scrollTop);
    };
    document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onAnyScroll, { capture: true });
  }, []);

  /**
   * Everything comes back when you leave, and when the list underneath you
   * changes out from under the scroll position.
   *
   * A collapsed nav is an artefact of where you are scrolled, not a setting, so
   * arriving anywhere new starts with the furniture back.
   */
  // Nothing on this page reads the chrome state any more. The toolbar scrolls
  // away on its own because it is part of the scrolling content, and the bottom
  // nav subscribes to the state directly over in BottomNav. This page only
  // feeds scroll positions in, and resets it on the way out.

  useEffect(() => {
    resetChrome();
    return () => resetChrome();
  }, [activeTab, allMode, selectedCardSet, selectedBinder?.id, selectedWishListSet]);

  // IntersectionObserver callback ref for Safari/iOS where onScroll doesn't fire reliably
  const loadMoreRef = useCallback((node) => {
    if (loadMoreObserver.current) loadMoreObserver.current.disconnect();
    if (!node) return;
    loadMoreObserver.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (activeTab === 'cards' && cardsViewMode !== 'sets' && !selectedCardSet) {
          setCardsDisplayLimit(prev => {
            if (prev >= filteredCards.length) return prev;
            return Math.min(prev + 30, filteredCards.length);
          });
        } else if (activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet) {
          setSetsDisplayLimit(prev => {
            if (prev >= allSets.length) return prev;
            return Math.min(prev + 30, allSets.length);
          });
        } else if (activeTab === 'all' && allMode === 'cards' && !selectedCardSet) {
          loadMoreCatalogRef.current();
        }
      }
    }, { rootMargin: '300px' });
    loadMoreObserver.current.observe(node);
  }, [activeTab, cardsViewMode, allMode, selectedCardSet, filteredCards.length, allSets.length]);

  // Get card count for a binder (computed from cards array)
  const getBinderCardCount = (binderId) => {
    return cards.filter(card => card.binder_id === binderId).length;
  };

  // Get total value for a binder
  const getBinderValue = (binderId) => {
    const binderCards = cards.filter(card => card.binder_id === binderId);
    const total = binderCards.reduce((sum, c) => sum + (cardPrices[c.id]?.price || 0), 0);
    return total > 0 ? formatPrice(total) : null;
  };

  // Handle refresh prices button
  const handleRefreshPrices = async () => {
    if (refreshCooldown || refreshing) {
      console.log('[Collection] Refresh blocked - cooldown:', refreshCooldown, 'refreshing:', refreshing);
      return;
    }
    console.log('[Collection] Starting price refresh for', cards.length, 'cards');
    setRefreshing(true);
    setRefreshProgress({ done: 0, total: 0 });

    const cardLookups = cards.map(card => ({
      id: card.id,
      name: card.english_name || card.name,
      set_name: card.set_name || '',
      set_number: card.set_number || card.card_id?.split('-').pop() || '',
      grade: card.grade || 'NM',
      variant: card.variant || '',
    }));

    const { updated } = await refreshCollectionPrices(cardLookups, (done, total) => {
      setRefreshProgress({ done, total });
    });

    // Re-read all prices
    const results = await getBulkCardPrices(cardLookups);
    const prices = {};
    let oldestRefresh = null;
    for (const [cardId, result] of results) {
      // Keep what the screen believes and what the effect believes in step, or
      // the next wave of cards recomputes the cooldown from a stale picture.
      priceRowsRef.current.set(cardId, result);
      if (result.price != null && result.price > 0) {
        prices[cardId] = { price: result.price, formatted: result.formatted };
      }
      const ts = result.row?.last_refreshed_at;
      if (ts && (!oldestRefresh || new Date(ts) < new Date(oldestRefresh))) {
        oldestRefresh = ts;
      }
    }
    setCardPrices(prices);
    if (oldestRefresh) setLastCollectionRefresh(oldestRefresh);
    setRefreshing(false);
    showToast(`Prices updated for ${updated} cards`);
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(<StarIcon key={i} filled={i <= Math.round(rating)} />);
    }
    return stars;
  };

  // Handle card click - navigate to card detail page or toggle selection in bulk edit mode
  const handleCardClick = (card) => {
    if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
    if (bulkEditMode) {
      // Toggle selection
      setSelectedCards(prev =>
        prev.includes(card.id)
          ? prev.filter(id => id !== card.id)
          : [...prev, card.id]
      );
    } else {
      // The arrows walk the list you tapped from, which inside a binder is that
      // binder and not the whole collection. It used to be the collection
      // either way, so a card opened from an eight card binder said "2 of 156"
      // and eight taps of Next walked you out of the binder without a word.
      const inBinder = Boolean(selectedBinder?.id);
      const list = inBinder
        ? getFilteredAndSortedCards(cards.filter((c) => c.binder_id === selectedBinder.id))
        : filteredCards;
      const cardIds = list.map((c) => c.id);
      const currentIndex = cardIds.indexOf(card.id);
      setCardNavigationContext({
        type: inBinder ? 'binder' : 'collection',
        cardIds,
        currentIndex: currentIndex >= 0 ? currentIndex : 0,
        sourceId: inBinder ? selectedBinder.id : undefined,
        // The list's own name, so the counter can say where you are rather than
        // naming the kind of place you are in.
        sourceLabel: inBinder
          ? selectedBinder.name
          : (isViewingOther ? `@${profile?.username}` : 'My Collection'),
      });
      navigate(`/card/${card.id}`);
    }
  };


  // Hold a card to start selecting. Medium haptic, the card is selected, and
  // the bars appear. Never on someone else's collection.
  // The hold fires here: haptic, pop, and the card is chosen. The bar itself
  // waits for the finger to come off, the way iOS does it, so the release is
  // what opens the menu and nothing jumps under a finger that is still down.
  const beginSelect = useCallback((card) => {
    if (isViewingOther) return;
    longPressFiredRef.current = true;
    haptics.heavy();
    setPoppedId(card.id);
    setTimeout(() => setPoppedId((id) => (id === card.id ? null : id)), 360);
    setSelectedCards((prev) => (prev.includes(card.id) ? prev : [...prev, card.id]));
    armedRef.current = card.id;
  }, [isViewingOther]);
  const releaseSelect = useCallback((card) => {
    if (armedRef.current !== card.id) return;
    armedRef.current = null;
    setBulkEditMode('select');
  }, []);
  // Hold feel: the tile lifts while the finger rests on it, a small drift is
  // tolerated, a real drag cancels cleanly, and the lift ends on release.
  const longPressProps = useCallback((card) => {
    let timer = null;
    let liftTimer = null;
    let start = null;
    let stopScrollWatch = null;
    const clear = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (liftTimer) { clearTimeout(liftTimer); liftTimer = null; }
      if (stopScrollWatch) { stopScrollWatch(); stopScrollWatch = null; }
      start = null;
      setPressingId((id) => (id === card.id ? null : id));
    };
    const release = () => {
      const fired = armedRef.current === card.id;
      clear();
      releaseSelect(card);
      // The tap iOS sends at the end of a hold is swallowed by the fired flag.
      // If no tap follows (Done was pressed, or the finger drifted off), the
      // flag must not lie in wait for the next genuine tap on a card.
      if (fired) setTimeout(() => { longPressFiredRef.current = false; }, 500);
    };
    return {
      onTouchStart: (e) => {
        clear();
        const t = e.touches?.[0];
        start = t ? { x: t.clientX, y: t.clientY } : null;
        // A tap never lifts. The lift begins once the finger has rested a
        // moment, which is what separates a press from a touch on its way to
        // becoming a scroll or a tap.
        liftTimer = setTimeout(() => { liftTimer = null; setPressingId(card.id); }, LIFT_AFTER_MS);
        timer = setTimeout(() => { timer = null; beginSelect(card); }, HOLD_MS);
        // The moment the list moves, this was a scroll and never a press.
        stopScrollWatch = cancelOnScroll(() => {
          if (armedRef.current === card.id) release(); else clear();
        });
      },
      onTouchEnd: release,
      onTouchCancel: release,
      onTouchMove: (e) => {
        if (!start) return;
        const t = e.touches?.[0];
        if (!t) return;
        // The drift check stays, for the case the scroll watch cannot catch:
        // a finger dragging across the grid while the list is already at its
        // top or bottom and physically cannot move. No scroll event fires
        // there, so distance is the only signal left.
        if (Math.hypot(t.clientX - start.x, t.clientY - start.y) > HOLD_DRIFT_PX) {
          // Dragging away after the pop still counts as a release.
          if (armedRef.current === card.id) release(); else clear();
        }
      },
      onContextMenu: (e) => { e.preventDefault(); },
    };
  }, [beginSelect, releaseSelect]);

  // One tap on the action bar applies to everything selected.
  const applySelection = async (action) => {
    if (selectedCards.length === 0 || selectBusy) return;
    if (action === 'delete') { setShowDeleteConfirm(true); return; }
    if (action === 'binder') { setBinderPickerOpen(true); return; }
    const updateData = action === 'tradeable' ? { trading_status: 'TRADE' }
      : action === 'pc' ? { trading_status: 'PC' }
      : action === 'public' ? { is_private: false }
      : action === 'private' ? { is_private: true }
      : null;
    if (!updateData) return;
    const ids = [...selectedCards];

    // TCG Pocket cards are digital. Trade Now is two people at a table, and a
    // card that only exists inside the Pocket app can never cross one, so it
    // cannot be marked tradable. A mixed selection writes the rest and says
    // how many were left out; dropping some without a word is how a count
    // stops being trusted. The skipped number comes from the ids that are
    // never sent, so it can never absorb an unrelated failure below.
    let pocketCount = 0;
    let targetIds = ids;
    if (action === 'tradeable') {
      const selected = new Set(ids);
      const pocket = new Set(
        cards.filter((c) => selected.has(c.id) && isTcgPocketCard(c)).map((c) => c.id),
      );
      pocketCount = pocket.size;
      targetIds = ids.filter((id) => !pocket.has(id));
      // Nothing left to write. Select mode stays open, because the person most
      // likely wants PC instead and should not have to re-select everything to
      // recover from a message rather than a mistake.
      if (targetIds.length === 0) { showToast(TCG_POCKET_TRADE_NONE, 'info'); return; }
    }

    setSelectBusy(true);
    try {
      // .select() so the toast counts rows the server actually changed rather
      // than rows asked for. RLS can drop one and a card can be deleted between
      // the tap and the write, neither of which raises; saying fifteen when
      // eleven landed is the app lying about his own collection.
      const { data, error } = await supabase
        .from('cards')
        .update(updateData)
        .in('id', targetIds)
        .select('id');
      if (error) throw error;
      const written = new Set((data || []).map((r) => r.id));
      if (written.size === 0) {
        // Accepted, matched nothing. RLS and a deleted row look identical from
        // here, so the sentence hedges rather than naming a cause it cannot see.
        showToast('Nothing changed. Those cards may no longer be there.', 'error');
        return;
      }
      setCards((prev) => prev.map((card) => (written.has(card.id) ? { ...card, ...updateData } : card)));

      // Marking tradable moves the cards into the trade binder, and the move
      // happens in the database rather than here. Read back where they landed
      // so the grid does not keep showing them under their old binder.
      if (action === 'tradeable' && written.size > 0) {
        const { data: moved } = await supabase
          .from('cards')
          .select('id, binder_id')
          .in('id', [...written]);
        const landed = new Map((moved || []).map((r) => [r.id, r.binder_id]));
        if (landed.size) {
          setCards((prev) => prev.map((card) => (
            landed.has(card.id) ? { ...card, binder_id: landed.get(card.id) } : card
          )));
        }
        showToast(tradeBinderMoveNote(written.size), 'info');
      }

      const word = action === 'tradeable' ? 'tradable' : action === 'pc' ? 'PC' : action;
      const n = written.size;
      const head = `${n} ${n === 1 ? 'card' : 'cards'} marked ${word}`;
      showToast(pocketCount ? `${head}. ${tcgPocketSkippedLine(pocketCount)}` : head, 'success');
      haptics.success();
      setBulkEditMode(null);
      setSelectedCards([]);
    } catch (e) {
      console.error('Error applying selection:', e);
      showToast('That did not save. Try again.', 'error');
    } finally {
      setSelectBusy(false);
    }
  };

  // Binder picker confirmed: move exactly these ids into the binder.
  //
  // When the picker was opened by "Mark tradable", the move is only half of
  // it. The status write covers every card being marked, including the ones
  // that already had a binder and never needed moving.
  /** What the roll will carry, in the order the binder is showing it. */
  const printCards = useMemo(() => (selectedBinder
    ? getFilteredAndSortedCards(cards.filter((c) => c.binder_id === selectedBinder.id))
      // An asking price beats the market: the owner has decided.
      .map((c) => ({ ...c, price: c.ask_price ?? cardPrices[c.id]?.price ?? null }))
    : []),
  // Deliberately not exhaustive: getFilteredAndSortedCards closes over the
  // screen's filter and sort state, and listing every one of those here would
  // be a list to keep in step with a function that already reads them.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [selectedBinder, cards, cardPrices]);

  /**
   * A price typed on the rounding screen is a price.
   *
   * It saves to `cards.ask_price` rather than only to this print run, because
   * somebody pricing a card has priced it - finding the same blank again on the
   * next run would be the app forgetting what it was just told.
   *
   * Not `cost_basis`, which is what they paid, and not the market price, which
   * is what the pricing stack thinks it is worth. Three different numbers.
   */
  const handlePriceTyped = useCallback(async (cardId, price) => {
    setCardPrices((prev) => ({ ...prev, [cardId]: { ...(prev[cardId] || {}), price } }));
    try {
      await supabase.from('cards').update({ ask_price: price }).eq('id', cardId);
    } catch (e) {
      console.error('[print] could not save the typed price:', e);
    }
  }, []);

  /** Connect if we have to, then print what the rounding pass decided. */
  const runPrint = printLink.run;

  const handleSelectionToBinder = async (binder, ids) => {
    if (!ids.length) { showToast('Nothing to add', 'info'); return; }
    setSelectBusy(true);
    try {
      const { error } = await supabase.from('cards').update({ binder_id: binder.id }).in('id', ids);
      if (error) throw error;
      setCards((prev) => prev.map((card) => (ids.includes(card.id) ? { ...card, binder_id: binder.id } : card)));
      showToast(`${ids.length} ${ids.length === 1 ? 'card' : 'cards'} added to ${binder.name}`, 'success');
      haptics.success();
      setBulkEditMode(null);
      setSelectedCards([]);
    } catch (e) {
      console.error('Error moving selection to binder:', e);
      showToast('That did not save. Try again.', 'error');
    } finally {
      setSelectBusy(false);
    }
  };

  // Confirm and execute delete
  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await handleBulkDelete(selectedCards);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle bulk card deletion (soft delete with cascade)
  const handleBulkDelete = async (cardIds) => {
    try {
      // 1. Check for cards in scheduled/confirmed trades
      const { data: scheduledInterests } = await supabase
        .from('trade_interests')
        .select(`
          card_id,
          trade_request_id,
          trade_requests!inner (
            id, status,
            trade_schedules (id, status)
          )
        `)
        .in('card_id', cardIds)
        .in('trade_requests.status', ['accepted']);

      const blockedCards = [];
      if (scheduledInterests) {
        for (const interest of scheduledInterests) {
          const schedules = interest.trade_requests?.trade_schedules || [];
          const hasScheduled = schedules.some(s => s.status === 'confirmed' || s.status === 'pending_confirmation');
          if (hasScheduled) blockedCards.push(interest.card_id);
        }
      }

      // Also check trade_requests.card_id directly for the initiating card
      const { data: scheduledRequests } = await supabase
        .from('trade_requests')
        .select('card_id, status, trade_schedules (id, status)')
        .in('card_id', cardIds)
        .in('status', ['accepted']);

      if (scheduledRequests) {
        for (const req of scheduledRequests) {
          const schedules = req.trade_schedules || [];
          const hasScheduled = schedules.some(s => s.status === 'confirmed' || s.status === 'pending_confirmation');
          if (hasScheduled && !blockedCards.includes(req.card_id)) blockedCards.push(req.card_id);
        }
      }

      if (blockedCards.length > 0) {
        const blockedNames = cards
          .filter(c => blockedCards.includes(c.id))
          .map(c => c.name)
          .join(', ');
        alert(`These cards are in scheduled trades and can't be deleted right now: ${blockedNames}. Cancel those trades first.`);
        return;
      }

      // 2. Soft delete the cards
      const { error: deleteError } = await supabase
        .from('cards')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', cardIds);

      if (deleteError) throw deleteError;

      // 3. Posts cascade: remove from posts_cards, delete orphan posts
      for (const cardId of cardIds) {
        const { data: postLinks } = await supabase
          .from('posts_cards')
          .select('post_id')
          .eq('card_id', cardId);

        if (postLinks?.length) {
          // Remove this card from all posts
          await supabase.from('posts_cards').delete().eq('card_id', cardId);

          // Check each affected post for remaining cards
          for (const link of postLinks) {
            const { data: remaining } = await supabase
              .from('posts_cards')
              .select('id')
              .eq('post_id', link.post_id);

            if (!remaining || remaining.length === 0) {
              // No cards left, delete the post
              await supabase.from('posts').delete().eq('id', link.post_id);
            }
          }
        }
      }

      // 4. Active trades cascade: remove from trade_interests, notify
      const { data: activeInterests } = await supabase
        .from('trade_interests')
        .select('id, card_id, trade_request_id, user_id')
        .in('card_id', cardIds);

      if (activeInterests?.length) {
        const cardNameMap = {};
        cards.forEach(c => { cardNameMap[c.id] = c.name; });
        const userName = profile?.username || 'Someone';

        // Group by trade
        const byTrade = {};
        for (const interest of activeInterests) {
          if (!byTrade[interest.trade_request_id]) byTrade[interest.trade_request_id] = [];
          byTrade[interest.trade_request_id].push(interest);
        }

        for (const [reqId, interests] of Object.entries(byTrade)) {
          // Delete the interests
          await supabase
            .from('trade_interests')
            .delete()
            .in('id', interests.map(i => i.id));

          // Check remaining interests for this user in this trade
          const { data: remaining } = await supabase
            .from('trade_interests')
            .select('id')
            .eq('trade_request_id', reqId)
            .eq('user_id', activeProfile.id);

          // Get trade request to find partner
          const { data: tradeReq } = await supabase
            .from('trade_requests')
            .select('requester_id, card_owner_id, status')
            .eq('id', reqId)
            .single();

          if (!tradeReq || tradeReq.status === 'completed' || tradeReq.status === 'cancelled') continue;

          const partnerId = tradeReq.requester_id === activeProfile.id
            ? tradeReq.card_owner_id
            : tradeReq.requester_id;

          const removedNames = interests.map(i => cardNameMap[i.card_id] || 'a card').join(', ');

          if (!remaining || remaining.length === 0) {
            // No cards left for this user in the trade, cancel it
            await supabase.from('trade_requests').update({ status: 'cancelled' }).eq('id', reqId);
            await supabase.from('messages').insert({
              trade_request_id: reqId,
              sender_id: '550e8400-e29b-41d4-a716-446655440000',
              message: `@${userName} removed all their cards from this trade. The trade has been cancelled.`,
              message_type: 'system',
              is_ignored: false,
            });
            if (partnerId) sendCardRemovedFromTradeNotification(partnerId, userName, removedNames, reqId);
          } else {
            // Other cards remain, just notify
            await supabase.from('messages').insert({
              trade_request_id: reqId,
              sender_id: '550e8400-e29b-41d4-a716-446655440000',
              message: `@${userName} removed ${removedNames} from their collection. The trade can continue with the remaining cards.`,
              message_type: 'system',
              is_ignored: false,
            });
            if (partnerId) sendCardRemovedFromTradeNotification(partnerId, userName, removedNames, reqId);
          }
        }
      }

      // 5. Update local state
      setCards(prev => prev.filter(c => !cardIds.includes(c.id)));
      setBulkEditMode(null);
      setSelectedCards([]);

    } catch (e) {
      console.error('Error deleting cards:', e);
      alert('Error deleting cards. Please try again.');
    }
  };

  // Cancel bulk edit mode
  const cancelBulkEdit = useCallback(() => {
    setBulkEditMode(null);
    setSelectedCards([]);
    armedRef.current = null;
    longPressFiredRef.current = false;
  }, []);

  // Handle add card from the All tab
  const handleAddCard = ({ type, binder }) => {
    setAddCardModalOpen(false);
    // The binder the person picked rides along, so the scanner adds to it
    // rather than dropping the cards loose in the collection.
    navigate(binder?.id ? `/bulk-scanner?binder=${binder.id}` : '/bulk-scanner');
  };

  // Handle create binder
  const handleCreateBinder = async ({ name, image, isPrivate }) => {
    try {
      // Generate UUID
      const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });

      const { error } = await supabase
        .from('binders')
        .insert({
          id: uuid,
          user_id: profile.id,
          name,
          image,
          // The modal has always collected this and the insert has never
          // written it, so the private toggle did nothing at all.
          is_private: !!isPrivate,
          // A binder of cards the person owns. Wish binders and master sets
          // are binders rows too now, told apart by this column.
          binder_type: BINDER_TYPE_PC,
          card_count: 0,
          last_updated: new Date().toISOString(),
          created_at: new Date().toISOString(),
        });

      if (error) throw error;

      // Refresh binders
      const { data: bindersData } = await supabase
        .from('binders')
        .select('*')
        .eq('user_id', profile.id)
        .match(PC_ONLY)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (bindersData) setBinders(bindersData);
      // Trigger congrats if first_binder tutorial not yet completed
      handleBinderAdded();
    } catch (err) {
      console.error('Error creating binder:', err);
      // The reserved-name guard raises a sentence meant to be read. Anything
      // else gets the generic line, and either way the modal stays open rather
      // than closing on a binder that was never made.
      showToast(
        /trade binder/i.test(err?.message || '')
          ? err.message
          : 'That binder could not be created. Try again.',
        'error',
      );
      throw err;
    }
  };

  // Handle add new card to binder (from inside binder)
  const handleAddNewCardToBinder = (binder) => {
    setAddToBinderModalOpen(false);
    navigate(binder?.id ? `/bulk-scanner?binder=${binder.id}` : '/bulk-scanner');
  };

  // Handle move cards to binder
  const handleMoveCardsToBinder = async (cardsToMove, targetBinder) => {
    try {
      const cardIds = cardsToMove.map((c) => c.id);

      const { error } = await supabase
        .from('cards')
        .update({ binder_id: targetBinder.id })
        .in('id', cardIds);

      if (error) throw error;

      // Refresh cards
      const { data: cardsData } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', profile.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (cardsData) setCards(cardsData);
    } catch (err) {
      console.error('Error moving cards:', err);
    }
  };

  // Stats Panel Component
  const StatsPanel = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={styles.statsPanelOverlay}
      onClick={() => setShowStatsPanel(false)}
    >
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={styles.statsPanel}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.statsPanelHeader}>
          <h2 style={styles.statsPanelTitle}>{profile?.username}'s Stats</h2>
          <button style={styles.statsPanelClose} onClick={() => setShowStatsPanel(false)}>
            <CloseIcon />
          </button>
        </div>

        {/* User Actions - only show when viewing other user */}
        {isViewingOther && (
          <div style={styles.userActionsSection}>
            <button
              style={{
                ...styles.userActionButton,
                ...(isFollowing ? styles.userActionButtonActive : {}),
              }}
              onClick={handleToggleFollow}
            >
              <span style={styles.userActionText}>
                {isFollowing ? 'Following' : 'Follow'}
              </span>
            </button>
            <button style={styles.userActionButton} onClick={handleBlockUser}>
              <span style={styles.userActionText}>Block</span>
            </button>
            <button style={styles.userActionButtonDanger} onClick={handleReportUser}>
              <span style={styles.userActionTextDanger}>Report</span>
            </button>
          </div>
        )}

        {/* Trade Reputation */}
        <div style={styles.tradeReputationSection}>
          <h3 style={styles.sectionTitle}>Trade Reputation</h3>
          {tradeStats.totalReviews > 0 ? (
            <>
              <div style={styles.overallRating}>
                <div style={styles.starsRow}>
                  {renderStars(tradeStats.averageRating)}
                </div>
                <span style={styles.ratingText}>
                  {tradeStats.averageRating.toFixed(1)} ({tradeStats.totalReviews} reviews)
                </span>
              </div>

              {/* Rating Breakdown */}
              <div style={styles.ratingBreakdown}>
                {Object.entries(tradeStats.ratingBreakdown).map(([key, value]) => (
                  <div key={key} style={styles.ratingRow}>
                    <span style={styles.ratingLabel}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                    <div style={styles.ratingBarContainer}>
                      <div
                        style={{
                          ...styles.ratingBar,
                          width: `${(value / 5) * 100}%`,
                        }}
                      />
                    </div>
                    <span style={styles.ratingValue}>{value.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.noReviews}>
              <span style={styles.noReviewsText}>No trade reviews yet</span>
              <span style={styles.noReviewsSubtext}>Complete trades to build your reputation</span>
            </div>
          )}
        </div>

        {/* All Badges */}
        <div style={{ padding: '0 20px 12px' }}>
          <button
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              backgroundColor: theme.bgCard,
              borderRadius: '12px',
              border: `1px solid ${theme.border}`,
              cursor: 'pointer',
              outline: 'none',
            }}
            onClick={() => {
              setBadgesOpenedFromStats(true);
              setShowStatsPanel(false);
              setAllBadgesOpen(true);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['charter', 'trader', 'officer', 'researcher', 'collector', 'organized'].map((type) => (
                  <BadgeProgressRing key={type} type={type} data={badgeData} size={24} showLabel={false} theme={theme} />
                ))}
              </div>
              <span style={{ color: theme.text, fontSize: '14px', fontWeight: '600' }}>All Badges</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Quick Stats */}
        <div style={styles.quickStatsGrid}>
          <div style={styles.quickStatCard}>
            <div style={styles.quickStatIcon}>
              <BindersIcon />
            </div>
            <span style={styles.quickStatValue}>{binders.length}</span>
            <span style={styles.quickStatLabel}>Binders</span>
          </div>
          <div style={styles.quickStatCard}>
            <div style={styles.quickStatIcon}>
              <CardsIcon />
            </div>
            <span style={styles.quickStatValue}>{cards.length}</span>
            <span style={styles.quickStatLabel}>Cards</span>
          </div>
          <div style={styles.quickStatCard}>
            <div style={styles.quickStatIcon}>
              <TrendingUpIcon />
            </div>
            <span style={styles.quickStatValue}>{tradeStats.totalTrades}</span>
            <span style={styles.quickStatLabel}>Trades</span>
          </div>
        </div>

        {/* Collection Value */}
        {pricingEnabled && totalCollectionValue && (
          <div style={{ padding: '0 20px 12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              backgroundColor: theme.bgCard,
              borderRadius: '12px',
              border: `1px solid ${theme.border}`,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: '500' }}>Est. Collection Value</span>
                {/* A total is a snapshot of prices worked out at different
                    moments, not a live quote. Saying when the oldest of them
                    was calculated is the honest version, and it is also what
                    makes leaving the number alone until a refresh reasonable. */}
                {lastCollectionRefresh && (
                  <span style={{ color: theme.textSecondary, fontSize: '11px', opacity: 0.75 }}>
                    Last calculated {daysSinceRefresh(lastCollectionRefresh)}
                  </span>
                )}
              </div>
              <span style={{ color: '#4ade80', fontSize: '20px', fontWeight: '700' }}>{totalCollectionValue}</span>
            </div>
          </div>
        )}

        {/* Trade Activity */}
        <div style={styles.tradeReputationSection}>
          <h3 style={styles.sectionTitle}>Trade Activity</h3>
          <div style={styles.tradeActivityGrid}>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.accent }}>{tradeStats.messagesStarted}</span>
              <span style={styles.tradeActivityLabel}>Started</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.warning }}>{tradeStats.scheduled}</span>
              <span style={styles.tradeActivityLabel}>Scheduled</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.success }}>{tradeStats.completed}</span>
              <span style={styles.tradeActivityLabel}>Completed</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.textSecondary }}>{tradeStats.incomplete}</span>
              <span style={styles.tradeActivityLabel}>Incomplete</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: tradeStats.cancelled > 0 ? theme.error : theme.textSecondary }}>{tradeStats.cancelled || 0}</span>
              <span style={styles.tradeActivityLabel}>Cancelled{tradeStats.cancelledLastMinute > 0 ? ` (${tradeStats.cancelledLastMinute} late)` : ''}</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.success }}>{tradeStats.completionRate}%</span>
              <span style={styles.tradeActivityLabel}>Success</span>
            </div>
          </div>

          {/* Activity Indicators */}
          <div style={styles.activityIndicatorsRow}>
            <div style={styles.activityIndicator}>
              <span style={styles.activityLabel}>Last Active</span>
              <span style={{ ...styles.activityValue, color: tradeStats.lastActive ? theme.success : theme.textMuted }}>
                {tradeStats.lastActive
                  ? (() => {
                      const diff = Date.now() - new Date(tradeStats.lastActive);
                      const hours = Math.floor(diff / (1000 * 60 * 60));
                      const days = Math.floor(hours / 24);
                      if (days > 30) return '30d+';
                      if (days > 0) return `${days}d ago`;
                      if (hours > 0) return `${hours}h ago`;
                      return 'Just now';
                    })()
                  : 'None'}
              </span>
            </div>
            <div style={styles.activityIndicator}>
              <span style={styles.activityLabel}>Avg Response</span>
              <span style={{
                ...styles.activityValue,
                color: tradeStats.avgResponseTime !== null
                  ? (tradeStats.avgResponseTime < 2 ? theme.success : tradeStats.avgResponseTime < 12 ? theme.warning : theme.error)
                  : theme.textMuted
              }}>
                {tradeStats.avgResponseTime !== null
                  ? (() => {
                      const hours = tradeStats.avgResponseTime;
                      if (hours < 1) return `${Math.round(hours * 60)}m`;
                      if (hours < 24) return `${Math.round(hours)}h`;
                      return `${Math.round(hours / 24)}d`;
                    })()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  // Chevron Icon for collapsible sections
  const ChevronIcon = ({ isOpen }) => (
    <motion.svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <polyline points="6 9 12 15 18 9" />
    </motion.svg>
  );

  // Close smart search handler
  const closeSmartSearch = useCallback(() => {
    setSmartSearch({ open: false, query: '', expandedSection: null });
    setSearchQuery('');
  }, []);

  // Smart search card click handler
  const handleSmartSearchCardClick = useCallback((card, type) => {
    closeSmartSearch();
    if (type === 'owned') {
      navigate(`/card/${card.id}`);
    } else if (type === 'wish') {
      navigate(`/card/tcg_${card.tcgdex_card_id.replace('-', '_')}`);
    } else if (type === 'tcg') {
      const parts = card.id.split('-');
      const setId = parts.slice(0, -1).join('-');
      const localId = parts[parts.length - 1];
      navigate(`/card/tcg_${setId}_${localId}`);
    }
  }, [closeSmartSearch, navigate]);

  // Smart search set click handler
  const handleSmartSearchSetClick = useCallback((set) => {
    closeSmartSearch();
    setSelectedCardSet(set.id);
    setSetCardFilter('all');
  }, [closeSmartSearch]);

  // Memoized search panel data
  const searchPanelData = useMemo(() => {
    if (!smartSearch.open || !smartSearch.query || smartSearch.query.length < 2) {
      return null;
    }

    const matchingOwnedCards = cards.filter(card =>
      card.name?.toLowerCase().includes(smartSearch.query.toLowerCase())
    );

    const matchingWishCards = wishListItems.filter(item =>
      item.card_name?.toLowerCase().includes(smartSearch.query.toLowerCase())
    );

    const matchingSets = allSets.filter(set =>
      set.name?.toLowerCase().includes(smartSearch.query.toLowerCase())
    ).slice(0, 20);

    const isCardOwned = (tcgId) => cards.some(c => c.card_id === tcgId);
    const isCardWished = (tcgId) => wishListItems.some(w => w.tcgdex_card_id === tcgId);

    return {
      matchingOwnedCards,
      matchingWishCards,
      matchingSets,
      isCardOwned,
      isCardWished,
      sections: [
        {
          id: 'yourCards',
          title: 'Your Cards',
          count: matchingOwnedCards.length,
          color: theme.accent,
          bgColor: theme.accentBg,
        },
        {
          id: 'wishList',
          title: 'Wish List',
          count: matchingWishCards.length,
          color: theme.warning,
          bgColor: theme.warningBg,
        },
        {
          id: 'allCards',
          title: `All "${smartSearch.query}"`,
          count: tcgSearchResults.length,
          color: theme.purple,
          bgColor: theme.purpleBg,
          loading: tcgSearchLoading,
        },
        {
          id: 'sets',
          title: 'Sets',
          count: matchingSets.length,
          color: theme.success,
          bgColor: theme.successBg,
        },
      ],
    };
  }, [smartSearch.open, smartSearch.query, cards, wishListItems, allSets, tcgSearchResults, tcgSearchLoading, theme]);

  // Smart Search Results Panel - memoized JSX
  const searchResultsPanel = useMemo(() => {
    if (!searchPanelData) return null;

    const { matchingOwnedCards, matchingWishCards, matchingSets, isCardOwned, isCardWished, sections } = searchPanelData;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        style={styles.searchResultsPanel}
      >
        {/* Backdrop blur overlay */}
        <div
          style={styles.searchResultsBackdrop}
          onClick={closeSmartSearch}
        />

        {/* Results Container */}
        <motion.div
          style={styles.searchResultsContainer}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, delay: 0.05 }}
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              style={styles.searchSection}
            >
              {/* Section Header */}
              <motion.button
                style={{
                  ...styles.searchSectionHeader,
                  borderColor: smartSearch.expandedSection === section.id ? section.color : theme.border,
                }}
                onClick={() => section.count > 0 && toggleSearchSection(section.id)}
                whileTap={{ scale: 0.98 }}
                disabled={section.count === 0 && !section.loading}
              >
                <div style={styles.searchSectionLeft}>
                  <span style={{
                    ...styles.searchSectionTitle,
                    color: section.count > 0 ? theme.text : theme.textSecondary,
                  }}>
                    {section.title}
                  </span>
                </div>
                <div style={styles.searchSectionRight}>
                  {section.loading ? (
                    <motion.div
                      style={styles.searchSectionSpinner}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  ) : (
                    <motion.span
                      style={{
                        ...styles.searchSectionCount,
                        backgroundColor: section.count > 0 ? section.bgColor : theme.bgCard,
                        color: section.count > 0 ? section.color : theme.textSecondary,
                      }}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {section.count}
                    </motion.span>
                  )}
                  {section.count > 0 && (
                    <ChevronIcon isOpen={smartSearch.expandedSection === section.id} />
                  )}
                </div>
              </motion.button>

              {/* Expanded Content */}
              <AnimatePresence>
                {smartSearch.expandedSection === section.id && section.count > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={styles.searchSectionContent}
                  >
                    <div style={styles.searchSectionScrollable}>
                    <div style={styles.searchCardsGrid}>
                      {section.id === 'yourCards' && matchingOwnedCards.map((card) => (
                        <motion.div
                          key={card.id}
                          style={styles.searchCardItem}
                          onClick={() => handleSmartSearchCardClick(card, 'owned')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <CardImage
                            digitalImage={card.digital_image_url}
                            userPhoto={card.original_image_url}
                            name={card.name}
                            number={card.number}
                            setName={card.set_name}
                            thumb
                            imgStyle={styles.searchCardImg}
                            theme={theme}
                          />
                          <div style={{
                            ...styles.searchCardBadge,
                            backgroundColor: theme.accent,
                          }}>O</div>
                        </motion.div>
                      ))}
                      {section.id === 'wishList' && matchingWishCards.map((item) => {
                        // Extract localId same way as normal wish list view
                        const cardNum = item.tcgdex_card_id?.replace(`${item.set_id}-`, '') || '';
                        return (
                        <motion.div
                          key={item.id}
                          style={styles.searchCardItem}
                          onClick={() => handleSmartSearchCardClick(item, 'wish')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <CardImage
                            digitalImage={thumbOf(item.image_url)}
                            name={item.card_name}
                            number={cardNum}
                            setName={item.set_name}
                            imgStyle={styles.searchCardImg}
                            theme={theme}
                          />
                          <div style={{
                            ...styles.searchCardBadge,
                            backgroundColor: theme.warning,
                          }}>W</div>
                        </motion.div>
                        );
                      })}
                      {section.id === 'allCards' && tcgSearchResults.map((card) => {
                        const owned = isCardOwned(card.id);
                        const wished = isCardWished(card.id);
                        return (
                          <motion.div
                            key={card.id}
                            style={{
                              ...styles.searchCardItem,
                              opacity: (owned || wished) ? 1 : 0.6,
                            }}
                            onClick={() => handleSmartSearchCardClick(card, 'tcg')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <CardImage
                              digitalImage={thumbOf(card.image)}
                              name={card.name}
                              number={card.localId}
                              setName={card.set?.name}
                              imgStyle={styles.searchCardImg}
                              theme={theme}
                            />
                            {(owned || wished) && (
                              <div style={styles.searchCardStatus}>
                                {owned && <span style={{ ...styles.searchCardBadgeSmall, backgroundColor: theme.accent }}>O</span>}
                                {wished && <span style={{ ...styles.searchCardBadgeSmall, backgroundColor: theme.warning }}>W</span>}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                      {section.id === 'sets' && matchingSets.map((set) => (
                        <motion.div
                          key={set.id}
                          style={styles.searchSetItem}
                          onClick={() => handleSmartSearchSetClick(set)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {set.logo && (
                            <img
                              src={`${set.logo}.png`}
                              alt={set.name}
                              style={styles.searchSetLogo}
                            />
                          )}
                          <span style={styles.searchSetName}>{set.name}</span>
                        </motion.div>
                      ))}
                    </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    );
  }, [searchPanelData, smartSearch.open, smartSearch.expandedSection, tcgSearchLoading, theme, toggleSearchSection, handleSmartSearchCardClick, handleSmartSearchSetClick, closeSmartSearch, tcgSearchResults]);

  // Cards View Component
  const cardsView = useMemo(() => (
    <div style={styles.viewContainer}>
      {/* Cards Grid */}
      <LayoutGroup>
        <motion.div
          layout={gridSettled && layoutBurst}
          style={{
            ...styles.cardsGrid,
            gridTemplateColumns: cardZoom === 2
              ? 'repeat(auto-fill, minmax(140px, 1fr))'
              : cardZoom === 1
              ? 'repeat(auto-fill, minmax(105px, 1fr))'
              : 'repeat(auto-fill, minmax(75px, 1fr))',
            gap: cardZoom === 2 ? '12px' : cardZoom === 1 ? '10px' : '8px',
          }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Add Card Placeholder */}
          {!isViewingOther && (
            <motion.div
              layout
              style={{
                ...styles.addCardPlaceholder,
                ...(cardZoom === 0 ? styles.addCardPlaceholderSmall : {}),
              }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => navigate('/bulk-scanner')}
            >
              <div style={styles.addCardInner}>
                <PlusIcon />
                <AnimatePresence>
                  {cardZoom >= 1 && (
                    <motion.span
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      style={styles.addCardText}
                    >
                      Add Card
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* The only part of this screen that is genuinely waiting. */}
          {initialLoading && filteredCards.length === 0 && (
            <div style={styles.gridLoading}>
              <div style={styles.spinner} />
            </div>
          )}

          {/* A filter that now matches nothing says so, and says which one.
              The case this exists for: someone filters to Fairy, then trades
              away their last Fairy card. Silently dropping the filter would
              show them everything and hide what happened; leaving a blank grid
              would be worse. So the filter stays on, the chip that is causing
              it stays tappable with a strike through it, and this line names
              the way out. */}
          {!initialLoading && filteredCards.length === 0 && collectionFilterCount > 0 && (
            <div style={styles.emptyFiltered}>
              <span style={styles.emptyFilteredTitle}>No cards match that</span>
              <span style={styles.emptyFilteredBody}>
                {emptyFilterReason}
              </span>
              <button
                style={styles.emptyFilteredClear}
                onClick={() => { haptics.tap(); setCardFilters(EMPTY_COLLECTION_FILTERS); }}
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Card Items */}
          {filteredCards.slice(0, cardsDisplayLimit).map((card, index, page) => {
            const isSelected = selectedCards.includes(card.id);
            // Set grouping: a quiet full-width label before the first card of
            // each set. Only when the sort is by set, so the grid stays flat
            // for everything else.
            const groupBySet = cardSortBy === 'set-newest' || cardSortBy === 'set-oldest';
            let setHeader = null;
            if (groupBySet) {
              const meta = cardMeta[card.card_id] || {};
              const key = meta.setId || card.set_name || 'other';
              const prev = index > 0 ? page[index - 1] : null;
              const prevMeta = prev ? (cardMeta[prev.card_id] || {}) : null;
              const prevKey = prev ? (prevMeta.setId || prev.set_name || 'other') : null;
              if (key !== prevKey) {
                const label = meta.setName || card.set_name || 'Other';
                const year = meta.releaseDate ? String(meta.releaseDate).slice(0, 4) : '';
                setHeader = (
                  <div key={`set-${key}`} style={styles.setGroupHeader}>
                    <span style={styles.setGroupName}>{label}</span>
                    {year && <span style={styles.setGroupYear}>{year}</span>}
                  </div>
                );
              }
            }
            return (
            <Fragment key={card.id}>
            {setHeader}
            <motion.div
              // Arriving is not a movement. Layout animation is for a sort, a
              // filter or a zoom, where a tile genuinely travels from somewhere
              // the eye can follow. The hold lift lives on the inner layer so
              // the layout pass never snaps it.
              layout={gridSettled && layoutBurst}
              initial={false}
              style={{ zIndex: pressingId === card.id || poppedId === card.id ? 2 : undefined }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => handleCardClick(card)}
              {...(isViewingOther ? {} : longPressProps(card))}
            >
            <motion.div
              animate={{ scale: holdScale(card.id, pressingId, poppedId) }}
              transition={{ scale: holdTransition(card.id, pressingId, poppedId) }}
              style={{
                ...styles.cardItem,
                ...(bulkEditMode && isSelected ? styles.cardItemSelected : {}),
              }}
            >
              <div style={styles.cardImageWrapper}>
                <CardImage
                  digitalImage={card.digital_image_url}
                  userPhoto={card.original_image_url}
                  name={card.name}
                  number={card.number}
                  setName={card.set_name}
                  preferPhoto={imageFilter === 'photo'}
                  thumb
                  imgStyle={styles.cardImage}
                  theme={theme}
                  loading="lazy"
                />
                {/* Which printing this is, top left.
                    Every printing shares one piece of art, so the picture
                    cannot tell a 1st Edition from an Unlimited and the gap
                    between them is the gap between $40 and $400. Top edge,
                    because price and trade status already own the bottom. */}
                <OwnedPrintingBadge
                  card={card}
                  style={{ position: 'absolute', top: '4px', left: '4px', zIndex: 2 }}
                />
                {/* Trade status badge - bottom left (show for all users) */}
                <div style={{
                  ...styles.cardBadge,
                  ...styles.cardBadgeBottomLeft,
                  ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                  backgroundColor: card.trading_status === 'TRADE' ? 'rgba(34, 197, 94, 0.9)' : 'rgba(14, 165, 233, 0.9)',
                }}>
                  <span style={styles.cardBadgeText}>
                    {card.trading_status === 'TRADE' ? 'T' : 'PC'}
                  </span>
                </div>
                {/* Price badge - bottom right. A TCG Pocket card is digital
                    only and has no price to put here, so the badge says what
                    the card is instead of leaving a gap. */}
                {pricingEnabled && isTcgPocketCard(card) && (
                  <TcgPocketChip theme={theme} isDark={isDark} small={cardZoom === 0} />
                )}
                {pricingEnabled && !isTcgPocketCard(card) && cardPrices[card.id] && (
                  <div style={{
                    ...styles.cardBadge,
                    ...styles.cardBadgeBottomRight,
                    ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: '#4ade80',
                  }}>
                    <span style={styles.cardBadgeText}>
                      {cardPrices[card.id].formatted}
                    </span>
                  </div>
                )}
                {/* Visibility badge - only on own collection, only if private */}
                {!isViewingOther && card.is_private && (
                  <div style={{
                    ...styles.cardBadge,
                    ...(bulkEditMode ? styles.cardBadgeTopLeft : styles.cardBadgeTopRight),
                    ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                    backgroundColor: 'rgba(107, 114, 128, 0.9)',
                  }}>
                    <svg width={cardZoom === 0 ? 8 : 10} height={cardZoom === 0 ? 8 : 10} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 17a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2 2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm6-9a6 6 0 0 0-12 0v2H4v10h16V10h-2V8zm-2 0v2H8V8a4 4 0 1 1 8 0z"/>
                    </svg>
                  </div>
                )}
                {/* Bulk edit selection indicator */}
                {bulkEditMode && (
                  <div style={{
                    ...styles.selectionIndicator,
                    ...(isSelected ? styles.selectionIndicatorActive : {}),
                  }}>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              <AnimatePresence>
                {cardZoom >= 1 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={styles.cardInfo}
                    transition={{ duration: 0.25 }}
                  >
                    <span style={styles.cardName}>{card.name}</span>
                    {cardZoom === 2 && <span style={styles.cardGrade}>{card.grade}</span>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            </motion.div>
            </Fragment>
            );
          })}
        </motion.div>
      </LayoutGroup>
      <div ref={loadMoreRef} style={{ height: 1 }} />
    </div>
  ), [cardZoom, filteredCards, cardsDisplayLimit, selectedCards, bulkEditMode, isViewingOther, styles, handleCardClick, cardPrices, pricingEnabled, gridSettled, initialLoading, cardSortBy, cardMeta, cancelBulkEdit, longPressProps, pressingId, poppedId, layoutBurst]);

  /**
   * All view (item 23).
   *
   * Deliberately NOT a full-screen takeover: it lives under the same header,
   * search bar, zoom and sort controls as every other tab. Search narrows all
   * three sections at once, so one query answers "do I have anything to do with
   * this word" across eras, sets and cards.
   */
  // Loaded lazily the first time Explore (key 'all') is opened, so it never
  // slows the All tab (key 'cards').
  //
  // Deliberately does NOT depend on allSets. It used to, and that was a race:
  // allSets resolving changed the deps mid-flight, the cleanup marked the
  // in-flight request cancelled, the retry bailed on the still-true loading
  // flag, and the loading flag was itself only cleared on the non-cancelled
  // path. Net effect, eras never arrived and the spinner never stopped.
  // All is a drill-down built from state, not routes, so ScrollManager (which
  // keys off location) never sees these moves. Reset the scroller by hand or
  // you open a set already halfway down it.
  useEffect(() => {
    if (activeTab !== 'all') return;
    const el = document.querySelector('.app-content');
    if (el) el.scrollTop = 0;
  }, [activeTab, allMode, allEra, selectedCardSet]);

  // Entering a set fresh should show the whole set. A sticky 'owned' or 'wish'
  // filter from a previous visit reads as "no cards loaded".
  useEffect(() => {
    if (selectedCardSet) setSetCardFilter('all');
  }, [selectedCardSet]);

  const erasRequestedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'all' || erasRequestedRef.current) return;
    erasRequestedRef.current = true;
    setAllErasLoading(true);
    (async () => {
      try {
        const eraRows = await cardData.getEras();
        setAllEras(eraRows || []);
      } catch (e) {
        console.error('[All] era load failed:', e.message);
        erasRequestedRef.current = false; // allow a retry on the next visit
      } finally {
        setAllErasLoading(false);
      }
    })();
  }, [activeTab]);

  // Era artwork is derived from the sets list, which arrives separately.
  useEffect(() => {
    if (!allSets.length) return;
    const byEra = {};
    for (const set of allSets) {
      const eid = set.serie?.id;
      if (!eid || !set.logo) continue;
      (byEra[eid] = byEra[eid] || []).push(set);
    }
    const logos = {};
    for (const [eid, sets] of Object.entries(byEra)) {
      const oldestFirst = [...sets].reverse();
      logos[eid] = (
        oldestFirst.find((x) => (x.cardCount?.official || x.cardCount?.total || 0) >= 20)
        || oldestFirst[0]
      )?.logo;
    }
    // An era with its own stored art wins over the derived pick.
    for (const era of allEras) {
      if (era?.logo_url) logos[era.id] = era.logo_url;
    }
    setAllEraLogos(logos);
  }, [allSets, allEras]);

  /**
   * One search box for the whole tab.
   *
   * "all search bars are smart" — so this is the shared module, not a filter
   * over whatever list happens to be on screen. It answers with eras, sets and
   * cards at once and labels each, because "mega" is legitimately all three.
   * A query outranks the current level: you should not have to already know
   * which era a set is in to find it.
   */
  const smart = useSmartSearch(activeTab === 'all' ? searchQuery : '', {
    eras: allEras,
    sets: allSets,
    limit: 60,
    enabled: activeTab === 'all',
    // The facets describe the whole match for the filter sheet. They are a
    // third query on every keystroke, so they are asked for only while the
    // sheet is open; a ticked filter fetches them regardless, inside
    // searchCatalog, because they decide which tier answers.
    facets: sortFilterModalOpen || hasCatalogFilters(searchFilters),
    // Both pushed into the query, never applied to the sixty rows on screen.
    // The owner wanted the results workable - "so i could do the same filter
    // sort thing as i do on other screens" - and re-ordering a page would put
    // the page in order and lie about the rest of the match.
    sort: searchSort,
    filters: searchFilters,
  });

  const ownedCardIdSet = useMemo(
    () => new Set(cards.map((c) => c.card_id).filter(Boolean)),
    [cards],
  );

  /**
   * Each era's worth, the same way the era tiles work it out: the sum of its
   * sets' totals from set_price_totals. Computed once here rather than inside
   * the search results, so a search hit and the tile it opens can never quote
   * two different numbers for the same era.
   */
  const eraValues = useMemo(() => {
    if (!pricingEnabled) return null;
    const out = {};
    for (const era of allEras) {
      const ids = allSets.filter((x) => x.serie?.id === era.id).map((x) => x.id);
      const { total } = sumSetTotals(ids, setTotals);
      if (total > 0) out[era.id] = total;
    }
    return out;
  }, [pricingEnabled, allEras, allSets, setTotals]);

  /** Open a set's cards directly, from anywhere in the tab. */
  const openCardSet = useCallback((setId) => {
    haptics.tap();
    setSearchQuery('');
    setSelectedCardSet(setId);
  }, []);

  /** Open a catalogue card, preferring the user's own copy if they have one. */
  /**
   * Wishing for a card from Explore, without walking to a picker first.
   *
   * Hold a card, say where it goes, and the grid you were already using becomes
   * the selection surface - same search, same sort, same filters, same zoom.
   * The owner's framing: "If they are on explore and go OH I REALLY WANT THAT
   * CARD, they tap hold and wish for it in the various ways." Only offered on
   * your own collection; there is nothing to wish for while viewing somebody
   * else's.
   */
  const wish = useWishFromExplore({
    userId: profile?.id,
    showToast,
    enabled: !isViewingOther,
  });

  const openCatalogCard = useCallback((card, badge = null) => {
    // While selecting, a tap is a tick rather than a door. Same tile, same
    // grid; only what the tap means changes.
    if (wish.selecting) { wish.onToggle(card); return; }
    haptics.tap();
    const mine = cards.find((c) => c.card_id === card.id);
    // An owned card already knows which printing it is, so it needs no
    // parameter. A catalogue card does not, and without one every printing of
    // a Charizard opened the same screen.
    navigate(mine
      ? `/card/${mine.id}`
      : `/card/tcg_${card.set_id}_${card.local_id}${printingParam(badge)}`);
  }, [cards, navigate, wish]);

  const allView = useMemo(() => {
    const q = (searchQuery || '').toLowerCase().trim();
    const ownedInSet = (setId) => cards.filter((c) => c.card_id?.startsWith(`${setId}-`)).length;
    // Printings first, because that is what the set grid draws and what the
    // tile promises: Base Set reads 408, not 102.
    const setTotal = (set) => set.printingCount || set.cardCount?.official || set.cardCount?.total || 0;

    // Sorting lives in the shared filter/sort modal (the funnel button in the
    // toolbar), not a second control of its own.
    const applySort = (list, nameOf, dateOf) => {
      const out = [...list];
      out.sort((a, b) => {
        if (allSortDir === 'a-z') return (nameOf(a) || '').localeCompare(nameOf(b) || '');
        if (allSortDir === 'z-a') return (nameOf(b) || '').localeCompare(nameOf(a) || '');
        const da = dateOf(a) || '', db = dateOf(b) || '';
        // A set with no date goes last in BOTH directions. Comparing '' as
        // a date made ten undated promo sets the "oldest" sets in the game,
        // ahead of Base Set 1999.
        if (!da && db) return 1;
        if (da && !db) return -1;
        return allSortDir === 'oldest' ? da.localeCompare(db) : db.localeCompare(da);
      });
      return out;
    };

    // Search is no longer handled here. A query outranks wherever you are
    // standing, so it is answered one level up by SmartSearchResults for the
    // whole tab — see the content switch below.

    // ---------- SETS LEVEL ----------
    if (allEra) {
      const era = allEras.find((e) => e.id === allEra);
      let sets = allSets.filter((set) => set.serie?.id === allEra);
      if (q) sets = sets.filter((set) => (set.name || '').toLowerCase().includes(q));
      sets = applySort(sets, (x) => x.name, (x) => x.releaseDate);

      return (
        <div style={styles.allWrap}>
          <div style={styles.allBar}>
            <button style={styles.allBack} onClick={() => { haptics.tap(); setAllEra(null); }}>
              ‹ Eras
            </button>
            <span style={styles.allBarTitle}>{era?.display_name || allEra}</span>
            <span style={styles.allBarSpacer} />
          </div>

          <div style={{ ...styles.allGrid, gridTemplateColumns: `repeat(${allZoom === 0 ? 3 : allZoom === 1 ? 2 : 1}, 1fr)` }}>
          {sets.map((set) => {
            const owned = ownedInSet(set.id);
            const total = setTotal(set);
            const pct = total ? Math.min(100, Math.round((owned / total) * 100)) : 0;
            return (
              <button
                key={set.id}
                style={styles.allCard}
                onClick={() => { haptics.tap(); setSelectedCardSet(set.id); }}
              >
                <div style={allZoom === 2 ? styles.allCardArtLarge : styles.allCardArt}>
                  {set.logo ? (
                    <img src={`${set.logo}.png`} alt={set.name} loading="lazy"
                         style={styles.allTileImg}
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : <span style={styles.allTileFallback}>{set.name.slice(0, 2)}</span>}
                </div>
                <div style={styles.allCardBody}>
                  <span style={styles.allTileName}>{set.name}</span>
                  {/* An unreleased set has no owned count worth printing: nobody
                      holds one, so "0/26" reads as a gap in your collection
                      rather than as a date. It says when it lands instead. */}
                  {isUpcomingSet(set) ? (
                    <SetUpcomingBadge
                      set={set}
                      theme={theme}
                      withDate
                      style={{ alignSelf: 'flex-start', marginTop: '2px' }}
                    />
                  ) : (
                    <span style={styles.allTileMeta}>
                      {owned}/{total || '?'}{set.releaseDate ? ` · ${String(set.releaseDate).slice(0, 4)}` : ''}
                    </span>
                  )}
                  {pricingEnabled && isTcgPocketSet(set.id) && (
                    <TcgPocketLine theme={theme} isDark={isDark} compact />
                  )}
                  {pricingEnabled && !isTcgPocketSet(set.id) && formatMoney(setTotals[set.id]?.total_near_mint) && (
                    <span style={styles.allTileValue}>Set value {formatMoney(setTotals[set.id]?.total_near_mint)}</span>
                  )}
                  {/* An empty progress bar under a set nobody can own yet reads
                      as failure rather than as information. */}
                  {!isUpcomingSet(set) && (
                    <div style={styles.allBarTrack}>
                      <div style={{ ...styles.allBarFill, width: `${pct}%` }} />
                    </div>
                  )}
                </div>
                
              </button>
            );
          })}
          </div>
          {!sets.length && <div style={styles.allEmpty}>No sets match that search.</div>}
        </div>
      );
    }

    // ---------- ERA LEVEL ----------
    let eras = allEras;
    if (q) eras = eras.filter((e) => (e.display_name || e.id).toLowerCase().includes(q));
    // Eras arrive in display_order, which is already oldest to newest.
    const ordered = (allSortDir === 'a-z' || allSortDir === 'z-a')
      ? applySort(eras, (e) => e.display_name || e.id, () => '')
      : (allSortDir === 'oldest' ? eras : [...eras].reverse());

    return (
      <div style={styles.allWrap}>
        <div style={styles.allBar}>
          {/* The era list is now one door of several, so it needs a way back
              to the menu just like the others. */}
          <button style={styles.allBack} onClick={() => { haptics.tap(); setAllMode(null); }}>
            &#8249; Menu
          </button>
          <span style={styles.allBarTitle}>Eras</span>
          <span style={styles.allBarSpacer} />
        </div>

        {allErasLoading && !ordered.length && <div style={styles.allEmpty}>Loading eras…</div>}

        <div style={{ ...styles.allGrid, gridTemplateColumns: `repeat(${allZoom === 0 ? 3 : allZoom === 1 ? 2 : 1}, 1fr)` }}>
        {ordered.map((era) => {
          const setsInEra = allSets.filter((set) => set.serie?.id === era.id);
          const owned = setsInEra.reduce((n, set) => n + ownedInSet(set.id), 0);
          const total = setsInEra.reduce((n, set) => n + setTotal(set), 0);
          const pct = total ? Math.min(100, Math.round((owned / total) * 100)) : 0;
          const logo = allEraLogos[era.id];
          const eraValue = pricingEnabled ? formatMoney(sumSetTotals(setsInEra.map((x) => x.id), setTotals).total) : '';
          return (
            <button
              key={era.id}
              style={styles.allCard}
              onClick={() => { haptics.tap(); setAllEra(era.id); }}
            >
              <div style={allZoom === 2 ? styles.allCardArtLarge : styles.allCardArt}>
                {logo ? (
                  <img src={`${logo}.png`} alt={era.display_name} loading="lazy"
                       style={styles.allTileImg}
                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : <span style={styles.allTileFallback}>{(era.display_name || era.id).slice(0, 2)}</span>}
              </div>
              <div style={styles.allCardBody}>
                <span style={styles.allTileName}>{era.display_name || era.id}</span>
                <span style={styles.allTileMeta}>
                  {setsInEra.length} sets · {owned}/{total || '?'}
                  {era.start_year ? ` · ${era.start_year}` : ''}
                </span>
                {pricingEnabled && era.id === TCG_POCKET_ERA_ID ? (
                  <TcgPocketLine theme={theme} isDark={isDark} compact />
                ) : (
                  eraValue && <span style={styles.allTileValue}>Era value {eraValue}</span>
                )}
                <div style={styles.allBarTrack}>
                  <div style={{ ...styles.allBarFill, width: `${pct}%` }} />
                </div>
              </div>
              
            </button>
          );
        })}

        </div>

        {!allErasLoading && !ordered.length && (
          <div style={styles.allEmpty}>No eras match that search.</div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEras, allEraLogos, allErasLoading, allEra, allSortDir, allZoom, allSets, cards,
      searchQuery, styles, pricingEnabled, setTotals]);

  // ---- the flat catalogue behind the All Cards tile -----------------------
  //
  // 60 rows at a time, in whatever order and under whatever filter was chosen,
  // and both of those are answered by the database. Every row names its set,
  // because alphabetical order puts twenty Charizards in a row and without the
  // set they are indistinguishable.
  const CATALOG_PAGE = 60;
  const loadMoreCatalog = useCallback(async () => {
    if (catalogFetching.current || catalogExhausted.current) return;
    catalogFetching.current = true;
    setCatalogLoading(true);
    const from = catalogCursor.current;
    const run = catalogRun.current;
    try {
      const { rows, total } = await listCatalogCards({
        from,
        to: from + CATALOG_PAGE - 1,
        withCount: from === 0,
        sort: catalogSort,
        filters: catalogFilters,
      });
      // The order or the filter changed while this page was in the air. It is
      // now a page of the wrong list, so it is dropped rather than appended
      // under the new heading.
      if (run !== catalogRun.current) return;
      if (total != null) {
        setCatalogMatches(total);
        // The tile's subtitle is the size of the whole catalogue, not the size
        // of whatever is filtered right now, so it is only ever set from an
        // unfiltered read.
        if (!hasCatalogFilters(catalogFilters)) setCatalogTotal(total);
      }
      catalogCursor.current = from + rows.length;
      if (rows.length < CATALOG_PAGE) catalogExhausted.current = true;
      setCatalogCards((prev) => (from === 0 ? rows : [...prev, ...rows]));
    } catch (e) {
      console.error('[All] catalogue page failed:', e.message);
      if (run === catalogRun.current) catalogExhausted.current = true;
    } finally {
      catalogFetching.current = false;
      if (run === catalogRun.current) setCatalogLoading(false);
    }
  }, [catalogSort, catalogFilters]);

  /**
   * Start the list again whenever the order or the filter changes.
   *
   * The cursor, the exhausted flag and the rows already on screen all belong to
   * the old list. Bumping the run counter first means any page still in flight
   * lands on the floor instead of on top of the new first page.
   */
  const catalogSettingsKey = `${catalogSort}|${JSON.stringify(catalogFilters)}`;
  useEffect(() => {
    catalogRun.current += 1;
    catalogCursor.current = 0;
    catalogExhausted.current = false;
    catalogFetching.current = false;
    setCatalogCards([]);
    setCatalogMatches(null);
    if (activeTab !== 'all' || allMode !== 'cards' || selectedCardSet) return;
    loadMoreCatalogRef.current?.();
    // loadMoreCatalog is rebuilt by the same change that runs this, so it is
    // reached through the ref rather than listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogSettingsKey]);

  /**
   * The size of the catalogue, for the tile that opens it.
   *
   * The tile used to read "Every card" until you had already been inside once,
   * which is the one moment the number is worth having. This is a HEAD request
   * with an exact count and no rows, asked once per session.
   */
  useEffect(() => {
    if (activeTab !== 'all' || catalogTotal != null) return undefined;
    let alive = true;
    countCatalogCards({})
      .then((n) => { if (alive && n != null) setCatalogTotal(n); })
      .catch(() => { /* the tile keeps saying "Every card" */ });
    return () => { alive = false; };
  }, [activeTab, catalogTotal]);

  /**
   * The count behind the loading line, for a filtered list.
   *
   * The first page already returns an exact count, so this only runs when a
   * filter is on and the page has not answered yet - it is what lets the line
   * say "2,486 Ultra Rare cards" while the first rows are still coming back,
   * instead of naming the whole catalogue and being wrong.
   */
  useEffect(() => {
    if (activeTab !== 'all' || allMode !== 'cards' || selectedCardSet) return undefined;
    if (!hasCatalogFilters(catalogFilters)) { setCatalogMatches(catalogTotal); return undefined; }
    let alive = true;
    countCatalogCards({ filters: catalogFilters })
      .then((n) => { if (alive && n != null) setCatalogMatches(n); })
      .catch(() => { /* the line falls back to naming no number */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, allMode, selectedCardSet, catalogSettingsKey]);

  loadMoreCatalogRef.current = loadMoreCatalog;

  // Nothing is fetched until the tile is actually opened.
  useEffect(() => {
    if (activeTab !== 'all' || allMode !== 'cards' || selectedCardSet) return;
    if (catalogCursor.current === 0) loadMoreCatalog();
  }, [activeTab, allMode, selectedCardSet, loadMoreCatalog]);

  /**
   * The Explore-tab menu.
   *
   * Two columns, driven by a list, so a fifth or sixth door is one entry here
   * and not a rewrite. No zoom and no organise control: the owner has not
   * decided he wants either, and an empty shelf is easier to fill than a wrong
   * one is to clear.
   */
  const allMenuView = useMemo(() => {
    // Real, self-hosted artwork. The era rows carry logo_url and the set rows
    // carry their own, so the tiles show the actual thing they open rather
    // than a generic glyph.
    const eraArt = allEras.map((e) => allEraLogos[e.id] || e.logo_url).filter(Boolean);
    const setArt = allSets.filter((x) => x.logo).map((x) => x.logo);

    const collage = (urls) => (
      <div style={styles.allMenuCollage}>
        {urls.slice(0, 4).map((url, i) => (
          <div key={`${url}-${i}`} style={styles.allMenuCollageCell}>
            <img
              src={`${url}.png`}
              alt=""
              loading="lazy"
              style={styles.allMenuCollageImg}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        ))}
      </div>
    );

    const tiles = [
      {
        key: 'chase',
        label: 'Chase Cards',
        meta: 'Coming soon',
        art: <img src={cardChaseLogo} alt="CardChase" style={styles.allMenuLogo} />,
        go: () => setAllMode('chase'),
      },
      {
        key: 'era',
        label: 'Cards by Era',
        meta: allEras.length ? `${allEras.length} eras` : 'Every era',
        art: eraArt.length ? collage(eraArt) : <Layers size={26} strokeWidth={1.7} color="var(--text-secondary)" />,
        go: () => setAllMode('era'),
      },
      {
        key: 'sets',
        label: 'Cards by Set',
        meta: allSets.length ? `${allSets.length} sets` : 'Every set',
        art: setArt.length ? collage(setArt) : <Library size={26} strokeWidth={1.7} color="var(--text-secondary)" />,
        go: () => setAllMode('sets'),
      },
      {
        key: 'cards',
        // "Cards" was the thinnest word on a menu where it opens the widest
        // door. This one is the whole catalogue, so it says so.
        label: 'All Cards',
        // Printings, not cards, because that is what a set IS and what the grid
        // draws: 29,683 cards are 33,075 printings once Shadowless, 1st Edition
        // and the stamps each get their own tile. Summed from the sets already
        // in hand, so it costs no read.
        meta: (() => {
          const printings = allSets.reduce((n, s) => n + (s.printingCount || 0), 0);
          if (printings) return `${printings.toLocaleString()} printings`;
          return catalogTotal ? `${catalogTotal.toLocaleString()} cards` : 'Every card';
        })(),
        // A fan of five real cards, 1999 to 2024, instead of a grid glyph. The
        // other three tiles carry real artwork and this one looked like a
        // placeholder beside them.
        art: <CardFan />,
        go: () => setAllMode('cards'),
      },
    ];

    return (
      <div style={styles.allWrap}>
        <div style={styles.allMenuGrid}>
          {tiles.map((tile) => (
            <button
              key={tile.key}
              style={styles.allMenuTile}
              onClick={() => { haptics.tap(); tile.go(); }}
            >
              {/* The fan fills the box edge to edge; a logo needs breathing
                  room around it. Same box, two paddings. */}
              <div style={tile.key === 'cards' ? styles.allMenuArtFull : styles.allMenuArt}>
                {tile.art}
              </div>
              <div style={styles.allCardBody}>
                <span style={styles.allTileName}>{tile.label}</span>
                <span style={styles.allTileMeta}>{tile.meta}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }, [allEras, allEraLogos, allSets, catalogTotal, styles]);

  /**
   * Every set, flat, with no era above it.
   *
   * The owner's reason, verbatim: "sometimes people remember a set but don't
   * remember what era that would be in". So a tap here goes straight into the
   * set's cards — the era level is not a stop on this road.
   */
  const allSetsFlatView = useMemo(() => {
    const ownedInSet = (setId) => cards.filter((c) => c.card_id?.startsWith(`${setId}-`)).length;
    // Printings first, because that is what the set grid draws and what the
    // tile promises: Base Set reads 408, not 102.
    const setTotal = (set) => set.printingCount || set.cardCount?.official || set.cardCount?.total || 0;

    const sorted = [...allSets].sort((a, b) => {
      if (allSortDir === 'a-z') return (a.name || '').localeCompare(b.name || '');
      if (allSortDir === 'z-a') return (b.name || '').localeCompare(a.name || '');
      const da = a.releaseDate || '', db = b.releaseDate || '';
      // Undated sets last in both directions, same as the era list above.
      if (!da && db) return 1;
      if (da && !db) return -1;
      return allSortDir === 'oldest' ? da.localeCompare(db) : db.localeCompare(da);
    });

    return (
      <div style={styles.allWrap}>
        <div style={styles.allBar}>
          <button style={styles.allBack} onClick={() => { haptics.tap(); setAllMode(null); }}>
            &#8249; Menu
          </button>
          <span style={styles.allBarTitle}>All Sets</span>
          <span style={styles.allBarSpacer} />
        </div>

        {!sorted.length && <div style={styles.allEmpty}>Loading sets…</div>}

        <div style={{ ...styles.allGrid, gridTemplateColumns: `repeat(${allZoom === 0 ? 3 : allZoom === 1 ? 2 : 1}, 1fr)` }}>
          {sorted.map((set) => {
            const owned = ownedInSet(set.id);
            const total = setTotal(set);
            const pct = total ? Math.min(100, Math.round((owned / total) * 100)) : 0;
            return (
              <button key={set.id} style={styles.allCard} onClick={() => openCardSet(set.id)}>
                <div style={allZoom === 2 ? styles.allCardArtLarge : styles.allCardArt}>
                  {set.logo ? (
                    <img src={`${set.logo}.png`} alt={set.name} loading="lazy"
                         style={styles.allTileImg}
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : <span style={styles.allTileFallback}>{(set.name || '?').slice(0, 2)}</span>}
                </div>
                <div style={styles.allCardBody}>
                  <span style={styles.allTileName}>{set.name}</span>
                  {/* An unreleased set has no owned count worth printing: nobody
                      holds one, so "0/26" reads as a gap in your collection
                      rather than as a date. It says when it lands instead. */}
                  {isUpcomingSet(set) ? (
                    <SetUpcomingBadge
                      set={set}
                      theme={theme}
                      withDate
                      style={{ alignSelf: 'flex-start', marginTop: '2px' }}
                    />
                  ) : (
                    <span style={styles.allTileMeta}>
                      {owned}/{total || '?'}{set.releaseDate ? ` · ${String(set.releaseDate).slice(0, 4)}` : ''}
                    </span>
                  )}
                  {pricingEnabled && isTcgPocketSet(set.id) && (
                    <TcgPocketLine theme={theme} isDark={isDark} compact />
                  )}
                  {pricingEnabled && !isTcgPocketSet(set.id) && formatMoney(setTotals[set.id]?.total_near_mint) && (
                    <span style={styles.allTileValue}>Set value {formatMoney(setTotals[set.id]?.total_near_mint)}</span>
                  )}
                  {/* An empty progress bar under a set nobody can own yet reads
                      as failure rather than as information. */}
                  {!isUpcomingSet(set) && (
                    <div style={styles.allBarTrack}>
                      <div style={{ ...styles.allBarFill, width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [allSets, allSortDir, allZoom, cards, styles, theme, isDark, pricingEnabled, setTotals, openCardSet]);

  /**
   * What the screen is doing, said in words with the real number in it.
   *
   * Three rules, because this kind of copy is worse than a spinner when it is
   * wrong. The number is counted, never remembered - it is the count of what
   * the current filter matches, so a Fire list says how many Fire cards there
   * are and not how many cards exist. The verb is the work actually being
   * done: the database really does order every matching row before returning
   * the first sixty, so "Sorting 23,591 cards A to Z" is true, while "Loading
   * 23,591 cards" would not be, because sixty are being loaded. And a page
   * added to a list already on screen says so quietly instead of announcing
   * the whole catalogue again.
   */
  const catalogStatus = useMemo(() => {
    if (!catalogLoading) return null;
    if (catalogCards.length) return 'Loading more';
    const n = catalogMatches ?? catalogTotal;
    // The verb comes first here - "Ordering 238 Illustration Rare..." - so the
    // description joins the sentence rather than starting one.
    const described = describeCatalogFilters(catalogFilters, allEras);
    const what = described.charAt(0).toLowerCase() + described.slice(1);
    const verb = SORT_VERB[catalogSort] || 'Sorting';
    const phrase = SORT_PHRASE[catalogSort] || '';
    if (n == null) return `${verb} ${what} ${phrase}`.trim();
    return `${verb} ${n.toLocaleString()} ${what} ${phrase}`.trim();
  }, [catalogLoading, catalogCards.length, catalogMatches, catalogTotal, catalogFilters, catalogSort, allEras]);

  /**
   * Hold the status line back for a beat.
   *
   * A page usually comes back in well under a tenth of a second, and a line
   * that appears and vanishes in that time reads as a glitch rather than as
   * information. Same reasoning as LazyFallback in App.js, which is a blank
   * rectangle rather than a spinner for exactly this reason.
   */
  const [catalogStatusVisible, setCatalogStatusVisible] = useState(false);
  useEffect(() => {
    if (!catalogStatus) { setCatalogStatusVisible(false); return undefined; }
    const t = setTimeout(() => setCatalogStatusVisible(true), 220);
    return () => clearTimeout(t);
  }, [catalogStatus]);

  /**
   * What the search is doing, said in words with the real number in it.
   *
   * Same three rules as the catalogue's line, and the same two modules behind
   * it, so the two screens speak one language: the number is COUNTED from the
   * whole match rather than remembered or guessed, the verb is the work
   * actually being done, and the sort's own phrase names the order out loud.
   * "Ranking 305 cards by value, highest first" - the owner's "Sorting 22K
   * a-z", with a search's numbers in it.
   *
   * It appears UNDER the cards during a re-sort rather than replacing them. A
   * sort that blanks the grid and brings it back reads as a page reload.
   */
  const searchStatus = useMemo(() => {
    if (!smart.active || !smart.loading) return null;
    const verb = SORT_VERB[searchSort] || 'Sorting';
    const phrase = SORT_PHRASE[searchSort] || '';
    const described = describeCatalogFilters(searchFilters, allEras);
    const what = described.charAt(0).toLowerCase() + described.slice(1);
    const n = smart.matchTotal;
    if (n == null) return `${verb} ${what} ${phrase}`.trim();
    return `${verb} ${n.toLocaleString()} ${what} ${phrase}`.trim();
  }, [smart.active, smart.loading, smart.matchTotal, searchSort, searchFilters, allEras]);

  /**
   * Hold the status line back for a beat, the same 220ms the catalogue uses.
   *
   * A re-sort usually answers in well under a tenth of a second, and a line
   * that appears and vanishes in that time reads as a glitch rather than as
   * information.
   */
  const [searchStatusVisible, setSearchStatusVisible] = useState(false);
  useEffect(() => {
    if (!searchStatus) { setSearchStatusVisible(false); return undefined; }
    const t = setTimeout(() => setSearchStatusVisible(true), 220);
    return () => clearTimeout(t);
  }, [searchStatus]);

  /**
   * Which zoom and which filters the Explore toolbar is pointing at.
   *
   * A live search outranks whichever level you were standing on, exactly as the
   * results themselves do, so the two controls follow the results rather than
   * the level underneath them. One button, one icon, three places - not a
   * fourth zoom control built for the search.
   */
  const exploreZoom = smart.active
    ? searchZoom
    : (allMode === 'cards' ? catalogZoom : allZoom);
  const bumpExploreZoom = useCallback(() => {
    haptics.select();
    // Era rows, set rows and the flat catalogue all answer to the same
    // preference now, so there is nothing to branch on.
    cycleZoom();
  }, [cycleZoom]);
  const exploreFilters = activeTab !== 'all'
    ? null
    : (smart.active ? searchFilters : (allMode === 'cards' ? catalogFilters : null));
  // See the comment on the toolbar itself for why a live Explore search is the
  // one thing that keeps these buttons on screen.
  const toolsHidden = searchFocused && !(activeTab === 'all' && smart.active);

  /** Every card in the catalogue, flat and paged from the server. */
  const allCardsFlatView = useMemo(() => {
    const filtered = hasCatalogFilters(catalogFilters);
    const shown = catalogMatches ?? catalogTotal;
    const groupMode = catalogGrouping(catalogSort);
    return (
    <div style={styles.allWrap}>
      <div style={styles.allBar}>
        <button style={styles.allBack} onClick={() => { haptics.tap(); setAllMode(null); }}>
          &#8249; Menu
        </button>
        <span style={styles.allBarTitle}>
          {shown == null
            ? 'All Cards'
            : filtered
              ? `${shown.toLocaleString()} Cards`
              : `All ${shown.toLocaleString()} Cards`}
        </span>
        <span style={styles.allBarSpacer} />
      </div>

      {/* What is on, in one line, with the way out on it. Without this the
          only clue that a filter is narrowing 23,591 cards down to forty is
          the count in the title. */}
      {filtered && (
        <button
          style={styles.catalogFilterNote}
          onClick={() => { haptics.tap(); setCatalogFilters(EMPTY_CATALOG_FILTERS); }}
        >
          <span style={styles.catalogFilterNoteText}>
            {describeCatalogFilters(catalogFilters, allEras)}
          </span>
          <span style={styles.catalogFilterNoteClear}>Clear</span>
        </button>
      )}

      {/* The same grid the search results draw, which is the point: zoom, the
          separator headings and the price chip are one implementation shared by
          both screens rather than two that drift. */}
      <CatalogCardGrid
        cards={catalogCards}
        zoom={catalogZoom}
        groupMode={groupMode}
        eras={allEras}
        theme={theme}
        isDark={isDark}
        pricingEnabled={pricingEnabled}
        ownedCardIds={ownedCardIdSet}
        onCardTap={openCatalogCard}
        onCardHold={wish.onHold}
        selectedIds={wish.selectedIds}
        style={{ paddingBottom: 'var(--nav-clearance)' }}
      />

      {/* The loading line and the empty state can never both be on screen:
          one asks you to wait, the other says the waiting is over. */}
      {catalogStatus && catalogStatusVisible && (
        <div style={styles.catalogStatus}>
          <div style={styles.catalogStatusDot} />
          <span style={styles.catalogStatusText}>{catalogStatus}</span>
        </div>
      )}
      {!catalogLoading && !catalogCards.length && (
        <div style={styles.allEmpty}>
          {filtered ? 'No cards match all of that. Try unticking one.' : 'No cards loaded.'}
        </div>
      )}
      <div ref={loadMoreRef} style={{ height: 1 }} />
    </div>
    );
  }, [catalogCards, catalogTotal, catalogMatches, catalogLoading, catalogFilters,
      catalogSort, catalogZoom, catalogStatus, catalogStatusVisible, ownedCardIdSet,
      allEras, styles, theme, isDark, pricingEnabled, openCatalogCard, loadMoreRef]);

  /** Chase Cards. Announced, not built. */
  const chaseCardsView = useMemo(() => (
    <div style={styles.allWrap}>
      <div style={styles.allBar}>
        <button style={styles.allBack} onClick={() => { haptics.tap(); setAllMode(null); }}>
          &#8249; Menu
        </button>
        <span style={styles.allBarTitle}>Chase Cards</span>
        <span style={styles.allBarSpacer} />
      </div>
      <div style={styles.chasePanel}>
        <img src={cardChaseLogo} alt="CardChase" style={styles.chaseLogo} />
        <span style={styles.chaseTitle}>Chase Cards is coming</span>
        <span style={styles.chaseBody}>
          The cards worth chasing, gathered in one place.
        </span>
      </div>
    </div>
  ), [styles]);

  // Binders View Component
  const bindersView = useMemo(() => (
    <div style={styles.viewContainer}>
      <div style={styles.bindersGrid}>
        {/* Add Binder Placeholder */}
        {!isViewingOther && (
          <div style={styles.binderItem} onClick={() => setAddBinderModalOpen(true)}>
            <div style={styles.addBinderPlaceholder}>
              <PlusIcon />
            </div>
            <span style={styles.binderName}>Add Binder</span>
            <span style={styles.binderCount}>Create new</span>
          </div>
        )}

        {/* Binder Items */}
        {sortedBinders.map((binder) => (
          <div
            key={binder.id}
            style={styles.binderItem}
            onClick={() => {
              if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
              handleBinderClick(binder);
            }}
            {...binderHoldProps(binder)}
          >
            <div style={{
              ...styles.binderImageWrapper,
              ...(isTradeBinder(binder) ? styles.binderImageWrapperTrade : {}),
            }}>
              <img
                src={binderImages[binder.image] || binderImages.blue}
                alt={binder.name}
                style={styles.binderImage}
              />
              <div style={styles.binderGlow} />
            </div>
            {/* The one binder with a job, said plainly. Everything marked
                tradable lives here and this is what another trader sees.
                It rides on top of the binder rather than under the count: a
                line of its own above the art would push this tile's binder
                out of line with every other binder in the row. */}
            {isTradeBinder(binder) && (
              <span style={{ ...styles.binderTradeTag, ...styles.binderTradeTagTop }}>
                For trade
              </span>
            )}
            <span style={styles.binderName}>{binder.name}</span>
            <span style={styles.binderCount}>
              {getBinderCardCount(binder.id)}
              {isTradeBinder(binder) ? ` of ${TRADE_BINDER_LIMIT}` : ''} cards
            </span>
            {pricingEnabled && getBinderValue(binder.id) && (
              <span style={styles.binderValue}>{getBinderValue(binder.id)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [sortedBinders, styles, handleBinderClick, getBinderCardCount, pricingEnabled, getBinderValue, isViewingOther]);

  // Refresh wish list data
  const refreshWishList = async () => {
    if (profile?.id) {
      const { data: wishData } = await supabase
        .from('wish_list')
        .select('*')
        .eq('user_id', profile.id)
        .order('added_at', { ascending: false });
      setWishListItems(wishData || []);
    }
  };

  /**
   * Open a chase card in the existing card detail screen.
   *
   * A wish row is a catalogue card rather than an owned one, so the route is
   * the tcg_ form, and the left/right arrows walk the ranked ten in slot order
   * rather than in the order the rows came back.
   */
  const openWishCard = (card) => {
    const ranked = buildTopTenSlots(wishListItems.filter((w) => w.is_priority === true))
      .map((slot) => slot.card)
      .filter(Boolean);
    const routeFor = (w) => `tcg_${w.set_id}_${(w.tcgdex_card_id || '').replace(`${w.set_id}-`, '')}`;
    const index = ranked.findIndex((w) => w.id === card.id);
    setCardNavigationContext({
      type: 'wishlist',
      cardIds: ranked.map(routeFor),
      currentIndex: index >= 0 ? index : 0,
      sourceLabel: isViewingOther ? `${profile?.username || 'User'}'s Chase Cards` : 'My Chase Cards',
    });
    navigate(`/card/${routeFor(card)}`);
  };

  // The Wish tab is four chase sections now, and they live in
  // src/components/wish. What used to be here was one 250-line memo holding the
  // two-tier Top 20 / More grids, a sort, a zoom, and a 500ms hold cancelled
  // only by onTouchMove - the last copy of the bug where a slow scroll opened
  // the menu and yanked the cards out from under a thumb. The grid is ranked
  // and fixed at ten cells now, so there is nothing left to sort or zoom.
  const wishListView = (
    <WishTab
      userId={profile?.id}
      items={wishListItems}
      loading={wishListLoading}
      prices={catPrices}
      pricingEnabled={pricingEnabled}
      sets={allSets}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      setTotals={setTotals}
      /* The plus buttons in Wish do not open a picker of their own. They switch
         to Explore and arm selection there, leaving Explore's own sort, filters
         and zoom exactly as they were - which is the whole point of reusing it
         rather than rebuilding it. */
      onGoPick={(kind, binder) => {
        setActiveTab('all');
        setAllMode('cards');
        setSelectedCardSet(null);
        wish.beginFor(kind, { binder: binder || null });
      }}
      isViewingOther={isViewingOther}
      ownerName={profile?.username || ''}
      onOpenCard={openWishCard}
      onRefresh={refreshWishList}
      showToast={showToast}
    />
  );

  // Cards Sets View - shows all sets with owned card counts
  const cardsSetsView = useMemo(() => {
    // Loading state
    if (allSetsLoading) {
      return (
        <div style={styles.viewContainer}>
          <div style={styles.wishListEmpty}>
            <div style={styles.spinner} />
            <span style={{ color: theme.textSecondary, marginTop: '12px' }}>Loading sets...</span>
          </div>
        </div>
      );
    }

    // Calculate owned count per set
    const getOwnedCount = (setId) => {
      return cards.filter(c => c.card_id?.startsWith(`${setId}-`)).length;
    };

    // Calculate wish count per set
    const getWishCount = (setId) => {
      return wishListItems.filter(w => w.tcgdex_card_id?.startsWith(`${setId}-`)).length;
    };

    // Filter sets by search query
    let filteredSets = allSets
      .filter(set => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (set.name || '').toLowerCase().includes(query);
      });

    // Apply owned filter
    if (cardsSetsFilter === 'owned') {
      filteredSets = filteredSets.filter(set => getOwnedCount(set.id) > 0);
    }

    // Apply sorting using sortIndex (from API's releaseDate sort)
    filteredSets = filteredSets.sort((a, b) => {
      switch (cardsSetsSort) {
        case 'a-z':
          return (a.name || '').localeCompare(b.name || '');
        case 'z-a':
          return (b.name || '').localeCompare(a.name || '');
        case 'oldest':
          return String(a.releaseDate || '9999').localeCompare(String(b.releaseDate || '9999'));
        case 'newest':
        default:
          return String(b.releaseDate || '').localeCompare(String(a.releaseDate || ''));
      }
    }).slice(0, searchQuery ? 100 : setsDisplayLimit);

    // Zoom controls: 0 = 3 cols, 1 = 2 cols, 2 = 1 col
    const gridColumns = cardsSetsZoom === 0 ? 3 : cardsSetsZoom === 1 ? 2 : 1;
    const logoHeight = cardsSetsZoom === 0 ? '50px' : cardsSetsZoom === 1 ? '70px' : '100px';
    const imgHeight = cardsSetsZoom === 0 ? '36px' : cardsSetsZoom === 1 ? '54px' : '80px';

    return (
      <div style={styles.viewContainer}>
        <div style={{
          ...styles.setsBrowseGrid,
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        }}>
          {filteredSets.map((set) => {
            const ownedCount = getOwnedCount(set.id);
            const wishCount = getWishCount(set.id);
            const totalCount = set.printingCount || set.cardCount?.total || set.cardCount?.official || '?';
            const isEmpty = ownedCount === 0 && wishCount === 0 && !isUpcomingSet(set);

            return (
              <div
                key={set.id}
                style={{
                  ...styles.setBrowseCard,
                  opacity: isEmpty ? 0.4 : 1,
                  filter: isEmpty ? 'grayscale(50%)' : 'none',
                }}
                onClick={() => {
                  setSelectedCardSet(set.id);
                  setSetCardFilter('all'); // Reset filter when entering set
                }}
              >
                <div style={{ ...styles.setBrowseLogo, height: logoHeight }}>
                  {set.logo ? (
                    <img
                      src={`${set.logo}.png`}
                      alt={set.name}
                      style={{ ...styles.setBrowseLogoImg, height: imgHeight }}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{
                    ...styles.setBrowseLogoFallback,
                    display: set.logo ? 'none' : 'flex'
                  }}>
                    {set.name?.[0] || '?'}
                  </div>
                </div>
                <div style={styles.setBrowseInfo}>
                  {cardsSetsZoom >= 1 && <span style={styles.setBrowseName}>{set.name}</span>}
                  {isUpcomingSet(set) ? (
                    <SetUpcomingBadge set={set} theme={theme} />
                  ) : (
                    <div style={styles.setBrowseCounts}>
                      <span style={styles.setBrowseCountOwned}>{ownedCount}/{totalCount}</span>
                      <span style={styles.setBrowseCountWish}>{wishCount}/{totalCount}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        </div>
      </div>
    );
  }, [allSetsLoading, allSets, cards, wishListItems, cardsSetsFilter, cardsSetsSort, cardsSetsZoom, searchQuery, setsDisplayLimit, styles, theme]);

  /* WHAT THE SET VIEW DRAWS, worked out here rather than inside it.

     The view itself is a plain function called from the render tree, not a
     mounted component, so it cannot hold hooks of its own: the memos, the
     observer and the paging state have to live in the page. That is also why
     the printing button sits in the set's own header row instead of being
     registered onto the toolbar - this surface IS the page, so it reaches the
     toolbar directly rather than through GridControlsContext. Zoom and sort
     come from that toolbar; only the printing filter is drawn here, because it
     is the one control that has to say out loud which printing it is on. */
  const isSetCardOwned = useCallback(
    (localId) => cards.some((c) => c.card_id === `${selectedCardSet}-${localId}`),
    [cards, selectedCardSet],
  );
  const isSetCardWished = useCallback(
    (localId) => wishListItems.some((w) => w.tcgdex_card_id === `${selectedCardSet}-${localId}`),
    [wishListItems, selectedCardSet],
  );

  /** The cards of the open set that the search, the chips and the sort leave. */
  const setDetailCards = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return tcgSetCards
      .filter((card) => {
        if (!searchQuery) return true;
        return (card.name || '').toLowerCase().includes(query)
          || (card.localId || '').toLowerCase().includes(query);
      })
      .filter((card) => {
        if (setCardFilter === 'owned') return isSetCardOwned(card.localId);
        if (setCardFilter === 'wish') return isSetCardWished(card.localId);
        return true;
      })
      .sort((a, b) => {
        switch (setCardsSort) {
          case 'number-desc':
            return (b.localId || '').localeCompare(a.localId || '', undefined, { numeric: true });
          case 'name-asc':
            return (a.name || '').localeCompare(b.name || '');
          case 'name-desc':
            return (b.name || '').localeCompare(a.name || '');
          case 'number-asc':
          default:
            return (a.localId || '').localeCompare(b.localId || '', undefined, { numeric: true });
        }
      });
  }, [tcgSetCards, searchQuery, setCardFilter, setCardsSort, isSetCardOwned, isSetCardWished]);

  /**
   * One tile per (card, printing), in the card order the sort asked for.
   *
   * Four Charizards, four badges, and now four photographs - which is the fact
   * this grid exists to teach. A chosen printing drops every slot that is not
   * it, so Base Set goes from 408 tiles to the 102 of that one print run.
   */
  const setSlotTiles = useMemo(
    () => buildSetPrintingTiles({ cards: setDetailCards, slots: setSlots, printing: setPrinting }),
    [setDetailCards, setSlots, setPrinting],
  );

  /**
   * What one printing is worth, summed over its own slots.
   *
   * The full set value is the materialized total and stays the headline
   * everywhere else, but a grid narrowed to 1st Edition beside a number that
   * counts all four printings is a number contradicting the screen it sits on.
   */
  const setPrintingValue = useMemo(
    () => sumPrintingValue(setSlots, setPrinting),
    [setSlots, setPrinting],
  );

  // Back to the first page whenever the grid becomes a different grid.
  useEffect(() => {
    setSetSlotsShown(SET_SLOT_FIRST_PAGE);
  }, [selectedCardSet, setPrinting, setCardFilter, setCardsSort, searchQuery]);

  /* The grid grows as it is scrolled. The app scrolls inside .app-content
     rather than the document, which is the scroller the observer has to be
     rooted at - the same one CardImage is told about. */
  useEffect(() => {
    const node = setSlotSentinelRef.current;
    if (!node || setSlotsShown >= setSlotTiles.length) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSetSlotsShown((n) => n + SET_SLOT_PAGE_STEP);
      return undefined;
    }
    const root = node.closest('.app-content') || null;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSetSlotsShown((n) => n + SET_SLOT_PAGE_STEP);
    }, { root, rootMargin: '400px 0px', threshold: 0.01 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [setSlotsShown, setSlotTiles.length]);

  // Set Detail View - when drilling into a set from sets view (shows ALL cards from TCGdex)
  const SetDetailView = () => {
    const selectedSetInfo = allSets.find(s => s.id === selectedCardSet);
    const setName = selectedSetInfo?.name || 'Set';
    const totalCount = selectedSetInfo?.printingCount || selectedSetInfo?.cardCount?.total || selectedSetInfo?.cardCount?.official || '?';

    // Check owned/wish status for each card
    const isCardOwned = isSetCardOwned;
    const isCardWished = isSetCardWished;

    // Get owned card data for navigation to actual card detail
    const getOwnedCardData = (localId) => cards.find(c => c.card_id === `${selectedCardSet}-${localId}`);

    // Count for each filter
    const ownedCount = tcgSetCards.filter(c => isCardOwned(c.localId)).length;
    const wishCount = tcgSetCards.filter(c => isCardWished(c.localId)).length;

    /* The cards the chips, the search and the sort leave, and the slots those
       cards hold. Both are worked out above the view - it cannot hold hooks. */
    const filteredSetCards = setDetailCards;
    const tiles = setSlotTiles;

    /* The count follows the grid, and it says what it is out of.
       A bare "408" answered nothing: 408 of what. On All it is the tiles drawn
       against every printing the set has - 102 of 408 once a printing is
       chosen - so the number carries its own denominator and the narrowing is
       visible in it. Owned and Wish stay card-level fractions, which is what
       those chips have always meant. */
    const everyPrinting = selectedSetInfo?.printingCount || setSlots.length || totalCount;
    const getCountDisplay = () => {
      if (setCardFilter === 'owned') return `${ownedCount} of ${totalCount}`;
      if (setCardFilter === 'wish') return `${wishCount} of ${totalCount}`;
      const shown = tiles.length || totalCount;
      return shown === everyPrinting ? `${shown}` : `${shown} of ${everyPrinting}`;
    };

    const gridCols = cardZoom === 0 ? 4 : cardZoom === 1 ? 3 : 2;

    return (
      <div style={styles.viewContainer}>
        {/* Set Header - matches wishlist pattern */}
        <div style={styles.setDetailHeaderSingle}>
          <button
            style={styles.backButtonPremium}
            onClick={() => {
              setSelectedCardSet(null);
              setSetCardFilter('all');
            }}
          >
            <BackIcon />
          </button>
          <div style={styles.setFilterChips}>
            {['all', 'owned', 'wish'].map((filter) => (
              <button
                key={filter}
                style={{
                  ...styles.setFilterChip,
                  ...(setCardFilter === filter ? styles.setFilterChipActive : {}),
                }}
                onClick={() => setSetCardFilter(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
          {/* The printing filter, where a second sort button used to be.

              That button opened the same sheet the toolbar's sort button opens,
              two taps apart on the same screen, so the slot went to the one
              control this grid could not get anywhere else. It names the
              printing it is on rather than hiding it behind an icon: a grid
              quietly showing a quarter of a set is worse than no filter. A set
              with one printing has nothing to narrow, so it gets no button. */}
          {setPrintings.length > 1 && (
            <button
              style={{
                ...styles.setPrintingButton,
                ...(setPrinting
                  ? { backgroundColor: `${theme.accent}26`, border: `1px solid ${theme.accent}`, color: theme.accent }
                  : { backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary }),
              }}
              onClick={() => { haptics.tap(); setPrintingSheetOpen(true); }}
              aria-label={activePrinting
                ? `Showing only the ${activePrinting.label} printing. Change it.`
                : 'Showing every printing. Narrow to one.'}
            >
              <Layers size={13} />
              {/* The chosen printing's short name, or nothing at all when every
                  printing is showing. "Printings" next to a layers icon was the
                  icon said twice, on a row already carrying three chips and a
                  count. The full name is still the accessible name and the
                  sheet's own title, so nothing is only ever an abbreviation. */}
              {activePrinting && (
                <span style={styles.setPrintingLabel}>
                  {printingShortLabel(activePrinting.subtype, activePrinting.stamps, activePrinting.foil)}
                </span>
              )}
              <ChevronDown size={13} />
            </button>
          )}
        </div>
        {/* A TCG Pocket set has no value and no priced count - the cards were
            never printed. "0 of 331 priced" read as a job that had not run. */}
        {pricingEnabled && isTcgPocketSet(selectedCardSet) && (
          <div style={styles.setDetailValue}>
            <TcgPocketLine theme={theme} isDark={isDark} />
          </div>
        )}
        {/* One line, and it is the value of what is on screen.
            It used to be two: the whole set's total, then a second line naming
            the printing with its own total and a priced count. Chase, looking
            at it: "I hate all the text, we just want set value - if you change
            the printing version then it would say that set value". He is right.
            The grid already says which printing it is showing, so repeating it
            here spent three lines saying what one line says. */}
        {pricingEnabled && !isTcgPocketSet(selectedCardSet) && (
          setPrinting
            ? setPrintingValue && formatMoney(setPrintingValue.total) && (
              <div style={styles.setDetailValue}>
                <span style={styles.setDetailCountLead}>{getCountDisplay()}</span>
                <span style={styles.setDetailValueAmount}>
                  Set value {setPrintingValue.approx ? '~' : ''}{formatMoney(setPrintingValue.total)}
                </span>
              </div>
            )
            : setTotals[selectedCardSet] && formatMoney(setTotals[selectedCardSet].total_near_mint) && (
              <div style={styles.setDetailValue}>
                <span style={styles.setDetailCountLead}>{getCountDisplay()}</span>
                <span style={styles.setDetailValueAmount}>
                  Set value {formatMoney(setTotals[selectedCardSet].total_near_mint)}
                </span>
              </div>
            )
        )}

        {/* No explainer line above the grid.
            It carried an apology for repeated art, then an instruction about
            the count badge, and both were a paragraph standing between
            somebody and the cards they came to look at. The badge is a number
            on a card and the Printings control is an arm's length away; if
            that is not enough the answer is a better badge, not a sentence. */}
        {/* The printings are waited for rather than drawn around. Painting 102
            cards and then reflowing to 408 the moment the slots land would move
            every tile under a thumb that had already started scrolling.
            The `tiles.length === 0` this used to also require never held: with
            no slots yet the builder returns one tile per card, so the guard was
            false exactly when it was needed. The grid painted the card's
            artwork, then swapped in each printing's a second later - "one photo
            of the base is what it looks like every time it loads, the variant
            version takes one to two seconds". */}
        {tcgSetCardsLoading || setSlotsLoading ? (
          <div style={styles.wishListEmpty}>
            <div style={styles.spinner} />
            <span style={{ color: theme.textSecondary, marginTop: '12px' }}>
              {tcgSetCardsLoading ? 'Loading cards...' : 'Laying out the printings...'}
            </span>
          </div>
        ) : (
          <>
          <div style={{ ...styles.setCardsGrid, gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
            {/* Only what has been scrolled to is mounted. The rest arrives in
                sixties behind the sentinel below. */}
            {tiles.slice(0, setSlotsShown).map((tile) => {
              const card = tile.card;
              /* Owned means THIS printing, not the card.
                 `isCardOwned` asks "do you own an Alakazam", and the answer was
                 painted on all four of its printings - so a plain Holo in the
                 collection marked the 1st Edition, the Shadowless and the
                 Unlimited as owned too. The slot row already answers the real
                 question, and it also says when it had to guess: a card saved
                 without a print run is inferred onto one printing and carries
                 `printingUnknown`, which is an honest maybe rather than a
                 false yes on three cards somebody does not have. */
              const owned = tile.owned != null ? tile.owned : isCardOwned(card.localId);
              const wished = isCardWished(card.localId);
              const ownedCardData = getOwnedCardData(card.localId);
              const cached = catPrices[tcgKey(selectedCardSet, card.localId)];
              // This printing's price when it has one, the card's when it does
              // not, and a tilde on the second so the two are never confused.
              const priceText = tile.price != null
                ? `${tile.priceIsForPrinting ? '' : '~'}${formatMoney(tile.price)}`
                : (cached ? `~${cached.formatted}` : null);

              return (
                <div
                  key={tile.key}
                  style={{
                    ...styles.setCardItem,
                    opacity: owned ? 1 : 0.75,
                  }}
                  onClick={() => {
                    /* A slot is a printing of a card and the card detail screen
                       is a card, so every printing of one card opens the same
                       card. The prev/next context stays card-level for the same
                       reason: stepping through the same artwork four times would
                       be a worse walk through a set. */
                    if (owned && ownedCardData) {
                      // Navigate to owned card
                      const visibleCards = filteredSetCards.filter(c => isCardOwned(c.localId));
                      const cardIds = visibleCards.map(c => getOwnedCardData(c.localId)?.id).filter(Boolean);
                      const currentIndex = cardIds.indexOf(ownedCardData.id);
                      setCardNavigationContext({
                        type: 'collection-set',
                        cardIds,
                        currentIndex: currentIndex >= 0 ? currentIndex : 0,
                        sourceLabel: setName,
                      });
                      navigate(`/card/${ownedCardData.id}`);
                    } else {
                      // Navigate to TCGdex card (for wish list viewing)
                      const cardIds = filteredSetCards.map(c => `tcg_${selectedCardSet}_${c.localId}`);
                      const currentIndex = filteredSetCards.findIndex(c => c.id === card.id);
                      setCardNavigationContext({
                        type: 'collection-set',
                        cardIds,
                        currentIndex: currentIndex >= 0 ? currentIndex : 0,
                        sourceLabel: setName,
                      });
                      navigate(`/card/tcg_${selectedCardSet}_${card.localId}${printingParam(tile.badge)}`);
                    }
                  }}
                >
                  <div style={styles.setCardImgWrapper}>
                    <CardImage
                      /* The printing's own picture. Four Charizards used to be
                         four badges over one photograph; the badge said
                         Shadowless and the picture was whatever the card came
                         in with. Where a printing shares the card's artwork -
                         plain holo, reverse holo - this is that same URL. */
                      digitalImage={thumbOf(tile.image || card.image)}
                      name={card.name}
                      number={card.localId}
                      setName={card.set?.name}
                      imgStyle={styles.setCardImg}
                      theme={theme}
                      loading="lazy"
                    />
                    {/* The printing, on the top edge. Price owns the bottom, so
                        the two can never fight over the same corner, and the
                        colour of a printing is the same on every screen.

                        On the main printing the badge counts instead of naming.
                        The grid's job is to teach that a Charizard is one of
                        four, and a set opening on one printing would hide that
                        unless the tile says so. "Unlimited" on every card says
                        nothing; "4 printings" is the whole point. */}
                    {setPrinting && tile.printingCount > 1 ? (
                      <button
                        style={{ ...styles.setCardPrinting, ...styles.setCardPrintingCount }}
                        onClick={(ev) => { ev.stopPropagation(); haptics.tap(); setPrintingSheetOpen(true); }}
                      >
                        <Layers size={9} color={theme.text} />
                        <span style={{ ...styles.setCardPrintingText, color: theme.text }}>
                          {tile.printingCount}
                        </span>
                      </button>
                    ) : tile.badge ? (
                      <div style={{ ...styles.setCardPrinting, backgroundColor: tile.badge.color }}>
                        <span style={{ ...styles.setCardPrintingText, color: tile.badge.textColor }}>
                          {tile.badge.label}
                        </span>
                      </div>
                    ) : null}
                    {/* Status indicators */}
                    {(owned || wished) && (
                      <div style={styles.setCardStatus}>
                        {owned && <span style={styles.setCardStatusOwned}>O</span>}
                        {wished && <span style={styles.setCardStatusWish}>W</span>}
                      </div>
                    )}
                    {/* No per-card badge in here. Every card in a TCG Pocket
                        set is a TCG Pocket card and the header above already
                        says so once; 331 copies of it is wallpaper. */}
                    {pricingEnabled && !isTcgPocketSet(selectedCardSet) && priceText && (
                      <div style={{
                        ...styles.cardBadge,
                        ...styles.cardBadgeBottomRight,
                        ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        color: '#4ade80',
                      }}>
                        <span style={styles.cardBadgeText}>{priceText}</span>
                      </div>
                    )}
                  </div>
                  {cardZoom >= 2 && (
                    <span style={styles.setCardName}>{card.name}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div ref={setSlotSentinelRef} style={styles.setSlotSentinel}>
            {setSlotsShown < tiles.length && (
              <span style={styles.setSlotSentinelText}>{tiles.length - setSlotsShown} more...</span>
            )}
          </div>
          </>
        )}

        {!tcgSetCardsLoading && !setSlotsLoading && tiles.length === 0 && (
          <div style={styles.emptyState}>
            <span style={styles.emptyText}>
              {setCardFilter === 'owned' ? 'No owned cards from this set' :
               setCardFilter === 'wish' ? 'No wished cards from this set' :
               'No cards found'}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Binder Detail View
  const BinderDetailView = () => (
    <div style={styles.viewContainer}>
      {/* Binder Header */}
      <div style={styles.binderDetailHeader}>
        <div style={styles.binderDetailTopRow}>
          <button style={styles.backButtonPremium} onClick={handleBackFromBinder}>
            <BackIcon />
          </button>
          <h2 style={styles.binderDetailTitle}>{selectedBinder?.name}</h2>
          <div style={styles.binderDetailImageWrapper}>
            <img
              src={binderImages[selectedBinder?.image] || binderImages.blue}
              alt={selectedBinder?.name}
              style={styles.binderDetailImage}
            />
          </div>
        </div>
        <div style={styles.binderDetailMetaRow}>
          <span style={styles.binderDetailCount}>
            {isTradeBinder(selectedBinder) && (
              <span style={{ ...styles.binderTradeTag, marginTop: 0, marginRight: '8px' }}>For trade</span>
            )}
            {getBinderCardCount(selectedBinder?.id)}
            {isTradeBinder(selectedBinder) ? ` of ${TRADE_BINDER_LIMIT}` : ''} cards
            {pricingEnabled && getBinderValue(selectedBinder?.id) && (
              <span style={{ marginLeft: '8px', color: '#4ade80', fontWeight: '600' }}>{getBinderValue(selectedBinder?.id)}</span>
            )}
          </span>
          <div style={styles.binderActions}>
          {/* Only on your own binder: labels are priced from what you paid and
              what you are asking, which is not somebody else's to print. */}
          {!isViewingOther && (
            <button
              style={styles.binderPrintBtn}
              onClick={() => {
              haptics.tap();
              // Ask the printer where it stands on the way in, not on the way
              // out. A link made earlier in the session is still good.
              printLink.refresh();
              setRoundOpen(true);
            }}
            >
              <PrinterIcon />
              <span>Print</span>
            </button>
          )}
          {/* Beside Print: what in this binder makes money if sold tomorrow.
              Owner only, and only where prices are shown at all. */}
          {!isViewingOther && pricingEnabled && (
            <button
              style={styles.binderPrintBtn}
              onClick={() => {
                haptics.tap();
                navigate(`/binder/${selectedBinder.id}/analyze`);
              }}
            >
              <AnalyzeIcon />
              <span>Analyze</span>
            </button>
          )}
          </div>
        </div>
      </div>

      {/* Cards in Binder */}
      <LayoutGroup>
        <motion.div
          layout={gridSettled && layoutBurst}
          style={{
            ...styles.cardsGrid,
            gridTemplateColumns: cardZoom === 2
              ? 'repeat(auto-fill, minmax(140px, 1fr))'
              : cardZoom === 1
              ? 'repeat(auto-fill, minmax(105px, 1fr))'
              : 'repeat(auto-fill, minmax(75px, 1fr))',
            gap: cardZoom === 2 ? '12px' : cardZoom === 1 ? '10px' : '8px',
          }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Add Card to Binder Placeholder */}
          {!isViewingOther && (
            <motion.div
              layout={gridSettled && layoutBurst}
              initial={false}
              style={{
                ...styles.addCardPlaceholder,
                ...(cardZoom === 0 ? styles.addCardPlaceholderSmall : {}),
              }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => setAddToBinderModalOpen(true)}
            >
              <div style={styles.addCardInner}>
                <PlusIcon />
                <AnimatePresence>
                  {cardZoom >= 1 && (
                    <motion.span
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      style={styles.addCardText}
                    >
                      Add Card
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {getFilteredAndSortedCards(cards.filter(c => c.binder_id === selectedBinder?.id)).map((card) => (
            <motion.div
              key={card.id}
              // Same rule as the main grid: tiles travel for a sort or a zoom,
              // never on arrival and never because a price or a press ticked.
              layout={gridSettled && layoutBurst}
              initial={false}
              style={{ zIndex: pressingId === card.id || poppedId === card.id ? 2 : undefined }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => handleCardClick(card)}
              {...(isViewingOther ? {} : longPressProps(card))}
            >
            <motion.div
              animate={{ scale: holdScale(card.id, pressingId, poppedId) }}
              transition={{ scale: holdTransition(card.id, pressingId, poppedId) }}
              style={{
                ...styles.cardItem,
                ...(bulkEditMode && selectedCards.includes(card.id) ? styles.cardItemSelected : {}),
              }}
            >
              <div style={styles.cardImageWrapper}>
                <CardImage
                  digitalImage={card.digital_image_url}
                  userPhoto={card.original_image_url}
                  name={card.name}
                  number={card.number}
                  setName={card.set_name}
                  preferPhoto={imageFilter === 'photo'}
                  imgStyle={styles.cardImage}
                  theme={theme}
                />
                {bulkEditMode && (
                  <div style={{
                    ...styles.selectionIndicator,
                    ...(selectedCards.includes(card.id) ? styles.selectionIndicatorActive : {}),
                  }}>
                    {selectedCards.includes(card.id) && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                )}
                {/* Price badge - bottom right. A TCG Pocket card is digital
                    only and has no price to put here, so the badge says what
                    the card is instead of leaving a gap. */}
                {pricingEnabled && isTcgPocketCard(card) && (
                  <TcgPocketChip theme={theme} isDark={isDark} small={cardZoom === 0} />
                )}
                {pricingEnabled && !isTcgPocketCard(card) && cardPrices[card.id] && (
                  <div style={{
                    ...styles.cardBadge,
                    ...styles.cardBadgeBottomRight,
                    ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: '#4ade80',
                  }}>
                    <span style={styles.cardBadgeText}>
                      {cardPrices[card.id].formatted}
                    </span>
                  </div>
                )}
                {card.is_private && (
                  <div style={{
                    ...styles.privateIndicator,
                    ...(cardZoom === 0 ? styles.privateIndicatorSmall : cardZoom === 2 ? styles.privateIndicatorLarge : {}),
                  }}>
                    <LockIcon size={cardZoom === 0 ? 10 : cardZoom === 2 ? 14 : 12} />
                  </div>
                )}
              </div>
              <AnimatePresence>
                {cardZoom >= 1 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={styles.cardInfo}
                    transition={{ duration: 0.25 }}
                  >
                    <span style={styles.cardName}>{card.name}</span>
                    {cardZoom === 2 && <span style={styles.cardGrade}>{card.grade}</span>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </LayoutGroup>
    </div>
  );

  // Safety: force initialLoading off after 5s to prevent stuck spinner
  useEffect(() => {
    if (!initialLoading) return;
    const timeout = setTimeout(() => {
      console.warn('[Collection] Force-clearing initialLoading after timeout');
      setInitialLoading(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [initialLoading]);

  // One frame after the first cards land, so that paint is static and every
  // move after it animates.
  useEffect(() => {
    if (gridSettled || cards.length === 0) return undefined;
    const id = requestAnimationFrame(() => setGridSettled(true));
    return () => cancelAnimationFrame(id);
  }, [cards.length, gridSettled]);

  // Deliberately NO full-screen return for initialLoading.
  //
  // Blanking the page meant leaving Cards and coming back gave a black screen
  // with one spinner on it, when the title, the tabs and the search box are all
  // known before any query runs and cost nothing to draw. Only the grid is
  // actually waiting on data, so only the grid says so.

  // Guest mode: only block when viewing OWN collection (/cards). Viewing another
  // user's collection (/user/:userId/collection) stays browsable.
  if (!user && isGuest() && !viewUserId) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg, minHeight: '100vh' }}>
        <GuestEmptyState
          title="Sign in to build your collection"
          body="Create an account to scan cards into binders, build your trade reputation, and connect with traders nearby."
          action="Create Account"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Add Card/Binder Tutorial - Step 2: Point to Add button */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_BUTTONS}
        spotlightMode="bottom-nav-add"
        message={[
          "Next, tap (+ Add) and",
          "select your option!",
          "",
          "Look for the (i) if you",
          "need more help!",
        ]}
      />

      {/* Backdrop for congrats overlays */}
      <TutorialBackdrop
        visible={
          currentStep === TUTORIAL_STEPS.FIRST_CARD_CONGRATS ||
          currentStep === TUTORIAL_STEPS.FIRST_BINDER_CONGRATS
        }
      />

      {/* Congrats after adding first card */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_CARD_CONGRATS}
        mode="chase-congrats"
        message={[
          "Nice work! You just added",
          "your first card!",
          "",
          "Keep building your",
          "collection!",
        ]}
        onContinue={handleCongratsComplete}
        continueText="Got it!"
        onSecondary={() => {
          handleMoreTutorials();
          navigate('/home');
        }}
        secondaryText="More Tutorials"
        onClose={handleCongratsComplete}
      />

      {/* Congrats after adding first binder */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_BINDER_CONGRATS}
        mode="chase-congrats"
        message={[
          "Awesome! You just created",
          "your first binder!",
          "",
          "Now add some cards",
          "to organize!",
        ]}
        onContinue={handleCongratsComplete}
        continueText="Got it!"
        onSecondary={() => {
          handleMoreTutorials();
          navigate('/home');
        }}
        secondaryText="More Tutorials"
        onClose={handleCongratsComplete}
      />

      {/* Header */}
      <div style={styles.header}>
        {isViewingOther && (
          <button style={styles.headerBackBtn} onClick={() => {
            if (location.state?.from) {
              navigate(location.state.from);
            } else {
              navigate(-1);
            }
          }}>
            <BackIcon />
          </button>
        )}
        <div style={{ ...(isViewingOther ? { textAlign: 'center', flex: 1 } : {}) }}>
          <h1 style={{ ...styles.headerTitle, margin: 0 }}>
            {isViewingOther
              ? (profile?.username ? `@${profile.username}` : 'Trainer')
              : 'Collection'
            }
          </h1>
          {isViewingOther && distanceToUser !== null && (
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
              {distanceToUser < 5 ? '< 5' : distanceToUser} mi away
            </span>
          )}
        </div>
        <div style={styles.headerButtons}>
          {/* Wish List Match Indicator - only when viewing others and any matches exist */}
          {isViewingOther && (wishListMatches.length > 0 || reverseMatches.length > 0) && (
            <button
              style={{
                ...styles.matchIndicatorButton,
                // Special glow for mutual matches (both directions have cards)
                ...(wishListMatches.length > 0 && reverseMatches.length > 0 ? styles.mutualMatchButton : {})
              }}
              onClick={() => setMatchModalOpen(true)}
            >
              <MatchBoltIcon />
              <span style={styles.matchBadge}>{wishListMatches.length + reverseMatches.length}</span>
            </button>
          )}
          <button style={styles.iconButton} onClick={() => { haptics.tap(); setShowStatsPanel(true); }}>
            <ActivityIcon />
          </button>
          {/* The manual "refresh all prices" button used to sit here.
              Removed 09.12.2026 because it was the wrong answer to the right
              problem. It repriced the whole collection on a tap, which is a
              vendor call per card whether or not anything had moved, while the
              card that actually needed it - a chase card that traded three
              times in a week - got no more attention than a bulk common. The
              replacement is a refresh policy driven by how often a card is
              actually looked at, which is still being designed. Until then the
              nightly tier job is the only thing that decides, so nobody can
              spend a few hundred vendor calls by tapping an icon twice.
              `handleRefreshPrices` and `refreshCollectionPrices` are left in
              place for that work; the icon went with the button and git has it. */}
          {!isViewingOther && (
            <button style={styles.iconButton} onClick={() => navigate('/profile')}>
              <UserIcon />
            </button>
          )}
        </div>
        <div style={styles.headerBorder} />
      </div>

      {/* Refresh Progress Bar */}
      {refreshing && refreshProgress.total > 0 && (
        <div style={{
          width: '100%',
          height: '3px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <div style={{
            width: `${(refreshProgress.done / refreshProgress.total) * 100}%`,
            height: '100%',
            backgroundColor: theme.accent,
            transition: 'width 0.3s ease',
          }} />
        </div>
      )}

      {/* Tab Switcher */}
      <div style={styles.tabWrapper}>
        <div style={styles.tabContainer}>
          <div
            style={{
              ...styles.tabIndicator,
              ...styles.tabIndicator4Tabs,
              /* Each step is a true quarter of the container.
                 translateX is a percentage of the ELEMENT, not of its parent,
                 and the pill is narrower than a tab by its two 4px margins. So
                 a bare translateX(100%) moved it a quarter minus 8px and the
                 error accumulated: Binder sat 8px left of its tab, Explore
                 16px, Wish 24px, which is why the label looked pushed to the
                 right inside the pill and got worse the further right you went.
                 Adding the 8px back per step makes each one exactly 25%. */
              transform: activeTab === 'cards'
                ? 'translateX(0)'
                : activeTab === 'binders'
                  ? 'translateX(calc(100% + 8px))'
                  : activeTab === 'all'
                    ? 'translateX(calc(200% + 16px))'
                    : 'translateX(calc(300% + 24px))',
            }}
          />
          <button
            style={{ ...styles.tab, ...(activeTab === 'cards' ? styles.tabActive : {}) }}
            onClick={() => {
              if (leavingExploreBlocked('cards')) return;
              haptics.select(); setActiveTab('cards'); setSelectedBinder(null);
            }}
          >
            <CardsIcon />
            <span>All</span>
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'binders' ? styles.tabActive : {}) }}
            onClick={() => {
              if (leavingExploreBlocked('binders')) return;
              haptics.select(); setActiveTab('binders'); setSelectedBinder(null);
            }}
          >
            <BindersIcon />
            <span>Binder</span>
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'all' ? styles.tabActive : {}) }}
            onClick={() => {
              haptics.select();
              setActiveTab('all');
              setSelectedBinder(null);
              // Tapping the tab is "take me to the top", not "put me back where
              // I was three screens deep".
              setAllMode(null);
              setAllEra(null);
              setSelectedCardSet(null);
            }}
          >
            <LayersIcon />
            <span>Explore</span>
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'wishlist' ? styles.tabActive : {}) }}
            onClick={() => {
              if (leavingExploreBlocked('wishlist')) return;
              haptics.select(); setActiveTab('wishlist'); setSelectedBinder(null);
            }}
          >
            <WishListIcon />
            <span>Wish</span>
          </button>
        </div>
      </div>

      <div style={styles.content}>
      {/* The toolbar and the search chips live INSIDE the scroller.

          They used to be siblings of it, pinned above, and hidden by removing
          them from the flex column once you had scrolled 140px. That is what
          the owner felt as a pop: removing a 60px box from above the scroller
          moves everything in it up by 60px in one frame, on top of whatever
          the finger was already doing.

          It was compensated by subtracting the same 60px from scrollTop in the
          same layout pass, which is correct arithmetic and does not work. On
          iOS the scroll position belongs to the gesture while a finger is down:
          the assignment is simply overridden on the next frame. Measured on the
          simulator, crossing the threshold moved the content 61.6px further
          than the drag, and the on-screen readout showed scrollTop going
          120 -> 160 with the subtraction nowhere in it. A fast flick hid it,
          which is why it survived a round of testing; a slow drag is exactly
          the case where the gesture is still holding the scroller.

          So nothing is removed from any layout any more. The toolbar is the
          first thing in the scrolling content and it scrolls away like the top
          of any page, which gains the same room, needs no compensation, and
          cannot jump at any speed because no box ever changes size. Scrolling
          back up brings it back on the way, rather than only at the very top.

          The tab switcher stays pinned above: that is navigation, not chrome,
          and it is how you leave the screen. */}
      {/* Global Toolbar.

          The first thing to go when someone starts scrolling a grid. It
          collapses its own height rather than just fading, because a fade would
          leave the gap it occupies and give back no room at all, and room is
          the entire point of the ask. */}
      {/* No transition on the height, deliberately.
          An animated collapse changes the layout a little on every frame, and
          compensating that exactly would mean measuring and correcting the
          scroll position sixty times a second. Snapping it means one
          measurement and one subtraction, which is exact by construction and
          does not depend on frame rate. The result reads as crisper, not more
          abrupt: because the content does not move at all, the toolbar simply
          is not there any more. The animation the owner asked for is on the nav
          ball, and that one is free to animate because it is position: fixed
          and owns no space in the flow. */}
      <div style={styles.globalToolbar}>
        {/* Search */}
        <div style={styles.searchContainer}>
          <SearchIcon />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={
              activeTab === 'all' ? 'Search cards, sets, eras…'
                : activeTab === 'wishlist' ? 'Search cards or a set'
                  : 'Search...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              setSearchFocused(true);
              // Pull the catalogue snapshot the moment a search is likely, so
              // the first keystroke can answer from memory.
              warmCatalogIndex();
            }}
            onBlur={() => {
              // Delay blur to allow chip clicks
              setTimeout(() => {
                if (!searchQuery) setSearchFocused(false);
              }, 150);
            }}
            style={styles.searchInput}
          />
          {(searchQuery || searchFocused) && (
            <button
              style={styles.searchClearButton}
              onClick={() => {
                setSearchQuery('');
                setSearchFocused(false);
                searchInputRef.current?.blur();
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Toolbar buttons.

            They collapse to nothing while the field is focused, to give the
            input the whole row. THE ONE EXCEPTION is a live search on Explore,
            and it is the exception that fixes the actual bug: `searchFocused`
            is deliberately left true as long as there is text in the box, so
            while a search was on screen these buttons were animated to zero
            width. The zoom and the sort were not merely absent from the search
            results, they were rendered and then collapsed - "theres no way to
            change it", exactly as reported. A screen that offers controls for
            its results has to leave them where a thumb can reach them. */}
        <motion.div
          style={styles.toolbarButtons}
          animate={{
            opacity: toolsHidden ? 0 : 1,
            width: toolsHidden ? 0 : 'auto',
            marginLeft: toolsHidden ? 0 : undefined,
          }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* A nested grid's controls, when one has offered them.
              A wish surface is several components deep inside this page, so it
              registers its zoom and sort rather than drawing a second row of
              them under its own title. They belong on this row, beside the
              search, the way Cards and Binder have always had them. */}
          {gridControls ? (
            <>
              {gridControls.onZoom && (
                <button
                  style={{ ...styles.toolButton, ...(sharedZoom > 0 ? styles.toolButtonActive : {}) }}
                  onClick={() => { haptics.select(); gridControls.onZoom(); }}
                  aria-label={zoomLabel(sharedZoom)}
                >
                  <ZoomStepIcon level={sharedZoom} />
                </button>
              )}
              {gridControls.onSort && (
                <button
                  style={{ ...styles.toolButton, ...(gridControls.sortActive ? styles.toolButtonActive : {}) }}
                  onClick={() => { haptics.select(); gridControls.onSort(); }}
                  aria-label="Sort and filter"
                >
                  <SortIcon />
                  {gridControls.filterCount > 0 && (
                    <span style={styles.toolBadge}>{gridControls.filterCount}</span>
                  )}
                </button>
              )}
              {/* "What am I still missing" is the question these grids exist to
                  answer, so it rides the same row rather than keeping a row of
                  its own alive underneath. */}
              {gridControls.onMissingOnly && (
                <button
                  style={{ ...styles.toolButton, ...(gridControls.missingOnly ? styles.toolButtonActive : {}) }}
                  onClick={() => { haptics.select(); gridControls.onMissingOnly(); }}
                  aria-label={gridControls.missingOnly ? 'Showing only what you are missing' : 'Show only what you are missing'}
                  aria-pressed={!!gridControls.missingOnly}
                >
                  <MissingOnlyIcon />
                </button>
              )}
            </>
          ) : (activeTab === 'cards' || selectedBinder) && (
            <button
              style={{
                ...styles.toolButton,
                ...(cardZoom > 0 ? styles.toolButtonActive : {}),
              }}
              onClick={() => { haptics.select(); cycleZoom(); }}
              aria-label={zoomLabel(cardZoom)}
            >
              <ZoomStepIcon level={cardZoom} />
            </button>
          )}
          {/* The era grid, the flat set grid and the catalogue all have columns
              to change. The menu is four fixed tiles and the owner has not
              asked for a control on it.

              The catalogue was excluded along with sort, and that was wrong:
              the reason to hide sort was that re-ordering one loaded page lies
              about the rows it has not loaded. Zoom makes no such claim. It
              resizes what is already on screen, sends no query, and cannot
              misrepresent anything. */}
          {/* A live query outranks the level underneath it, so the zoom follows
              the results wherever they are shown - including over a set's own
              cards, which is where the content switch puts them too. */}
          {/* A set's own grid answers to it too. It was excluded while the set
              view had a second control row of its own; it draws slots now, four
              hundred of them in Base Set, and resizing them is exactly the kind
              of claim zoom is allowed to make - it sends no query and
              misrepresents nothing. */}
          {!gridControls && activeTab === 'all'
            && (smart.active
              || selectedCardSet
              || (allMode === 'era' || allMode === 'sets' || allMode === 'cards')) && (
            <button
              style={{
                ...styles.toolButton,
                ...(exploreZoom > 0 ? styles.toolButtonActive : {}),
              }}
              onClick={bumpExploreZoom}
            >
              <ZoomStepIcon level={exploreZoom} />
            </button>
          )}
          {/* The menu and the Chase placeholder have no order to choose. The
              flat catalogue does now: it used to be excluded because re-sorting
              one loaded page would have lied about the other 23,000 rows, and
              the sort it offers today runs in the database over all of them. */}
          {/* The Wish tab has no order to choose either: its ten squares are
              ranked by the slot the database handed out, and its sets are
              listed by name. A control that changed nothing was worse than no
              control. */}
          {!gridControls && activeTab !== 'wishlist'
            && !(activeTab === 'all' && !selectedCardSet && !smart.active
             && (!allMode || allMode === 'chase')) && (
            <button
              style={{
                ...styles.toolButton,
                ...(hasCatalogFilters(exploreFilters) ? styles.toolButtonActive : {}),
              }}
              onClick={() => { haptics.select(); setSortFilterModalOpen(true); }}
            >
              <SortIcon />
              {countCatalogFilters(exploreFilters) > 0 && (
                <span style={styles.toolBadge}>{countCatalogFilters(exploreFilters)}</span>
              )}
            </button>
          )}
        </motion.div>
      </div>

      {/* Search Filter Chips — not on All, where the smart results are already
          grouped and labelled by kind. */}
      {searchQuery.length >= 2 && activeTab !== 'all' && (
        <div style={styles.searchFilterChips}>
          <button
            style={{
              ...styles.searchFilterChip,
              backgroundColor: activeTab === 'cards' && cardsViewMode === 'cards' ? 'var(--purple)' : 'var(--bg-card)',
              color: activeTab === 'cards' && cardsViewMode === 'cards' ? '#fff' : 'var(--text)',
            }}
            onClick={() => {
              setActiveTab('cards');
              setCardsViewMode('cards');
              setSelectedCardSet(null);
              setSelectedBinder(null);
              searchInputRef.current?.blur();
            }}
          >
            My Cards ({cards.filter(c =>
              c.card_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              c.name?.toLowerCase().includes(searchQuery.toLowerCase())
            ).length})
          </button>
          <button
            style={{
              ...styles.searchFilterChip,
              backgroundColor: activeTab === 'wishlist' ? 'var(--purple)' : 'var(--bg-card)',
              color: activeTab === 'wishlist' ? '#fff' : 'var(--text)',
            }}
            onClick={() => {
              setActiveTab('wishlist');
              setSelectedWishListSet(null);
              setSelectedBinder(null);
              searchInputRef.current?.blur();
            }}
          >
            Wish List ({wishListItems.filter(w =>
              w.card_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              w.name?.toLowerCase().includes(searchQuery.toLowerCase())
            ).length})
          </button>
          <button
            style={{
              ...styles.searchFilterChip,
              backgroundColor: activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet ? 'var(--purple)' : 'var(--bg-card)',
              color: activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet ? '#fff' : 'var(--text)',
            }}
            onClick={() => {
              searchInputRef.current?.blur();
              navigate('/sets');
            }}
          >
            Sets ({allSets.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase())).length})
          </button>
        </div>
      )}

      {/* Main Content */}
        <AnimatePresence mode="wait">
          {selectedBinder ? (
            <motion.div
              key="binder-detail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {BinderDetailView()}
            </motion.div>
          ) : activeTab === 'cards' ? (
            selectedCardSet ? (
              <motion.div
                key="set-detail"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {SetDetailView()}
              </motion.div>
            ) : cardsViewMode === 'sets' ? (
              <motion.div
                key="cards-sets"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {cardsSetsView}
              </motion.div>
            ) : (
              <motion.div
                key="cards"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {cardsView}
              </motion.div>
            )
          ) : activeTab === 'all' ? (
            /* A query outranks the level you are standing on, so it is answered
               here rather than inside any one view. */
            smart.active ? (
              <motion.div
                key="all-search"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <SmartSearchResults
                  result={smart}
                  theme={theme}
                  isDark={isDark}
                  ownedCardIds={ownedCardIdSet}
                  onPickEra={(era) => {
                    haptics.tap();
                    setSearchQuery('');
                    setAllMode('era');
                    setAllEra(era.id);
                  }}
                  onPickSet={(set) => openCardSet(set.id)}
                  /* The tap seam. A picker will reuse this whole surface to
                     SELECT cards instead of opening them, so this is the one
                     line that changes then - swap the handler, pass
                     selectedCardIds, and the grid, the zoom, the sort and the
                     filters all come along unchanged. */
                  onPickCard={openCatalogCard}
                  onHoldCard={wish.onHold}
                  selectedCardIds={wish.selectedIds}
                  pricingEnabled={pricingEnabled}
                  setTotals={setTotals}
                  eraValues={eraValues}
                  zoom={searchZoom}
                  sort={searchSort}
                  eras={allEras}
                  status={searchStatusVisible ? searchStatus : null}
                  filterNote={hasCatalogFilters(searchFilters) ? {
                    text: describeCatalogFilters(searchFilters, allEras),
                    onClear: () => { haptics.tap(); setSearchFilters(EMPTY_CATALOG_FILTERS); },
                  } : null}
                />
              </motion.div>
            ) : selectedCardSet ? (
              <motion.div
                key="all-set"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {SetDetailView()}
              </motion.div>
            ) : allMode === 'era' ? (
              <motion.div
                key="all-era"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {allView}
              </motion.div>
            ) : allMode === 'sets' ? (
              <motion.div
                key="all-sets-flat"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {allSetsFlatView}
              </motion.div>
            ) : allMode === 'cards' ? (
              <motion.div
                key="all-cards-flat"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {allCardsFlatView}
              </motion.div>
            ) : allMode === 'chase' ? (
              <motion.div
                key="all-chase"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {chaseCardsView}
              </motion.div>
            ) : (
              <motion.div
                key="all-menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {allMenuView}
              </motion.div>
            )
          ) : activeTab === 'binders' ? (
            <motion.div
              key="binders"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {bindersView}
            </motion.div>
          ) : (
            <motion.div
              key="wishlist"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {wishListView}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Stats Panel */}
      <AnimatePresence>
        {showStatsPanel && <StatsPanel />}
      </AnimatePresence>

      {/* All Badges Modal */}
      <AllBadgesModal
        isOpen={allBadgesOpen}
        onClose={() => {
          setAllBadgesOpen(false);
          if (badgesOpenedFromStats) {
            setBadgesOpenedFromStats(false);
            setShowStatsPanel(true);
          }
        }}
        badgeData={badgeData}
        theme={theme}
      />

      {/* Select mode: action bar over the nav, binder picker on top of it */}
      <SelectionActionBar
        open={Boolean(bulkEditMode)}
        count={selectedCards.length}
        total={selectBinderCards ? selectBinderCards.length : filteredCards.length}
        busy={selectBusy}
        onAction={applySelection}
        onSelectAll={() => setSelectedCards((selectBinderCards || filteredCards).map((c) => c.id))}
        onDone={cancelBulkEdit}
      />
      <BinderPickerSheet
        isOpen={binderPickerOpen}
        onClose={() => setBinderPickerOpen(false)}
        binders={binders}
        selectedCards={cards.filter((c) => selectedCards.includes(c.id))}
        onConfirm={handleSelectionToBinder}
      />

      {/* Held binder: Share / Rename / Delete */}
      <BinderActionSheet
        isOpen={!!heldBinder}
        onClose={() => setHeldBinder(null)}
        binder={heldBinder}
        cardCount={heldBinder ? getBinderCardCount(heldBinder.id) : 0}
        onShare={handleShareBinder}
        onRename={handleRenameBinder}
        onDeleteBinderOnly={handleDeleteBinderOnly}
        onDeleteWithCards={handleDeleteBinderWithCards}
      />

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 10004,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
              }}
              onClick={() => !deleteLoading && setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={e => e.stopPropagation()}
                style={{
                  backgroundColor: theme === 'dark' ? '#1a1a2e' : '#ffffff',
                  borderRadius: '20px',
                  padding: '28px 24px',
                  width: '100%',
                  maxWidth: '340px',
                  textAlign: 'center',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: 'rgba(220, 38, 38, 0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </div>
                <h3 style={{
                  fontSize: '18px', fontWeight: 600, margin: '0 0 8px',
                  color: theme === 'dark' ? '#ffffff' : '#1a1a1a',
                }}>
                  Delete {selectedCards.length} card{selectedCards.length !== 1 ? 's' : ''}?
                </h3>
                <p style={{
                  fontSize: '14px', color: theme === 'dark' ? '#999' : '#666',
                  margin: '0 0 24px', lineHeight: 1.5,
                }}>
                  This will remove {selectedCards.length === 1 ? 'this card' : 'these cards'} from your collection. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteLoading}
                    style={{
                      flex: 1, padding: '14px', fontSize: '15px', fontWeight: 500,
                      border: 'none', borderRadius: '12px', cursor: 'pointer',
                      backgroundColor: theme === 'dark' ? '#2a2a3e' : '#f1f5f9',
                      color: theme === 'dark' ? '#ccc' : '#333',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleteLoading}
                    style={{
                      flex: 1, padding: '14px', fontSize: '15px', fontWeight: 600,
                      border: 'none', borderRadius: '12px', cursor: 'pointer',
                      backgroundColor: '#dc2626', color: '#ffffff',
                      opacity: deleteLoading ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                  >
                    {deleteLoading ? (
                      <>
                        <div style={{
                          width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#ffffff', borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite',
                        }} />
                        Deleting...
                      </>
                    ) : 'Delete'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Report User Modal */}
      <ReportUserModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        userId={viewUserId}
        currentUserId={currentUserId}
        onSuccess={handleReportSuccess}
      />

      {/* Add Card Modal (from the All tab) */}
      <AddCardModal
        isOpen={addCardModalOpen}
        onClose={() => setAddCardModalOpen(false)}
        binders={binders}
        onAddCard={handleAddCard}
      />

      {/* Add Binder Modal (from Binders tab) */}
      <AddBinderModal
        isOpen={addBinderModalOpen}
        onClose={() => setAddBinderModalOpen(false)}
        existingBinders={binders}
        onCreateBinder={handleCreateBinder}
      />

      {/* Add to Binder Modal (from inside binder) */}
      <AddToBinderModal
        isOpen={addToBinderModalOpen}
        onClose={() => setAddToBinderModalOpen(false)}
        binder={selectedBinder}
        allCards={cards}
        binders={binders}
        onAddNewCard={handleAddNewCardToBinder}
        onMoveCards={handleMoveCardsToBinder}
      />

      {/* Printing the binder's labels. The cards are passed through the same
          filter and sort the grid is drawing, so the roll comes off in the
          order the pages are in - which is the whole point of doing it the
          night before a show. */}
      {/* Price is not a column on a card. It is looked up per card and kept in
          its own map, so it is merged on below rather than the label renderer
          being taught where the app keeps its prices. */}
      {selectedBinder && (
        <>
          <RoundPricesSheet
            open={roundOpen}
            onClose={() => setRoundOpen(false)}
            cards={printCards}
            binderName={selectedBinder.name}
            printerName={rememberedPrinter()?.name || printerModel()}
            linked={printLink.linked}
            linking={printLink.linking}
            printing={printLink.printing}
            printedCount={printLink.done}
            error={printLink.error}
            onSettings={() => setPrintSheetOpen(true)}
            onPriceSaved={handlePriceTyped}
            onPrint={runPrint}
          />
          <PrintLabelsSheet
            open={printSheetOpen}
            onClose={() => setPrintSheetOpen(false)}
            cards={printCards}
            binderName={selectedBinder.name}
            theme={theme}
            qrUrlFor={qrUrlForLabel(selectedBinder.id)}
          />
        </>
      )}

      {/* Old scan modals removed - now using /scanner page */}

      {/* Sort/Filter Modal */}
      <SortFilterModal
        isOpen={sortFilterModalOpen}
        onClose={() => setSortFilterModalOpen(false)}
        mode={
          selectedBinder ? 'binder-detail'
          : activeTab === 'binders' ? 'binders'
          : activeTab === 'wishlist' ? (selectedWishListSet ? 'wishlist-cards' : 'wishlist-sets')
          : activeTab === 'all' && smart.active ? 'search'
          : activeTab === 'all' && selectedCardSet ? 'set-cards'
          : activeTab === 'all' && allMode === 'cards' ? 'catalog'
          : activeTab === 'all' ? 'cards-sets'
          : activeTab === 'cards' && cardsViewMode === 'sets' && selectedCardSet ? 'set-cards'
          : activeTab === 'cards' && cardsViewMode === 'sets' ? 'cards-sets'
          : 'cards'
        }
        isOwnCollection={!isViewingOther}
        tradeFilter={tradeFilter}
        setTradeFilter={setTradeFilter}
        visibilityFilter={visibilityFilter}
        setVisibilityFilter={setVisibilityFilter}
        imageFilter={imageFilter}
        setImageFilter={setImageFilter}
        sortBy={
          activeTab === 'all' && smart.active ? searchSort
          : activeTab === 'all' && selectedCardSet ? setCardsSort
          : activeTab === 'all' && allMode === 'cards' ? catalogSort
          : activeTab === 'all' ? allSortDir
          : activeTab === 'cards' && cardsViewMode === 'sets' && selectedCardSet ? setCardsSort
          : activeTab === 'cards' && cardsViewMode === 'sets' ? cardsSetsSort
          : activeTab === 'wishlist' ? wishListSortBy
          : selectedBinder || activeTab === 'cards' ? cardSortBy
          : binderSortBy
        }
        setSortBy={
          // Its own setter, so a sort chosen while searching is remembered for
          // searches and nowhere else.
          activeTab === 'all' && smart.active ? setSearchSort
          : activeTab === 'all' && selectedCardSet ? setSetCardsSort
          : activeTab === 'all' && allMode === 'cards' ? setCatalogSort
          : activeTab === 'all' ? setAllSortDir
          : activeTab === 'cards' && cardsViewMode === 'sets' && selectedCardSet ? setSetCardsSort
          : activeTab === 'cards' && cardsViewMode === 'sets' ? setCardsSetsSort
          : activeTab === 'wishlist' ? setWishListSortBy
          // A binder's choice is written to that binder, not to the shared
          // tab setting, which is what lets each one remember its own.
          : selectedBinder ? ((id) => rememberBinderSort(selectedBinder.id, id))
          : activeTab === 'cards' ? setCardSortBy
          : setBinderSortBy
        }
        setsFilter={cardsSetsFilter}
        setSetsFilter={setCardsSetsFilter}
        setsLanguage={setsLanguage}
        setSetsLanguage={setSetsLanguage}
        /* The search sheet carries the catalogue's filter shape - same groups,
           same ids, same chips - through the same two props, pointed at the
           search's own state. One sheet, two places, no second vocabulary. */
        catalogFilters={activeTab === 'all' && smart.active ? searchFilters : catalogFilters}
        setCatalogFilters={activeTab === 'all' && smart.active ? setSearchFilters : setCatalogFilters}
        cardFilters={cardFilters}
        setCardFilters={setCardFilters}
        collectionFacets={collectionFacets}
        /* What THIS result set contains, so the chips it holds none of fade.
           The whole catalogue passes nothing, because every option exists in
           it; a search passes the rarities, types, kinds and eras its own
           matches actually have. */
        catalogFacets={activeTab === 'all' && smart.active ? smart.facets : null}
        catalogEras={allEras}
        onReset={
          activeTab === 'all' && smart.active
            ? () => { setSearchSort(DEFAULT_SEARCH_SORT); setSearchFilters(EMPTY_CATALOG_FILTERS); }
            : activeTab === 'all' && allMode === 'cards'
            ? () => { setCatalogSort(DEFAULT_CATALOG_SORT); setCatalogFilters(EMPTY_CATALOG_FILTERS); }
            : resetPreferences
        }
      />

      {/* Wish List Search Modal */}
      {/* Wish List Match Modal */}
      {/* The destination popup, the docked selection bar and the binder
          chooser. Mounted once for the page rather than per grid, so the
          selection survives moving between search results, a set's cards and
          the flat catalogue. */}
      <WishFromExplore {...wish.ui} theme={theme} />

      {/* Which printing the set view is showing.

          Mounted on the page rather than inside the set view, for the reason
          BottomSheet's own header gives: a sheet opened from inside the page
          scroller loses to the bottom nav and the tab pill whatever its
          z-index says. */}
      <BottomSheet
        open={printingSheetOpen && !!selectedCardSet}
        onClose={() => setPrintingSheetOpen(false)}
        label="Printings"
        overlayStyle={styles.printingOverlay}
        sheetStyle={styles.printingSheet}
      >
        <div style={styles.printingHandle} />
        <span style={styles.printingSheetTitle}>Printings</span>
        <span style={styles.printingSheetNote}>
          A set is its printings. Showing all of them is the whole set; picking one
          shows that print run on its own, and the value follows it.
        </span>
        <button
          style={{
            ...styles.printingOption,
            ...(setPrinting ? null : { backgroundColor: `${theme.accent}1f` }),
          }}
          onClick={() => { haptics.tap(); setSetPrinting(null); setPrintingSheetOpen(false); }}
        >
          <span style={{ ...styles.printingDot, backgroundColor: theme.textSecondary }} />
          <span style={styles.printingOptionLabel}>All printings</span>
          <span style={styles.printingOptionCount}>{setSlots.length}</span>
          {!setPrinting && <Check size={16} color={theme.accent} />}
        </button>
        {setPrintings.map((pr) => {
          const count = countPrintingSlots(setSlots, pr.key);
          const chosen = setPrinting === pr.key;
          return (
            <button
              key={pr.key}
              style={{
                ...styles.printingOption,
                ...(chosen ? { backgroundColor: `${theme.accent}1f` } : null),
              }}
              onClick={() => { haptics.tap(); setSetPrinting(pr.key); setPrintingSheetOpen(false); }}
            >
              <span style={{
                ...styles.printingDot,
                backgroundColor: printingColor(pr.subtype, pr.stamps, pr.foil),
              }} />
              <span style={styles.printingOptionLabel}>{pr.label}</span>
              <span style={styles.printingOptionCount}>{count}</span>
              {chosen && <Check size={16} color={theme.accent} />}
            </button>
          );
        })}
      </BottomSheet>

      <WishListMatchModal
        isOpen={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        theyHaveMatches={wishListMatches}
        youHaveMatches={reverseMatches}
        ownerUsername={profile?.username}
        ownerId={viewUserId}
      />

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              ...styles.toast,
              backgroundColor: toastType === 'warning' ? theme.warning : toastType === 'error' ? theme.error : theme.accent,
              color: toastType === 'warning' ? '#000' : '#fff',
            }}
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

const getStyles = (theme) => ({
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: theme.bg,
    flexShrink: 0,
    position: 'relative',
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: '40px',
    right: '40px',
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
    borderRadius: '1px',
  },
  headerTitle: {
    color: theme.text,
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerBackBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
    flexShrink: 0,
  },
  iconButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    transition: 'all 0.15s ease',
  },
  matchIndicatorButton: {
    position: 'relative',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: 'rgba(250, 204, 21, 0.15)',
    border: `2px solid ${theme.warning}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.warning,
    animation: 'matchPulse 2s ease-in-out infinite',
    boxShadow: '0 0 20px rgba(250, 204, 21, 0.3)',
  },
  matchBadge: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    backgroundColor: theme.error,
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    border: `2px solid ${theme.bg}`,
  },
  mutualMatchButton: {
    // Purple glow for mutual matches (you both have cards each other wants!)
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    border: `2px solid ${theme.purple}`,
    color: theme.purple,
    boxShadow: '0 0 25px rgba(139, 92, 246, 0.4)',
    animation: 'matchPulse 1.5s ease-in-out infinite',
  },
  menuWrapper: {
    position: 'relative',
  },
  tabWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 16px 16px',
    backgroundColor: theme.bg,
  },
  tabContainer: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    backgroundColor: theme.bgSheet,
    borderRadius: '16px',
    padding: '4px',
    border: `1px solid ${theme.border}`,
  },
  tabIndicator: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    height: 'calc(100% - 8px)',
    backgroundColor: theme.accent,
    borderRadius: '12px',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
  },
  tabIndicator2Tabs: {
    width: 'calc(50% - 8px)',
  },
  tabIndicator3Tabs: {
    width: 'calc(33.333% - 8px)',
  },
  allWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingBottom: '20px',
  },
  // Two columns, as asked. A fifth and sixth tile drop in underneath with no
  // change here, which is why the row count is nowhere in this rule.
  // The catalogue grid's own styles used to live here. They are in
  // components/collection/CatalogCardGrid.js now, shared with the search
  // results, which is the whole reason the search stopped honouring zoom: two
  // grids, and only one of them knew about it.
  toolBadge: {
    position: 'absolute',
    top: '-3px',
    right: '-3px',
    minWidth: '15px',
    height: '15px',
    padding: '0 3px',
    borderRadius: '8px',
    backgroundColor: 'var(--purple)',
    color: '#fff',
    fontSize: '9px',
    fontWeight: '700',
    lineHeight: '15px',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  emptyFiltered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '44px 24px',
    textAlign: 'center',
  },
  emptyFilteredTitle: {
    fontSize: '15px',
    fontWeight: '650',
    color: theme.text,
  },
  emptyFilteredBody: {
    fontSize: '13px',
    color: theme.textSecondary,
    maxWidth: '260px',
    lineHeight: 1.4,
  },
  emptyFilteredClear: {
    marginTop: '4px',
    padding: '9px 18px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.border,
    borderRadius: '10px',
    background: 'transparent',
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  catalogFilterNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    width: '100%',
    marginBottom: '8px',
    padding: '7px 10px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    borderRadius: '9px',
    background: 'var(--bg-card)',
    cursor: 'pointer',
    textAlign: 'left',
    WebkitTapHighlightColor: 'transparent',
  },
  catalogFilterNoteText: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  catalogFilterNoteClear: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--purple)',
    flexShrink: 0,
  },
  catalogStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 2px',
    paddingBottom: 'calc(16px + var(--nav-clearance))',
  },
  catalogStatusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--purple)',
    animation: 'pulse 1.1s ease-in-out infinite',
    flexShrink: 0,
  },
  catalogStatusText: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--text-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  allMenuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
    width: '100%',
    gridAutoRows: 'min-content',
    paddingBottom: 'var(--nav-clearance)',
  },
  allMenuTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
    padding: '11px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '14px',
    color: 'var(--text)',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    WebkitTapHighlightColor: 'transparent',
  },
  allMenuArt: {
    width: '100%',
    aspectRatio: '16 / 10',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    boxSizing: 'border-box',
    borderRadius: '10px',
    background: 'var(--bg-sheet)',
    overflow: 'hidden',
  },
  allMenuArtFull: {
    width: '100%',
    aspectRatio: '16 / 10',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
    boxSizing: 'border-box',
    borderRadius: '10px',
    background: 'var(--bg-sheet)',
    overflow: 'hidden',
  },
  allMenuLogo: {
    maxWidth: '78%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  allMenuCollage: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: '5px',
    width: '100%',
    height: '100%',
  },
  allMenuCollageCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  allMenuCollageImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  chasePanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '48px 24px',
    textAlign: 'center',
  },
  chaseLogo: {
    width: '120px',
    maxWidth: '54%',
    objectFit: 'contain',
    opacity: 0.92,
  },
  chaseTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: 'var(--text)',
  },
  chaseBody: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
    maxWidth: '260px',
  },
  allBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  allBarTitle: {
    // Centred, and the back button is absolutely balanced by a spacer so the
    // title sits in the true middle rather than being pushed off by it.
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  allBarSpacer: {
    width: '62px',
    flexShrink: 0,
  },
  allBack: {
    width: '62px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    color: 'var(--text)',
    fontSize: '12px',
    fontWeight: '650',
    padding: '6px 0',
    cursor: 'pointer',
    flexShrink: 0,
  },
  // A menu row, not a poster. Roughly 64px tall so several fit on screen.
  // Cards / Sets filter, sitting above the results so narrowing is one tap.
  searchKindRow: {
    display: 'flex', gap: 8, padding: '10px 12px 4px', flexWrap: 'wrap',
  },
  searchKindChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textSecondary,
    fontFamily: 'inherit', fontSize: 13, fontWeight: 650, lineHeight: 1,
    padding: '9px 13px', borderRadius: 999, cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  // `border` and not `borderColor` - the base sets the shorthand, and mixing
  // the two drops the rule.
  searchKindChipOn: {
    border: `1px solid ${theme.accent}`, background: theme.accentBg, color: theme.text,
  },
  searchKindCount: {
    fontSize: 12, fontWeight: 700, color: theme.textMuted, fontVariantNumeric: 'tabular-nums',
  },
  searchSectionLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: theme.textSecondary, padding: '14px 12px 8px',
  },
  allGrid: {
    display: 'grid',
    gap: '8px',
    width: '100%',
    // Grid items default to min-width:auto and refuse to shrink below their
    // content, which is how a 3-up grid ended up wider than the phone.
    gridAutoRows: 'min-content',
  },
  // Grid mode: the tile stacks vertically so it scales with the column width
  // instead of a horizontal row being crushed until it overflows sideways.
  allCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    padding: '9px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    color: 'var(--text)',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  allCardArt: {
    width: '100%',
    maxWidth: '100%',
    aspectRatio: '16 / 9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    background: 'var(--bg-sheet)',
    overflow: 'hidden',
  },
  // Single-column level: a taller banner so the set art is actually readable,
  // but nowhere near the full-width poster that was too big.
  allCardArtLarge: {
    width: '100%',
    maxWidth: '100%',
    aspectRatio: '16 / 6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    background: 'var(--bg-sheet)',
    overflow: 'hidden',
  },
  allCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    overflow: 'hidden',
  },
  allTile: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '9px 11px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '11px',
    color: 'var(--text)',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  allTileImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  allTileFallback: {
    fontSize: '13px',
    fontWeight: '800',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },
  allTileBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  allTileName: {
    fontSize: '13.5px',
    fontWeight: '650',
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  allTileMeta: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  allTileValue: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#4ade80',
    fontVariantNumeric: 'tabular-nums',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  allBarTrack: {
    marginTop: '3px',
    height: '3px',
    borderRadius: '999px',
    background: 'var(--border)',
    overflow: 'hidden',
  },
  allBarFill: {
    height: '100%',
    borderRadius: '999px',
    background: 'var(--accent, #0ea5e9)',
  },
  allEmpty: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    padding: '20px 2px',
  },
  tabIndicator4Tabs: {
    width: 'calc(25% - 8px)',
  },
  tab: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    // 4px, not 6px, because "Explore" is the longest label the bar has ever
    // carried. At 360dp, the most common Android width, the tab has 79.5px and
    // Explore wants 82px, so the icon was the flex item that gave way and
    // rendered at 13.8px next to its three 16px siblings. Four less padding a
    // side buys the 4px back and every icon stays 16px. Shorthand on purpose:
    // the base style uses padding, so an override uses padding too.
    padding: '10px 4px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    // Four tabs have to share the width. A minWidth here (it was 120px) forces
    // 480px of tabs onto a 390px phone and pushes Wish off screen.
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  tabActive: {
    color: '#fff',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    paddingBottom: 'var(--nav-clearance)',
  },
  globalToolbar: {
    display: 'flex',
    gap: '12px',
    // Longhand, because the scroll-away state overrides the top and bottom
    // individually and mixing the two forms drops the whole declaration.
    paddingTop: '0px',
    paddingRight: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    backgroundColor: theme.bg,
  },
  viewContainer: {
    padding: '0 16px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: theme.bgSheet,
    borderRadius: '10px',
    border: `1px solid ${theme.border}`,
    color: theme.textSecondary,
    flex: 1,
    minWidth: 0,
  },
  toolbarButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'hidden',
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: theme.text,
    fontSize: '13px',
  },
  toolButton: {
    // The filter-count badge anchors to this.
    position: 'relative',
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: theme.bgSheet,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    transition: 'all 0.2s ease',
  },
  toolButtonActive: {
    backgroundColor: theme.accent,
    border: `1px solid ${theme.accent}`,
    color: theme.text,
  },
  searchFilterChips: {
    display: 'flex',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: theme.bg,
    borderBottom: `1px solid ${theme.border}`,
    overflowX: 'auto',
  },
  searchFilterChip: {
    padding: '6px 12px',
    borderRadius: '16px',
    border: `1px solid ${theme.border}`,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
  },
  setGroupHeader: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    padding: '10px 2px 0',
    borderBottom: `1px solid ${theme.border}`,
    marginBottom: '-4px',
  },
  setGroupName: {
    fontSize: '13px',
    fontWeight: '600',
    color: theme.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  setGroupYear: {
    fontSize: '12px',
    color: theme.textMuted,
    flexShrink: 0,
  },
  addCardPlaceholder: {
    aspectRatio: '0.714',
    borderRadius: '12px',
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: theme.bgCard,
  },
  addCardPlaceholderSmall: {
    borderRadius: '8px',
  },
  addCardInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: theme.accent,
  },
  addCardText: {
    fontSize: '12px',
    fontWeight: '600',
  },
  cardItem: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.bgSheet,
    cursor: 'pointer',
  },
  cardImageWrapper: {
    position: 'relative',
    width: '100%',
    // Held before the image decodes. Without it each tile is zero-height until
    // its picture arrives, so the grid reflowed once per image and the rows
    // visibly pushed each other down.
    aspectRatio: '0.714',
    overflow: 'hidden',
  },
  cardImage: {
    width: '100%',
    aspectRatio: '0.714',
    objectFit: 'cover',
    display: 'block',
  },
  privateIndicator: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
  },
  privateIndicatorSmall: {
    top: '4px',
    right: '4px',
    width: '18px',
    height: '18px',
    borderRadius: '4px',
  },
  privateIndicatorLarge: {
    top: '8px',
    right: '8px',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
  },
  cardInfo: {
    padding: '8px',
    textAlign: 'center',
  },
  cardName: {
    display: 'block',
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardGrade: {
    display: 'block',
    color: theme.textSecondary,
    fontSize: '11px',
    marginTop: '2px',
  },
  bindersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    padding: '8px 0',
  },
  binderItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    // A hold opens our sheet, not WebKit's image callout or a text selection.
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  binderImageWrapper: {
    position: 'relative',
    width: '100%',
    aspectRatio: '0.8',
    marginBottom: '8px',
  },
  binderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.4))',
    transition: 'transform 0.3s ease, filter 0.3s ease',
  },
  binderGlow: {
    position: 'absolute',
    bottom: '-10px',
    left: '10%',
    right: '10%',
    height: '20px',
    background: 'radial-gradient(ellipse at center, rgba(14, 165, 233, 0.3) 0%, transparent 70%)',
    filter: 'blur(8px)',
  },
  addBinderPlaceholder: {
    width: '100%',
    aspectRatio: '0.8',
    borderRadius: '12px',
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    color: theme.accent,
    backgroundColor: theme.bgCard,
  },
  binderName: {
    color: theme.text,
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'center',
  },
  binderCount: {
    color: theme.textSecondary,
    fontSize: '11px',
    marginTop: '2px',
  },
  binderValue: {
    color: '#4ade80',
    fontSize: '11px',
    fontWeight: '600',
    marginTop: '1px',
    display: 'block',
  },
  // The trade binder, marked so it reads as the one with a job rather than
  // another binder that happens to sort first.
  binderImageWrapperTrade: {
    filter: 'drop-shadow(0 0 14px rgba(74, 222, 128, 0.35))',
  },
  binderTradeTag: {
    display: 'inline-block',
    marginTop: '3px',
    padding: '1.5px 7px',
    borderRadius: '999px',
    border: '0.5px solid rgba(74, 222, 128, 0.45)',
    background: 'rgba(74, 222, 128, 0.14)',
    color: '#4ade80',
    fontSize: '9.5px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  // Sitting on the binder in the grid. Absolute against the tile rather than
  // the art, because the trade binder's wrapper carries a green drop-shadow
  // and a filtered ancestor would paint that halo around the tag as well.
  binderTradeTagTop: {
    position: 'absolute',
    top: '2px',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '0',
    whiteSpace: 'nowrap',
    zIndex: 2,
  },
  // Wish List Empty State
  wishListEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 32px',
    textAlign: 'center',
    color: theme.textSecondary,
  },
  wishListEmptyTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: '16px 0 8px',
  },
  wishListEmptyText: {
    color: theme.textSecondary,
    fontSize: '14px',
    lineHeight: '1.5',
    maxWidth: '280px',
    margin: '0 0 24px',
  },
  wishListAddButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: theme.accent,
    color: theme.text,
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  wishListCounter: {
    color: theme.textSecondary,
    fontSize: '12px',
    marginTop: '12px',
  },
  wishListToolbarCounter: {
    backgroundColor: `${theme.accent}26`,
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '600',
    padding: '6px 10px',
    borderRadius: '12px',
    border: '1px solid rgba(14, 165, 233, 0.3)',
  },
  // Wish List Priority Sections
  wishPrioritySection: {
    margin: '8px 16px 16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid',
  },
  wishMoreSection: {
    margin: '0 16px 16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid',
  },
  wishSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  wishSectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  wishSectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
  },
  wishSectionCount: {
    fontSize: '14px',
    fontWeight: '600',
  },
  wishSectionEmpty: {
    padding: '24px 16px',
    textAlign: 'center',
  },
  // Toast notification
  toast: {
    position: 'fixed',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '12px 24px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    zIndex: 10005,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  // Sets Browse Grid (2-column layout)
  setsBrowseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    padding: '8px 16px 16px',
  },
  setBrowseCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, border-color 0.15s ease',
  },
  setBrowseLogo: {
    width: '100%',
    height: '60px',
    backgroundColor: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    overflow: 'hidden',
  },
  setBrowseLogoImg: {
    width: 'auto',
    height: '44px',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  setBrowseLogoFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
    fontSize: '24px',
    fontWeight: '700',
  },
  setBrowseLogoWishList: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(250, 204, 21, 0.15)',
    border: '1px solid rgba(250, 204, 21, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.warning,
  },
  setBrowseInfo: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  setBrowseName: {
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    maxWidth: '100%',
  },
  setBrowseCounts: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  setBrowseCountOwned: {
    color: theme.accent,
    fontSize: '11px',
    fontWeight: '600',
  },
  setBrowseCountWish: {
    color: theme.warning,
    fontSize: '11px',
    fontWeight: '600',
  },
  loadMoreCard: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px dashed ${theme.border}`,
    cursor: 'pointer',
    gap: '4px',
  },
  loadMoreText: {
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  loadMoreCount: {
    color: theme.textSecondary,
    fontSize: '11px',
  },
  // Set Cards Grid (for set detail view)
  setCardsGrid: {
    display: 'grid',
    gap: '8px',
    padding: '8px 16px 16px',
    alignItems: 'start',
  },
  setCardItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
  },
  setCardImgWrapper: {
    position: 'relative',
    width: '100%',
    // Was height:0 + paddingBottom:139.86% (the old aspect-ratio hack). That
    // gives the box a *painted* height but a real content height of ZERO, and
    // CardImage renders its own `height: 100%` container inside — 100% of zero.
    // The image was absolutely positioned inside a collapsed box, so nothing
    // showed: no picture, no error, not even the text fallback. Just the
    // wrapper's own grey background. aspectRatio gives it genuine height.
    aspectRatio: '1 / 1.3986',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
  },
  setCardImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  setCardStatus: {
    position: 'absolute',
    bottom: '4px',
    left: '4px',
    display: 'flex',
    gap: '4px',
  },
  setCardStatusOwned: {
    backgroundColor: `${theme.accent}e6`,
    color: '#fff',
    fontSize: '9px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  setCardStatusWish: {
    backgroundColor: 'rgba(250, 204, 21, 0.9)',
    color: '#000',
    fontSize: '9px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  setCardName: {
    color: theme.text,
    fontSize: '11px',
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  setCardNumber: {
    color: theme.textSecondary,
    fontSize: '10px',
  },
  // Wish List Sets Grid (legacy - keeping for compatibility)
  wishListSetsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '16px',
  },
  wishListSetCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 8px',
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
  },
  wishListSetLogo: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wishListSetLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  wishListSetLogoFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
    fontSize: '18px',
    fontWeight: '600',
  },
  wishListSetLogoAll: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.purple} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
  },
  wishListSetLogoAdd: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
  },
  wishListSetName: {
    color: theme.text,
    fontSize: '11px',
    fontWeight: '600',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  wishListSetCount: {
    color: theme.accent,
    fontSize: '10px',
    fontWeight: '600',
  },
  // Wish List Cards View
  wishListCardsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  wishListCardsTitle: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    margin: 0,
    whiteSpace: 'nowrap',
  },
  setDetailHeaderSingle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    // The row carries a back button, three chips, a count and the printings
    // pill. Everything under it - the value, the note - used to start 2px
    // later, so the whole block read as one crushed paragraph. The rule sits
    // under the controls and the numbers get their own air below it.
    padding: '12px 16px 14px',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  setFilterChips: {
    display: 'flex',
    gap: '8px',
    // Sized to its content, not stretched to fill.
    //
    // It was `flex: 1` with `minWidth: 0`, which lets the box shrink below the
    // width of the chips inside it - and the chips refuse to shrink - so they
    // overflowed their own box and sat on top of the count beside them. The row
    // scrolls sideways instead when it runs out of room, which is the only
    // honest answer on a narrow phone with five controls on one line.
    flex: '1 1 auto',
    minWidth: 0,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    paddingRight: '2px',
  },
  setFilterChip: {
    // Never gives ground and never breaks over two lines: the printing pill
    // beside it is longer than the icon button that used to sit there, and the
    // pill is the one thing on the row that can shorten itself.
    flexShrink: 0,
    whiteSpace: 'nowrap',
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '14px',
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  setFilterChipActive: {
    backgroundColor: `${theme.accent}26`,
    border: `1px solid ${theme.accent}`,
    color: theme.accent,
  },
  setDetailCount: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  // The count leads the line and the value follows it, one weight and one
  // size apart, because they answer the same question: how much is here and
  // how much is it worth.
  setDetailCountLead: {
    color: theme.accent,
    fontWeight: '600',
    marginRight: '10px',
  },
  setDetailValue: {
    padding: '12px 16px 12px',
    fontSize: '13px',
    lineHeight: 1.45,
    fontVariantNumeric: 'tabular-nums',
  },
  // What is on screen, under what the set is worth. Quieter and a size down,
  // so the eye lands on the set's value first and finds the qualifier second
  // rather than reading two green numbers that seem to disagree.
  setDetailShowing: {
    padding: '3px 16px 0',
    fontSize: '12px',
    lineHeight: 1.45,
    color: theme.textSecondary,
    fontVariantNumeric: 'tabular-nums',
  },
  setDetailValueAmount: {
    color: '#4ade80',
    fontWeight: '600',
  },
  setDetailValueMeta: {
    color: theme.textSecondary,
  },
  // The printing filter. It names the printing it is on, so it is a pill with
  // room for a word rather than a 32px icon square.
  setPrintingButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    maxWidth: '104px',
    minHeight: '32px',
    padding: '0 8px',
    borderRadius: '8px',
    fontSize: '11.5px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  setPrintingLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  setCardPrinting: {
    position: 'absolute',
    top: '3px',
    left: '3px',
    right: '3px',
    padding: '2px 4px',
    borderRadius: '4px',
    textAlign: 'center',
    overflow: 'hidden',
  },
  setCardPrintingText: {
    fontSize: '9px',
    fontWeight: '700',
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  setSlotSentinel: {
    minHeight: '24px',
    display: 'flex',
    justifyContent: 'center',
    padding: '0 16px 16px',
  },
  setSlotSentinelText: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  // BottomSheet draws nothing of its own - every caller brings the scrim and
  // the panel. This one brought a z-index and nothing else, so the overlay was
  // a static block at the end of the body: the sheet mounted, the nav stepped
  // aside for it, and there was nothing on screen. "You tap that printings
  // button, nothing happens. It just hides the bottom navigation."
  // Deliberately not a printing colour: it names no printing, it says how many
  // there are. A neutral chip reads as a count rather than as a fifth variant.
  setCardPrintingCount: {
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    cursor: 'pointer',
  },
  printingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 10040,
  },
  printingSheet: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '80vh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    borderRadius: '18px 18px 0 0',
    padding: '10px 16px calc(24px + var(--safe-area-bottom, 0px))',
    backgroundColor: theme.bgSheet,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  printingHandle: {
    width: '36px',
    height: '4px',
    borderRadius: '2px',
    margin: '0 auto 10px',
    backgroundColor: theme.border,
  },
  printingSheetTitle: {
    color: theme.text,
    fontSize: '17px',
    fontWeight: '700',
  },
  printingSheetNote: {
    color: theme.textSecondary,
    fontSize: '12px',
    lineHeight: 1.45,
    marginBottom: '4px',
  },
  printingOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    minHeight: '48px',
    padding: '0 10px',
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    backgroundColor: 'transparent',
    color: theme.text,
    textAlign: 'left',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  printingDot: {
    width: '10px',
    height: '10px',
    borderRadius: '5px',
    flexShrink: 0,
  },
  printingOptionLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: '14px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  printingOptionCount: {
    color: theme.textSecondary,
    fontSize: '12px',
    fontVariantNumeric: 'tabular-nums',
  },
  wishListCardsCount: {
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '600',
  },
  wishListCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '16px',
  },
  wishListCardItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
  },
  wishListCardImage: {
    position: 'relative',
    width: '100%',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
  },
  wishListCardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  wishListVariantBadge: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    padding: '2px 6px',
    backgroundColor: 'rgba(139, 92, 246, 0.9)',
    color: theme.text,
    fontSize: '8px',
    fontWeight: '600',
    borderRadius: '4px',
  },
  wishListCardName: {
    color: theme.text,
    fontSize: '11px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  wishListCardSet: {
    color: theme.textSecondary,
    fontSize: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  binderDetailHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginBottom: '24px',
  },
  binderDetailTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backButtonPremium: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
  binderDetailImageWrapper: {
    flexShrink: 0,
  },
  binderDetailImage: {
    width: '50px',
    height: '62px',
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
  },
  binderDetailTitle: {
    flex: 1,
    color: theme.text,
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
    textAlign: 'center',
  },
  binderDetailMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  binderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: '0 0 auto',
  },
  binderPrintBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 13px',
    borderRadius: '999px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  binderDetailCount: {
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center',
    display: 'block',
  },
  setDetailCount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    backgroundColor: theme.accentBg,
    borderRadius: '8px',
    minWidth: '60px',
  },
  setDetailCountText: {
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  // Stats Panel Styles
  statsPanelOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    // The bottom nav is fixed at z-index 10000, so it paints over this overlay.
    // Reserve its height (plus safe area) or the panel's lower edge is hidden.
    padding: '20px 20px var(--nav-clearance) 20px',
  },
  statsPanel: {
    backgroundColor: theme.bgCard,
    borderRadius: '20px',
    border: `1px solid ${theme.border}`,
    width: '100%',
    maxWidth: '400px',
    maxHeight: '70vh',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  statsPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  statsPanelTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
  },
  statsPanelClose: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  userActionsSection: {
    display: 'flex',
    gap: '8px',
    padding: '0 20px 16px',
  },
  userActionButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
  },
  userActionButtonActive: {
    backgroundColor: `${theme.accent}26`,
    border: `1px solid ${theme.accent}`,
  },
  userActionButtonDanger: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    cursor: 'pointer',
  },
  userActionText: {
    color: theme.text,
    fontSize: '13px',
    fontWeight: '600',
  },
  userActionTextDanger: {
    color: theme.error,
    fontSize: '13px',
    fontWeight: '600',
  },
  quickStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '20px',
  },
  quickStatCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 8px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
  },
  quickStatIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: `${theme.accent}26`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    color: theme.accent,
  },
  quickStatValue: {
    color: theme.text,
    fontSize: '20px',
    fontWeight: '700',
  },
  quickStatLabel: {
    color: theme.textSecondary,
    fontSize: '11px',
    fontWeight: '500',
    marginTop: '2px',
  },
  tradeReputationSection: {
    padding: '0 20px 20px',
  },
  sectionTitle: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    margin: '0 0 16px 0',
  },
  overallRating: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    marginBottom: '16px',
  },
  starsRow: {
    display: 'flex',
    gap: '4px',
  },
  ratingText: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  ratingBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  ratingLabel: {
    color: theme.textSecondary,
    fontSize: '12px',
    width: '100px',
    flexShrink: 0,
  },
  ratingBarContainer: {
    flex: 1,
    height: '6px',
    backgroundColor: theme.border,
    borderRadius: '3px',
    overflow: 'hidden',
  },
  ratingBar: {
    height: '100%',
    backgroundColor: theme.accent,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  ratingValue: {
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    width: '30px',
    textAlign: 'right',
  },
  noReviews: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
  },
  noReviewsText: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  noReviewsSubtext: {
    color: theme.textSecondary,
    fontSize: '12px',
    marginTop: '4px',
    textAlign: 'center',
  },
  // Trade Activity styles
  tradeActivityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '8px',
    marginBottom: '12px',
  },
  tradeActivityItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 4px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
  },
  tradeActivityValue: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '2px',
  },
  tradeActivityLabel: {
    fontSize: '9px',
    color: theme.textMuted,
    textTransform: 'uppercase',
  },
  activityIndicatorsRow: {
    display: 'flex',
    gap: '8px',
  },
  activityIndicator: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
  },
  activityLabel: {
    fontSize: '10px',
    color: theme.textMuted,
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  activityValue: {
    fontSize: '14px',
    fontWeight: '700',
  },
  gridLoading: {
    // Spans the grid rather than sitting in one cell, so it reads as the list
    // loading rather than as a card that failed.
    gridColumn: '1 / -1',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '60px 0',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  // Bulk Edit Modal
  bulkEditBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 1000,
  },
  bulkEditModal: {
    position: 'fixed',
    bottom: '100px',
    left: '8px',
    right: '8px',
    backgroundColor: theme.bgSheet,
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    overflow: 'hidden',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
    maxHeight: '65vh',
    overflowY: 'auto',
  },
  bulkEditHandle: {
    width: '36px',
    height: '4px',
    backgroundColor: theme.border,
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  bulkEditHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  bulkEditTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  bulkEditCloseBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  bulkEditSubtitle: {
    color: theme.textSecondary,
    fontSize: '13px',
    margin: '0 0 16px 0',
  },
  bulkEditOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  bulkEditOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    WebkitTapHighlightColor: 'transparent',
  },
  bulkEditOptionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bulkEditOptionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  bulkEditOptionTitle: {
    color: theme.text,
    fontSize: '15px',
    fontWeight: '500',
  },
  bulkEditOptionDesc: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  bulkEditCancelBtn: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Card status badges
  cardBadge: {
    position: 'absolute',
    padding: '3px 6px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
    fontWeight: '600',
    fontSize: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  cardBadgeSmall: {
    padding: '2px 4px',
    fontSize: '8px',
    borderRadius: '3px',
  },
  cardBadgeBottomLeft: {
    bottom: '4px',
    left: '4px',
  },
  cardBadgeTopRight: {
    top: '4px',
    right: '4px',
  },
  cardBadgeTopLeft: {
    top: '4px',
    left: '4px',
  },
  cardBadgeBottomRight: {
    bottom: '4px',
    right: '4px',
  },
  cardBadgeText: {
    lineHeight: 1,
  },
  // Selection indicator on cards
  cardItemSelected: {
    outline: `2px solid ${theme.accent}`,
    outlineOffset: '-2px',
    borderRadius: '10px',
  },
  selectionIndicator: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: `2px solid ${theme.text}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectionIndicatorActive: {
    backgroundColor: theme.accent,
    border: `2px solid ${theme.accent}`,
    color: theme.text,
  },
  // Floating action bar
  bulkEditActionBar: {
    position: 'fixed',
    bottom: '90px',
    left: '12px',
    right: '12px',
    backgroundColor: theme.bgCard,
    borderRadius: '16px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    border: `1px solid ${theme.border}`,
    zIndex: 100,
  },
  bulkEditCancelAction: {
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  bulkEditActionInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  bulkEditActionLabel: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  bulkEditActionCount: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  bulkEditApplyAction: {
    padding: '10px 20px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '10px',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Smart Search Results Panel Styles
  searchResultsPanel: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'calc(var(--safe-area-top, 0px) + 145px)', // Below header + tabs + toolbar
  },
  searchResultsBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  searchResultsContainer: {
    position: 'relative',
    margin: '8px 12px',
    maxHeight: 'calc(100vh - var(--safe-area-top, 0px) - 230px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  searchSection: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  searchSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '14px',
    border: '1px solid',
    borderColor: theme.border,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  searchSectionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  searchSectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
  },
  searchSectionRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: theme.textSecondary,
  },
  searchSectionCount: {
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    minWidth: '28px',
    textAlign: 'center',
  },
  searchSectionSpinner: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: `2px solid ${theme.border}`,
    borderTopColor: theme.accent,
  },
  searchSectionContent: {
    overflow: 'hidden',
    marginTop: '8px',
    backgroundColor: theme.bgCard,
    borderRadius: '14px',
    border: `1px solid ${theme.border}`,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
  },
  searchSectionScrollable: {
    maxHeight: '280px',
    overflowY: 'auto',
    padding: '12px',
    WebkitOverflowScrolling: 'touch',
  },
  searchCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
  },
  searchCardItem: {
    position: 'relative',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bg,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  searchCardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  searchCardBadge: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    color: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  searchCardStatus: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    display: 'flex',
    gap: '2px',
  },
  searchCardBadgeSmall: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    fontWeight: '700',
    color: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  searchSetItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: theme.bgSheet,
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '6px',
  },
  searchSetLogo: {
    height: '28px',
    width: 'auto',
    objectFit: 'contain',
  },
  searchSetName: {
    fontSize: '13px',
    fontWeight: '500',
    color: theme.text,
    flex: 1,
  },
  searchSeeMore: {
    marginTop: '8px',
    textAlign: 'center',
    padding: '8px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
  },
  searchClearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: theme.textSecondary,
    border: 'none',
    cursor: 'pointer',
    color: theme.bgSheet,
    flexShrink: 0,
    padding: 0,
  },
});

// Add keyframes for animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes matchPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 20px rgba(250, 204, 21, 0.3);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 30px rgba(250, 204, 21, 0.5);
    }
  }
  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
if (!document.querySelector('style[data-collection-animations]')) {
  styleSheet.setAttribute('data-collection-animations', 'true');
  document.head.appendChild(styleSheet);
}

export default Collection;
